import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from '../utils/axios';
import { API_BASE_URL } from '../config/api.config';
import toast from 'react-hot-toast';
import {
  Plus, Save, ArrowLeft, Eye, Download, CheckCircle,
  Search, Receipt, FileText, Calculator, ChevronLeft, ChevronRight,
  DollarSign, Clock, AlertCircle, Trash2, Copy, X, Link2, Paperclip, Sparkles, Upload, FileUp
} from 'lucide-react';
import ValidatedInput from '../components/ValidatedInput';
import ValidatedSelect from '../components/ValidatedSelect';
import { validateForm } from '../utils/validation';
import { useAuth } from '../context/AuthContext';
import { generatePDF } from '../utils/pdfGenerator';
import api, { getFormData } from '../utils/api';
import DocumentHistoryButton from '../components/DocumentHistoryButton';
import LoadingSpinner, { PageLoader, TableSkeleton } from '../components/LoadingSpinner';

import { calculateTotalGSTBreakdown, getStateNameFromGST, GST_STATE_CODES } from '../utils/gstUtils';
import { roundForCalculation } from '../utils/mathUtils';
import GSTBreakdown, { GSTSummary } from '../components/GSTBreakdown';
import { useModalScrollLock } from '../hooks/useModalScrollLock';
import LastUpdatedIndicator from '../components/LastUpdatedIndicator';
import InvoiceNumberField from '../components/InvoiceNumberField';
import TermsAndConditionsInput from '../components/TermsAndConditionsInput';
import PaymentModal from '../components/PaymentModal';
import AppModal, { modalTable, modalButton } from '../components/AppModal';
import FilterDropdown from '../components/FilterDropdown';
import AdvancedFilters from '../components/AdvancedFilters';
import { recalcItemsAmounts } from '../utils/itemAmounts';

// State options for dropdown
const stateOptions = Object.entries(GST_STATE_CODES).map(([code, name]) => ({
  value: name,
  label: name
})).sort((a, b) => a.label.localeCompare(b.label));

// ─── Wood Work Template ───────────────────────────────────────────────────────
// Pre-defined categories & items for Wood Work estimates.
// Qty is auto-calculated as: Nos × Length × Breadth
// Amount is auto-calculated as: Qty × Rate
const WOOD_WORK_TEMPLATE = [
  // ── Category A: TV UNIT ──────────────────────────────────────────────────
  { categoryLabel: 'TV UNIT',    product_name: 'TV framing with slide door', unit_price: 750,  nos: 1, length: 15,   breadth: 8.3  },
  { categoryLabel: 'TV UNIT',    product_name: 'Box',                         unit_price: 1000, nos: 1, length: 8,    breadth: 2    },
  { categoryLabel: 'TV UNIT',    product_name: 'Partition (two)',              unit_price: 750,  nos: 1, length: 5,    breadth: 8    },

  // ── Category B: GF GUEST BEDROOM ─────────────────────────────────────────
  { categoryLabel: 'GF GUEST BEDROOM', product_name: 'Wardrobe box',    unit_price: 1000, nos: 1, length: 18.4, breadth: 7 },
  { categoryLabel: 'GF GUEST BEDROOM', product_name: 'Wardrobe framing', unit_price: 750,  nos: 1, length: 18.4, breadth: 3 },
  { categoryLabel: 'GF GUEST BEDROOM', product_name: 'Partition door',  unit_price: 500,  nos: 1, length: 7,    breadth: 4 },

  // ── Category C: MASTER BED ROOM ──────────────────────────────────────────
  { categoryLabel: 'MASTER BED ROOM', product_name: 'Wardrobe box',    unit_price: 1000, nos: 1, length: 20.4, breadth: 8.3 },
  { categoryLabel: 'MASTER BED ROOM', product_name: 'Wardrobe framing', unit_price: 750,  nos: 1, length: 20.4, breadth: 3   },
  { categoryLabel: 'MASTER BED ROOM', product_name: 'TV Paneling',     unit_price: 550,  nos: 1, length: 4,    breadth: 4   },
  { categoryLabel: 'MASTER BED ROOM', product_name: 'Dressing mirror', unit_price: 100,  nos: 1, length: 2,    breadth: 7   },
  { categoryLabel: 'MASTER BED ROOM', product_name: 'Vanity box',      unit_price: 1000, nos: 1, length: 10,   breadth: 2.8 },
  { categoryLabel: 'MASTER BED ROOM', product_name: 'King size bed',   unit_price: 1000, nos: 1, length: 6,    breadth: 6.6 },
  { categoryLabel: 'MASTER BED ROOM', product_name: 'Bed panneling',   unit_price: 450,  nos: 1, length: 8,    breadth: 5   },

  // ── Category D: CROCKERY ─────────────────────────────────────────────────
  { categoryLabel: 'CROCKERY', product_name: 'Box', unit_price: 1000, nos: 1, length: 7, breadth: 7 },

  // ── Category E: CHILDREN ROOM 2 ──────────────────────────────────────────
  { categoryLabel: 'CHILDREN ROOM 2', product_name: 'Wardrobe box',    unit_price: 1000, nos: 1, length: 14, breadth: 7   },
  { categoryLabel: 'CHILDREN ROOM 2', product_name: 'Wardrobe framing', unit_price: 750,  nos: 1, length: 14, breadth: 3   },
  { categoryLabel: 'CHILDREN ROOM 2', product_name: 'Bunk bed',        unit_price: 1000, nos: 2, length: 6,  breadth: 6.6 },
  { categoryLabel: 'CHILDREN ROOM 2', product_name: 'Book shelve',     unit_price: 1000, nos: 1, length: 5,  breadth: 8   },
  { categoryLabel: 'CHILDREN ROOM 2', product_name: 'Study table',     unit_price: 1000, nos: 1, length: 4,  breadth: 4   },
  { categoryLabel: 'CHILDREN ROOM 2', product_name: 'Dressing mirror', unit_price: 1000, nos: 1, length: 7,  breadth: 3   },

  // ── Category F: KITCHEN ──────────────────────────────────────────────────
  { categoryLabel: 'KITCHEN', product_name: 'Box type',               unit_price: 1000, nos: 1, length: 42, breadth: 2.66 },
  { categoryLabel: 'KITCHEN', product_name: 'Loft frame',             unit_price: 750,  nos: 1, length: 32, breadth: 1.2  },
  { categoryLabel: 'KITCHEN', product_name: 'Breakfast counter box',  unit_price: 1000, nos: 1, length: 5,  breadth: 2.66 },
  { categoryLabel: 'KITCHEN', product_name: 'Framing',                unit_price: 750,  nos: 1, length: 19, breadth: 2    },
  { categoryLabel: 'KITCHEN', product_name: 'Tandon Boxes',           unit_price: 4000, nos: 8, length: 0,  breadth: 0    },
  { categoryLabel: 'KITCHEN', product_name: 'Tall unit',              unit_price: 1000, nos: 2, length: 7,  breadth: 2    },
  { categoryLabel: 'KITCHEN', product_name: 'Tall unit hardware',     unit_price: 0,    nos: 1, length: 0,  breadth: 0    },
  { categoryLabel: 'KITCHEN', product_name: 'Pooja unit',             unit_price: 1000, nos: 1, length: 7,  breadth: 4    },

  // ── Category G: PROJECTOR ROOM ───────────────────────────────────────────
  { categoryLabel: 'PROJECTOR ROOM', product_name: 'Box', unit_price: 1000, nos: 1, length: 14, breadth: 2 },
];

// Helper: compute qty for a woodwork item (Nos × L × B; if L or B is 0, qty = Nos)
const computeWoodWorkQty = (nos, length, breadth) => {
  const n = parseFloat(nos) || 0;
  const l = parseFloat(length) || 0;
  const b = parseFloat(breadth) || 0;
  if (l === 0 || b === 0) return n; // Tandon Boxes / hardware items
  return parseFloat((n * l * b).toFixed(4));
};


const AdminEstimateManagement = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();

  // State for form/history toggle
  const [activeTab, setActiveTab] = useState('history'); // Default to history view
  const [isEditing, setIsEditing] = useState(false);
  const [isWoodWorkMode, setIsWoodWorkMode] = useState(false);
  const [proformaFile, setProformaFile] = useState(null);
  const [proformaExtracting, setProformaExtracting] = useState(false);
  const [showProformaUploadModal, setShowProformaUploadModal] = useState(false);
  const [aiFilledFields, setAiFilledFields] = useState(new Set());
  const [newSectionName, setNewSectionName] = useState('');
  const prefilledCustomer = location.state?.prefilledCustomer;


  // Modal states (will be consolidated with history states below)

  // Form states
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [formData, setFormData] = useState({
    salesperson_id: '',
    customer: prefilledCustomer || {
      name: '',
      countryCode: '',
      phone: '',
      email: '',
      address: '',
      gstNumber: '',
      state: ''
    },
    dispatched_from: '',
    delivered_to: '',
    termsAndConditions: '',
    materialDescription: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [formTouched, setFormTouched] = useState(false);
  const [items, setItems] = useState([
    {
      category_id: '',
      product_id: '',
      product_name: '',
      hsn_code: '',
      product_instructions: '',
      quantity: 1,
      unit_price: 0,
      unit_type: 'SFT',
      gst_rate: '',
      gst_amount: 0,
      amount: 0,
      total_amount: 0,
      tax: 0,
      discount: '',
      subtotal: 0
    },
    {
      category_id: '',
      product_id: '',
      product_name: '',
      hsn_code: '',
      product_instructions: '',
      quantity: 1,
      unit_price: 0,
      unit_type: 'SFT',
      gst_rate: '',
      gst_amount: 0,
      amount: 0,
      total_amount: 0,
      tax: 0,
      discount: '',
      subtotal: 0
    },
    {
      category_id: '',
      product_id: '',
      product_name: '',
      hsn_code: '',
      product_instructions: '',
      quantity: 1,
      unit_price: 0,
      unit_type: 'SFT',
      gst_rate: '',
      gst_amount: 0,
      amount: 0,
      total_amount: 0,
      tax: 0,
      discount: '',
      subtotal: 0
    }
  ]);

  const [showDiscount, setShowDiscount] = useState(false);

  // History states
  const [estimates, setEstimates] = useState([]);
  const [filteredEstimates, setFilteredEstimates] = useState([]);
  
  // Advanced Filters State
  const [filters, setFilters] = useState({
    dateRange: 'all',
    status: 'all',
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: '',
    isWoodWork: 'all',
    salesperson_id: 'all'
  });

  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Stable ref — tracks current page without causing effect re-runs
  const currentPageRef = useRef(1);
  
  const [searchTerm, setSearchTerm] = useState('');
  const searchTermRef = useRef(searchTerm);
  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [dataFetched, setDataFetched] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [includeTermsEstimateModal, setIncludeTermsEstimateModal] = useState(false);
  const [showClosedOrderModal, setShowClosedOrderModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingEstimate, setDeletingEstimate] = useState(null);
  const [convertingEstimate, setConvertingEstimate] = useState(null);
  const [closedOrderData, setClosedOrderData] = useState({
    orderDescription: '',
    paymentType: 'cash',
    amountReceived: 0,
    paymentStatus: 'pending',
    gstNumber: '',
    state: '',
    dispatched_from: '',
    delivered_to: '',
    termsAndConditions: '',
    materialDescription: ''
  });
  const [showEditProductsModal, setShowEditProductsModal] = useState(false);
  const [editModalContext, setEditModalContext] = useState(null);
  const [editingProducts, setEditingProducts] = useState([]);
  const [editingApplyDiscount, setEditingApplyDiscount] = useState(false);
  const [showEditEstimateModal, setShowEditEstimateModal] = useState(false);
  const [editingEstimate, setEditingEstimate] = useState(null);
  const [paymentData, setPaymentData] = useState({
    payment_type: 'cash',
    payment_status: 'pending',
    amount_received: 0,
    gst_number: '', // Add GST number field
    state: '', // Add State field
    dispatched_from: '',
    delivered_to: ''
  });
  const [editMode, setEditMode] = useState(false);
  const [editSummaryMode, setEditSummaryMode] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState({});
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [lastUpdated, setLastUpdated] = useState(null);



  // Use modal scroll lock for various modals
  useModalScrollLock(showViewModal || showConvertModal || showPaymentModal || showClosedOrderModal || showEditProductsModal || showEditEstimateModal);

  // Initialize payment data when modal first opens - only run once per modal open
  useEffect(() => {
    if (showPaymentModal && convertingEstimate) {
      // Only initialize if payment data is empty (first time opening modal)
      setPaymentData({
        payment_type: convertingEstimate.payment_type || 'cash',
        payment_status: convertingEstimate.payment_status || 'pending',
        amount_received: convertingEstimate.amount_received || convertingEstimate.amountReceived || 0,
        gst_number: convertingEstimate.customer_gst || convertingEstimate.customerId?.gstNumber || '',
        state: convertingEstimate.customer_state || convertingEstimate.party_state_name || (convertingEstimate.customer_gst ? getStateNameFromGST(convertingEstimate.customer_gst) : '') || ''
      });
    }
  }, [showPaymentModal]);

  // Validation schema
  const validationSchema = {
    'customer.name': { type: 'name', required: true },
    'customer.phone': { type: 'phone', required: true },
    'customer.email': { type: 'email', required: false },
    'customer.address': { type: 'text', required: true },
    'salesperson_id': { type: 'select', required: true }
  };

  // Effects
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');

    if (id) {
      setIsEditing(true);
      setActiveTab('form');
      fetchEstimateForEdit(id);
    } else if (prefilledCustomer) {
      setActiveTab('form');
    } else if (tabParam === 'form') {
      setActiveTab('form');
    }
  }, [id, prefilledCustomer, location.search]);

  // Handle ?view= deep link
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get('view');
    if (viewParam) {
      const fetchAndOpen = async () => {
        try {
          // Search for this specific estimate
          const res = await axios.get(`/sales/estimates?search=${viewParam}`);
          if (res.data.estimates && res.data.estimates.length > 0) {
            // Find exact match just in case search returned multiple
            const exactMatch = res.data.estimates.find(e => String(e.estimate_number) === String(viewParam));
            if (exactMatch) {
              setTimeout(() => handleViewEstimate(exactMatch), 500);
            } else {
              setTimeout(() => handleViewEstimate(res.data.estimates[0]), 500);
            }
          }
        } catch (e) {
          console.error('Failed to deep link view estimate:', e);
        }
      };
      fetchAndOpen();
    }
  }, [location.search]);

  // Effect 1: Fetch data only when tab changes
  useEffect(() => {
    if (activeTab === 'form') {
      fetchFormData();
    } else if (activeTab === 'history') {
      fetchEstimates(0, 1);
    }
    // Fetch company settings on component mount
    fetchCompanySettings();
  }, [activeTab]);

  // 1b. Auto-apply filters (debounced) — watches primitives only
  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) { filterMounted.current = true; return; }
    if (activeTab !== 'history') return;
    const t = setTimeout(() => fetchEstimates(0, 1), 500);
    return () => clearTimeout(t);
  }, [filters.status, filters.startDate, filters.endDate, filters.minAmount, filters.maxAmount, filters.isWoodWork, filters.salesperson_id, filters.dateRange]);

  const fetchSalespersonsList = async () => {
    try {
      const res = await axios.get('/admin/employees').catch(() => axios.get('/sales/salespersons'));
      setSalespersons(res.data || []);
    } catch (err) {
      console.error('Failed to load salespersons list', err);
    }
  };

  useEffect(() => {
    fetchSalespersonsList();
  }, []);

  // Effect 2: Manage auto-refresh interval separately - watches modal states only to pause/resume
  useEffect(() => {
    if (activeTab !== 'history') return;

    const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

    const isAnyModalOpen = showViewModal || showConvertModal || showPaymentModal ||
      showClosedOrderModal || showEditProductsModal || showEditEstimateModal;

    if (!isAnyModalOpen) {
      const interval = setInterval(() => {
        const stillNoModals = !showViewModal && !showConvertModal && !showPaymentModal &&
          !showClosedOrderModal && !showEditProductsModal && !showEditEstimateModal;
        if (stillNoModals) {
          console.log('🔄 Auto-refreshing estimates (preserving page, search, and filters)...');
          fetchEstimates(0, currentPageRef.current);
        }
      }, AUTO_REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [activeTab, showViewModal, showConvertModal, showPaymentModal, showClosedOrderModal, showEditProductsModal, showEditEstimateModal]);

  const fetchCompanySettings = async () => {
    try {
      console.log('📋 AdminEstimateManagement: Fetching company settings...');
      const response = await axios.get(`${API_BASE_URL}/api/admin/company-settings`);
      console.log('📋 AdminEstimateManagement: Company settings response:', response.data);
      setCompanySettings(response.data);
      console.log('✅ AdminEstimateManagement: Company settings set successfully');
    } catch (error) {
      console.error('❌ AdminEstimateManagement: Failed to fetch company settings:', error);
      // Use default settings if fetch fails
      setCompanySettings({
        companyName: 'IDEAL TRENDZ INTERIORS',
        addressLine1: '# Vertex Privilege, Villa No. 32, Nizampet Road',
        addressLine2: '',
        city: 'HYDERNAGAR',
        state: 'TELANGANA',
        pincode: '500072',
        contactNumber: '9014814901',
        emailId: 'idealtrendz.int@gmail.com',
        gstNumber: '36AHZPN3524A1Z9',
        panNumber: 'AHZPN3524A',
        bankName: 'HDFC Bank',
        accountHolderName: 'IDEAL TRENDZ INTERIORS',
        accountNumber: '50200012345678',
        ifscCode: 'HDFC0001234',
        branchName: 'Nizampet'
      });
    }
  };

  useEffect(() => {
    filterEstimates();
  }, [estimates, searchTerm, sortConfig, filters.status]);

  // Helper to handle GST change and auto-fill state
  const handleGSTFieldChange = (gstValue, updateFunction, stateUpdateFunction) => {
    updateFunction(gstValue);
    const stateName = getStateNameFromGST(gstValue);
    if (stateName && stateUpdateFunction) {
      stateUpdateFunction(stateName);
    }
  };

  // Form functions
  const fetchFormData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching form data for estimate form...');

      // Try direct API calls first
      try {
        const [categoriesRes, productsRes, salespersonsRes] = await Promise.all([
          axios.get('/admin/categories'),
          axios.get('/admin/products'),
          axios.get('/admin/employees')
            .catch(() => axios.get('/sales/salespersons'))
        ]);

        // Normalize products to ensure category field exists
        const normalizedProducts = (productsRes.data || []).map(prod => {
          // Extract category ID - your backend has nested structure: product.categoryId._id
          let catId = '';

          // Try to extract from nested product.categoryId
          if (prod.product?.categoryId) {
            if (typeof prod.product.categoryId === 'object' && prod.product.categoryId !== null) {
              catId = prod.product.categoryId._id || prod.product.categoryId.id || String(prod.product.categoryId);
            } else {
              catId = String(prod.product.categoryId);
            }
          }
          // Try direct categoryId
          else if (prod.categoryId) {
            if (typeof prod.categoryId === 'object' && prod.categoryId !== null) {
              catId = prod.categoryId._id || prod.categoryId.id || String(prod.categoryId);
            } else {
              catId = String(prod.categoryId);
            }
          }
          // Try category_id
          else if (prod.category_id) {
            catId = String(prod.category_id);
          }
          // Try nested category
          else if (prod.category?._id) {
            catId = String(prod.category._id);
          }

          // Extract product details from nested structure
          return {
            id: prod.id || prod._id,
            name: prod.product?.name || prod.name || prod.productName || 'Unnamed Product',
            categoryId: catId,
            category_id: catId,
            categoryName: prod.product?.categoryName || prod.categoryName || '',
            sellingPrice: prod.product?.sellingPrice || prod.sellingPrice || prod.selling_price || prod.price || 0,
            quantity: prod.product?.quantity || prod.quantity || prod.stock || 0,
            sku: prod.product?.sku || prod.sku || '',
            description: prod.product?.description || prod.description || '',
            hsnCode: prod.product?.hsnCode || prod.hsnCode || '',
            gstPercentage: prod.product?.gstPercentage || prod.gstPercentage || 0
          };
        });

        setCategories((categoriesRes.data || []).map(cat => ({
          ...cat,
          id: cat.id || cat._id,
          isOthers: cat.isOthers || false
        })));
        setProducts(normalizedProducts);
        setSalespersons(salespersonsRes.data || []);

        // Auto-fill employee from logged-in user
        if (user && user.name && salespersonsRes.data) {
          const matchingSalesperson = salespersonsRes.data.find(sp =>
            sp.name.toLowerCase() === user.name.toLowerCase() ||
            sp.email === user.email
          );

          if (matchingSalesperson) {
            console.log('✅ Auto-filling employee:', matchingSalesperson.name);
            console.log('✅ Salesperson ID:', matchingSalesperson.id || matchingSalesperson._id);
            setFormData(prev => ({
              ...prev,
              salesperson_id: matchingSalesperson.id || matchingSalesperson._id || user.id
            }));
          } else {
            console.log('⚠️ No matching salesperson found for user:', user.name);
            // For admin users, use their user ID as fallback
            console.log('✅ Using admin user ID as fallback:', user.id);
            setFormData(prev => ({
              ...prev,
              salesperson_id: user.id
            }));
          }
        }

        if (normalizedProducts.length === 0) {
          toast.warning('No products found in database');
        }
      } catch (apiError) {
        console.error('❌ Direct API call failed:', apiError);
        throw apiError;
      }
    } catch (error) {
      console.error('❌ Failed to load form data:', error);
      toast.error('Failed to load form data. Please ensure backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const fetchEstimateForEdit = async (estimateId) => {
    try {
      // Fetch estimate and form data in parallel to ensure we have categories for mapping
      const [response, fetchedFormData] = await Promise.all([
        axios.get(`/sales/estimates/${estimateId}`),
        getFormData()
      ]);

      const estimate = response.data;
      const fetchedCategories = fetchedFormData.categories || [];
      const fetchedProducts = fetchedFormData.products || [];

      // Update reference data if needed
      if (categories.length === 0) setCategories(fetchedCategories);

      setFormData({
        salesperson_id: estimate.salesperson_id || '',
        customer: {
          name: estimate.customer_name || '',
          phone: estimate.customer_phone || '',
          email: estimate.customer_email || '',
          address: estimate.customer_address || '',
          gstNumber: estimate.customer_gst || '',
          state: estimate.party_state_name || estimate.customer_state || ''
        },
        dispatched_from: estimate.dispatched_from || '',
        delivered_to: estimate.delivered_to || '',
        termsAndConditions: estimate.termsAndConditions || '',
        materialDescription: estimate.materialDescription || '',
        date: estimate.date ? new Date(estimate.date).toISOString().split('T')[0] : (estimate.createdAt ? new Date(estimate.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0])
      });

      if (estimate.items && estimate.items.length > 0) {
        const itemsWithIds = estimate.items.map((item, idx) => {
          // Robust category ID extraction - checking all possible fields
          let catId = item.category_id ||
            item.categoryId ||
            item.category_name ||
            item.categoryName ||
            (typeof item.category === 'object' ? item.category?.id || item.category?._id || item.category?.name || item.category?.categoryName : item.category) ||
            '';

          // Robust product ID extraction
          let prodId = item.product_id ||
            item.productId ||
            (typeof item.product === 'object' ? item.product?.id || item.product?._id : item.product) ||
            '';

          // If catId is a name or missing, try to find the actual ID or infer from product
          if (!catId || !/^[0-9a-fA-F]{24}$/.test(String(catId))) {
            const foundCat = (fetchedCategories || []).find(c =>
              (c.name || '').toLowerCase() === String(catId).toLowerCase() ||
              (c.categoryName || '').toLowerCase() === String(catId).toLowerCase() ||
              (c.id || c._id) === String(catId)
            );

            if (foundCat) {
              catId = foundCat.id || foundCat._id;
            } else if (prodId) {
              // Try inferring from product if possible
              const foundProd = (fetchedProducts || []).find(p => String(p.id || p._id) === String(prodId));
              if (foundProd) {
                catId = foundProd.category_id || foundProd.categoryId;
              }
            } else if (item.product_name && !prodId) {
              // Infer Others if name exists but no ID
              const othersCat = (fetchedCategories || []).find(c =>
                c.isOthers ||
                (c.name || '').toLowerCase() === 'others' ||
                (c.name || '').toLowerCase() === 'other'
              );
              if (othersCat) {
                catId = othersCat.id || othersCat._id;
              }
            }
          }

          const selectedCategory = (fetchedCategories || []).find(c => String(c.id || c._id) === String(catId));
          const isOthers = selectedCategory?.isOthers ||
            (selectedCategory?.name || '').toLowerCase() === 'others' ||
            (selectedCategory?.name || '').toLowerCase() === 'other' ||
            item.isOthers;

          return {
            ...item,
            category_id: catId,
            product_id: prodId,
            product_name: item.product_name || item.name || (typeof item.product === 'object' ? item.product.name : ''),
            hsn_code: item.hsn_code || (typeof item.product === 'object' ? item.product.hsnCode : '') || item.productId?.product?.hsnCode || '',
            isOthers: !!isOthers,
            showFreeText: !!isOthers && !prodId, // Show free text if it's "Other" category and no product_id
            unit_type: String(item.unit_type || item.unitType || (isOthers ? 'PCS' : 'SFT')).toUpperCase(),
            // Ensure numeric values are preserved
            quantity: parseFloat(item.quantity) || 0,
            unit_price: parseFloat(item.unit_price) || 0,
            discount: parseFloat(item.discount) || 0,
            gst_rate: (item.gst_rate !== undefined && item.gst_rate !== null && item.gst_rate !== '') ? parseFloat(item.gst_rate) : 0,
            // Calculate proper amounts
            amount: item.amount || 0,
            gst_amount: item.gst_amount || 0,
            total_amount: item.total_amount || item.totalAmount || 0,
            subtotal: item.subtotal || item.total_amount || 0
          };
        });

        setItems(itemsWithIds);
        // Check if any item has a discount to enable the toggle
        const hasDiscount = itemsWithIds.some(item => (parseFloat(item.discount) || 0) > 0);
        setShowDiscount(hasDiscount);

        // ACTIVATE WOOD WORK MODE if detected in backend data
        if (estimate.isWoodWork) {
          console.log('🪵 Activating Wood Work mode for editing...');
          setIsWoodWorkMode(true);
        } else {
          setIsWoodWorkMode(false);
        }
      }
    } catch (error) {
      console.error('Failed to load estimate:', error);
      toast.error('Failed to load estimate for editing');
    }
  };

  // History functions
  // Effect for debounced search
  useEffect(() => {
    // Skip on initial render
    if (!dataFetched) return;

    const timer = setTimeout(() => {
      fetchEstimates(0, 1); // Reset to page 1 on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchEstimates = async (retryCount = 0, page = 1) => {
    try {
      setHistoryLoading(true);
      const currentFilters = filtersRef.current || filters;
      const currentSearch = (searchTermRef.current !== undefined ? searchTermRef.current : searchTerm) || '';
      const queryParams = new URLSearchParams({
        page,
        limit: 10
      });

      if (currentFilters.status && currentFilters.status !== 'all') queryParams.append('status', currentFilters.status);
      if (currentFilters.startDate) queryParams.append('startDate', currentFilters.startDate);
      if (currentFilters.endDate) queryParams.append('endDate', currentFilters.endDate);
      if (currentFilters.minAmount) queryParams.append('minAmount', currentFilters.minAmount);
      if (currentFilters.maxAmount) queryParams.append('maxAmount', currentFilters.maxAmount);
      if (currentFilters.isWoodWork && currentFilters.isWoodWork !== 'all') queryParams.append('isWoodWork', currentFilters.isWoodWork);
      if (currentFilters.salesperson_id && currentFilters.salesperson_id !== 'all') queryParams.append('salesperson_id', currentFilters.salesperson_id);

      if (currentSearch.trim()) {
        queryParams.append('search', currentSearch.trim());
      }

      const response = await axios.get(`/sales/estimates?${queryParams.toString()}`);

      console.log('📋 Raw estimates from backend:', response.data.estimates);

      // Normalize the estimates data to ensure consistent field names
      const normalizedEstimates = (response.data.estimates || []).map(estimate => {
        return {
          ...estimate,
          customer_name: estimate.customer_name ||
            estimate.customer?.name ||
            estimate.customerName ||
            estimate.client_name ||
            'Walk-in Customer',
          customer_phone: estimate.customer_phone ||
            estimate.customer?.phone ||
            estimate.customerPhone ||
            estimate.phone ||
            'N/A',
          customer_email: estimate.customer_email ||
            estimate.customer?.email ||
            estimate.customerEmail ||
            estimate.email ||
            '',
          customer_address: estimate.customer_address ||
            estimate.customer?.address ||
            estimate.customerAddress ||
            estimate.address ||
            '',
          totalAmount: estimate.totalAmount || estimate.total_amount || estimate.amount || 0,
          total_amount: estimate.totalAmount || estimate.total_amount || estimate.amount || 0,
          termsAndConditions: estimate.termsAndConditions || '',
          materialDescription: estimate.materialDescription || ''
        };
      });

      setEstimates(normalizedEstimates);
      const paginationData = response.data.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10
      };
      currentPageRef.current = paginationData.currentPage || 1;
      setPagination(paginationData);
      setDataFetched(true);
      setLastUpdated(Date.now());
    } catch (error) {
      console.error('Failed to load estimates:', error);
      if (error.response?.status === 429 && retryCount < 1) {
        setTimeout(() => fetchEstimates(retryCount + 1, page), 1000);
        return;
      }
      toast.error('Failed to load estimates');
    } finally {
      setHistoryLoading(false);
    }
  };

  const filterEstimates = () => {
    // Backend now handles search filtering. 
    // We only apply sorting locally here.
    let filtered = estimates;

    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'customer':
            aValue = (a.customer_name || '').toLowerCase();
            bValue = (b.customer_name || '').toLowerCase();
            break;
          case 'date':
            aValue = new Date(a.date || a.created_at || 0);
            bValue = new Date(b.date || b.created_at || 0);
            break;
          case 'amount':
            aValue = parseFloat(a.totalAmount || a.total_amount || 0);
            bValue = parseFloat(b.totalAmount || b.total_amount || 0);
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setFilteredEstimates(filtered);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // ── Proforma extraction handler ─────────────────────────────────────────────
  const handleProformaUpload = async () => {
    if (!proformaFile) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setProformaExtracting(true);
      const formDataUpload = new FormData();
      formDataUpload.append('poFile', proformaFile);

      console.log('🤖 Sending Proforma for AI extraction...');
      const response = await axios.post('/admin/extract-po?type=proforma', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.success && response.data?.data) {
        const extracted = response.data.data;
        console.log('✅ AI Extracted Proforma Data:', extracted);

        const newAiFields = new Set();
        const updatedCustomer = { ...formData.customer };
        if (extracted.customer_name) { updatedCustomer.name = extracted.customer_name; newAiFields.add('customer.name'); }
        if (extracted.phone_number)  { updatedCustomer.phone = extracted.phone_number; newAiFields.add('customer.phone'); }
        if (extracted.email)         { updatedCustomer.email = extracted.email; newAiFields.add('customer.email'); }
        if (extracted.address)       { updatedCustomer.address = extracted.address; newAiFields.add('customer.address'); }
        if (extracted.state)         { updatedCustomer.state = extracted.state; newAiFields.add('customer.state'); }
        if (extracted.gst_number)    { updatedCustomer.gstNumber = extracted.gst_number; newAiFields.add('customer.gstNumber'); }

        const updatedFormData = {
          ...formData,
          customer: updatedCustomer,
          dispatched_from: extracted.dispatched_from || formData.dispatched_from || '',
          delivered_to: extracted.delivered_to || formData.delivered_to || '',
          termsAndConditions: extracted.terms_and_conditions || formData.termsAndConditions || ''
        };
        if (extracted.dispatched_from)     newAiFields.add('dispatched_from');
        if (extracted.delivered_to)        newAiFields.add('delivered_to');
        if (extracted.terms_and_conditions) newAiFields.add('termsAndConditions');
        setFormData(updatedFormData);

        if (extracted.items && extracted.items.length > 0) {
          const othersCategory = categories.find(c =>
            c.isOthers ||
            (c.name || '').toLowerCase() === 'others' ||
            (c.name || '').toLowerCase() === 'other'
          );
          const othersCatId = othersCategory ? (othersCategory._id || othersCategory.id || '') : '';

          const mapUnit = (u) => {
            const unit = (u || '').toUpperCase().trim();
            const map = {
              'SFT': 'SFT', 'SQFT': 'SFT', 'SQ FT': 'SFT', 'SQ.FT': 'SFT',
              'RFT': 'RFT', 'RUNNING FEET': 'RFT',
              'PCS': 'PCS', 'PIECE': 'PCS', 'PIECES': 'PCS', 'PC': 'PCS',
              'KG': 'KG', 'KILOGRAM': 'KG',
              'LTR': 'LTR', 'LITRE': 'LTR', 'LITER': 'LTR',
              'MTR': 'MTR', 'METRE': 'MTR', 'METER': 'MTR',
              'LUMPSUM': 'LUMPSUM', 'LS': 'LUMPSUM', 'L.S': 'LUMPSUM',
              'NOS': 'NOS', 'NO': 'NOS', 'NUMBER': 'NOS', 'NUMBERS': 'NOS',
              'CM': 'CM', 'INCH': 'INCH', 'INCHES': 'INCH'
            };
            return map[unit] || 'SFT';
          };

          const newItems = extracted.items.map((item, idx) => {
            const qty = parseFloat(item.quantity) || 1;
            const price = parseFloat(item.price) || 0;
            const gst = parseFloat(item.gst) || 0;
            const amount = qty * price;
            const gstAmount = amount * (gst / 100);
            const totalAmount = amount + gstAmount;

            return {
              id: Date.now() + idx,
              category_id: othersCatId,
              category_name: 'Others',
              product_id: '',
              product_name: item.product_name || 'Custom Product',
              hsn_code: item.hsn_code || '',
              unit_type: mapUnit(item.unit),
              quantity: qty,
              unit_price: price,
              gst_rate: gst,
              discount: 0,
              amount: amount,
              gst_amount: gstAmount,
              total_amount: totalAmount,
              subtotal: totalAmount,
              tax: 0,
              isOthers: true,
              showFreeText: true,
              isWoodWork: false
            };
          });

          setItems(newItems);
          console.log('✅ Auto-filled items from proforma');
        }

        setAiFilledFields(newAiFields);
        setShowProformaUploadModal(false);
        setProformaFile(null);
        toast.success(`✅ Proforma imported! ${extracted.items?.length || 0} items and customer details auto-filled.`);
      } else {
        throw new Error('Invalid response from AI extraction service');
      }
    } catch (err) {
      console.error('❌ Proforma extraction failed:', err);
      toast.error(err.response?.data?.error || err.message || 'Failed to extract Proforma data. Please try again.');
    } finally {
      setProformaExtracting(false);
    }
  };

  const handleNewEstimate = () => {
    console.log('🆕 Creating new estimate...');
    setActiveTab('form');
    setIsEditing(false);
    setIsWoodWorkMode(false);

    // Get current date in YYYY-MM-DD format
    const today = new Date().toISOString().split('T')[0];

    setFormData({
      salesperson_id: '',
      date: today,  // Auto-fill current date
      customer: {
        name: '',
        phone: '',
        email: '',
        address: '',
        address: '',
        gstNumber: '',
        state: ''
      }
    });
    setItems([
      {
        category_id: '',
        product_id: '',
        quantity: 1,
        unit_price: 0,
        tax: 0,
        discount: 0,
        subtotal: 0
      },
      {
        category_id: '',
        product_id: '',
        quantity: 1,
        unit_price: 0,
        tax: 0,
        discount: 0,
        subtotal: 0
      },
      {
        category_id: '',
        product_id: '',
        quantity: 1,
        unit_price: 0,
        tax: 0,
        discount: 0,
        subtotal: 0
      }
    ]);

    // Reset discount toggle to default (disabled)
    setShowDiscount(false);

    // Trigger form data loading
    fetchFormData();
  };

  // ── Wood Work button handler ─────────────────────────────────────────────
  const handleWoodWorkClick = () => {
    console.log('🪵 Creating Wood Work estimate...');
    setActiveTab('form');
    setIsEditing(false);
    setIsWoodWorkMode(true);

    const today = new Date().toISOString().split('T')[0];
    setFormData({
      salesperson_id: '',
      date: today,
      customer: { name: '', phone: '', email: '', address: '', gstNumber: '', state: '' }
    });

    // Build items from template with auto-calculated qty & amount
    const templateItems = WOOD_WORK_TEMPLATE.map(tpl => {
      const qty = computeWoodWorkQty(tpl.nos, tpl.length, tpl.breadth);
      const amount = parseFloat((qty * tpl.unit_price).toFixed(2));
      return {
        category_id: '',      // resolved at save time via "Others"
        product_id: '',
        product_name: tpl.product_name,
        hsn_code: '',
        product_instructions: '',
        quantity: qty,
        unit_price: tpl.unit_price,
        unit_type: 'SFT',
        gst_rate: 0,
        gst_amount: 0,
        amount,
        total_amount: amount,
        tax: 0,
        discount: 0,
        subtotal: amount,
        isOthers: true,
        showFreeText: true,
        // Wood Work specific
        isWoodWork: true,
        categoryLabel: tpl.categoryLabel,
        nos: tpl.nos,
        length: tpl.length,
        breadth: tpl.breadth
      };
    });

    setItems(templateItems);
    setShowDiscount(false);
    fetchFormData();
  };

  // Fetch full estimate details for viewing
  const fetchEstimateDetails = async (estimateId) => {
    try {
      console.log('🔍 Fetching full details for estimate:', estimateId);
      const response = await axios.get(`/sales/estimates/${estimateId}`);
      console.log('📋 Full estimate details:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to fetch estimate details:', error);
      toast.error('Failed to load estimate details');
      return null;
    }
  };

  // Handle invoice PDF download
  const handleInvoicePdfDownload = async (invoiceId, invoiceNumber) => {
    try {
      console.log('📄 Downloading invoice PDF:', invoiceId, invoiceNumber);

      // Fetch the invoice details first
      const response = await axios.get(`/sales/invoices/${invoiceId}`);
      const invoiceData = response.data;

      // Generate PDF for the invoice
      generatePDF(invoiceData, 'invoice');

      toast.success(`Invoice ${invoiceNumber} PDF downloaded successfully!`);
    } catch (error) {
      console.error('❌ Failed to download invoice PDF:', error);
      toast.error('Failed to download invoice PDF');
    }
  };

  // Handle closed order PDF download
  const handleClosedOrderPdfDownload = async (closedOrderId, closedOrderNumber) => {
    try {
      console.log('📄 Downloading closed order PDF:', closedOrderId, closedOrderNumber);

      // Fetch the closed order details first
      const response = await axios.get(`/sales/closed-orders/${closedOrderId}`);
      const closedOrderData = response.data;

      // Generate PDF for the closed order
      generatePDF(closedOrderData, 'closed_order');

      toast.success(`Closed Order ${closedOrderNumber} PDF downloaded successfully!`);
    } catch (error) {
      console.error('❌ Failed to download closed order PDF:', error);
      toast.error('Failed to download closed order PDF');
    }
  };

  // Handle view estimate with full details
  const handleViewEstimate = async (estimate) => {
    console.log('👁️ Viewing estimate:', estimate);

    // Try to get full details from backend
    const fullDetails = await fetchEstimateDetails(estimate.id || estimate._id);

    if (fullDetails) {
      // Use full details if available
      setSelectedEstimate({
        ...fullDetails,
        // Ensure normalized fields are available
        customer_name: fullDetails.customer_name || fullDetails.customer?.name || estimate.customer_name || 'Walk-in Customer',
        customer_phone: fullDetails.customer_phone || fullDetails.customer?.phone || estimate.customer_phone || 'N/A',
        customer_email: fullDetails.customer_email || fullDetails.customer?.email || estimate.customer_email || '',
        customer_address: fullDetails.customer_address || fullDetails.customer?.address || estimate.customer_address || '',
        customer_state: fullDetails.customer_state || fullDetails.customer?.state || estimate.customer_state || '',
        items: fullDetails.items || estimate.items || [],
        totalAmount: fullDetails.totalAmount || fullDetails.total_amount || estimate.totalAmount || estimate.total_amount || 0
      });
      console.log('🔍 DEBUG customer_state:', fullDetails.customer_state, 'Full details:', fullDetails);
    } else {
      // Fallback to list data
      setSelectedEstimate(estimate);
    }

    setShowViewModal(true);
  };

  // Conversion functionality
  const handleConvertToInvoice = async (estimate) => {
    setIncludeTermsEstimateModal(false);
    setPaymentData(prev => ({ ...prev, termsAndConditions: "" }));
    console.log('🔄 Converting estimate to invoice:', estimate);
    setConvertingEstimate(estimate);
    // Fetch next invoice number
    setInvoiceNumber('');
    // Load the next invoice number in the background so the modal opens immediately
    api.get('/sales/next-document-number?type=invoice')
      .then(res => { if (res.data?.nextNumber) setInvoiceNumber(prev => prev || res.data.nextNumber); })
      .catch(err => console.error('Failed to fetch next invoice number:', err));
    setPaymentData({
      payment_type: estimate.payment_type || 'cash',
      payment_status: estimate.payment_status || 'pending',
      amount_received: estimate.amount_received || estimate.amountReceived || 0,
      dispatched_from: '',
      delivered_to: '',
      termsAndConditions: estimate.termsAndConditions || '',
      materialDescription: estimate.materialDescription || '',
      date: new Date().toISOString().split('T')[0]
    });
    setEditMode(false); // Reset edit mode
    setShowPaymentModal(true);
  };

  const handleConvertToClosedOrder = (estimate) => {
    console.log(' Converting estimate to closed order:', estimate);
    setConvertingEstimate(estimate);

    // If it's a Wood Work estimate, auto-fill the order description with the customer name
    const autoDescription = estimate.isWoodWork
      ? (estimate.customer_name || 'Wood Work Project')
      : '';

    setClosedOrderData({
      orderDescription: autoDescription,
      paymentType: 'cash',
      amountReceived: 0,
      termsAndConditions: estimate.termsAndConditions || '',
      materialDescription: estimate.materialDescription || '',
      gstNumber: estimate.customer_gst || estimate.customerId?.gstNumber || '',
      state: estimate.customer_state || estimate.party_state_name || (estimate.customer_gst ? getStateNameFromGST(estimate.customer_gst) : '') || '',
      date: new Date().toISOString().split('T')[0]
    });
    setEditMode(false); // Reset edit mode
    setShowClosedOrderModal(true);
  };

  // Duplicate estimate functionality - opens edit modal with pre-filled data
  const handleDuplicateEstimate = async (estimate) => {
    try {
      const loadingToast = toast.loading('Loading estimate for duplication...');

      console.log('📋 Preparing to duplicate estimate:', estimate.estimate_number);

      // Fetch next estimate number and full estimate details in parallel
      const [nextNumberRes, fullEstimateRes] = await Promise.all([
        axios.get('/sales/next-document-number?type=estimate'),
        api.get(`/sales/estimates/${estimate.id || estimate._id}`)
      ]);

      const nextEstimateNumber = nextNumberRes.data.nextNumber;
      const fullEstimate = fullEstimateRes.data;

      // Fetch form data to ensure we have categories and products
      const fetchedFormData = await getFormData();
      const fetchedCategories = fetchedFormData.categories || [];
      const fetchedProducts = fetchedFormData.products || [];

      // Update reference data if needed
      if (categories.length === 0) setCategories(fetchedCategories);
      if (products.length === 0) setProducts(fetchedProducts);

      // Prepare the editing estimate data (similar to fetchEstimateForEdit)
      const itemsWithIds = (fullEstimate.items || []).map((item, idx) => {
        // Robust category ID extraction
        let catId = item.category_id ||
          item.categoryId ||
          item.category_name ||
          item.categoryName ||
          (typeof item.category === 'object' ? item.category?.id || item.category?._id || item.category?.name || item.category?.categoryName : item.category) ||
          '';

        // Robust product ID extraction
        let prodId = item.product_id ||
          item.productId ||
          (typeof item.product === 'object' ? item.product?.id || item.product?._id : item.product) ||
          '';

        // If catId is a name or missing, try to find the actual ID or infer from product
        if (!catId || !/^[0-9a-fA-F]{24}$/.test(String(catId))) {
          const foundCat = fetchedCategories.find(c =>
            (c.name || '').toLowerCase() === String(catId).toLowerCase() ||
            (c.categoryName || '').toLowerCase() === String(catId).toLowerCase() ||
            (c.id || c._id) === String(catId)
          );

          if (foundCat) {
            catId = foundCat.id || foundCat._id;
          } else if (prodId) {
            // Try inferring from product if possible
            const foundProd = fetchedProducts.find(p => String(p.id || p._id) === String(prodId));
            if (foundProd) {
              catId = foundProd.category_id || foundProd.categoryId;
            }
          } else if (item.product_name && !prodId) {
            // Infer Others if name exists but no ID
            const othersCat = fetchedCategories.find(c =>
              c.isOthers ||
              (c.name || '').toLowerCase() === 'others' ||
              (c.name || '').toLowerCase() === 'other'
            );
            if (othersCat) {
              catId = othersCat.id || othersCat._id;
            }
          }
        }

        const selectedCategory = fetchedCategories.find(c => String(c.id || c._id) === String(catId));
        const isOthers = selectedCategory?.isOthers ||
          (selectedCategory?.name || '').toLowerCase() === 'others' ||
          (selectedCategory?.name || '').toLowerCase() === 'other' ||
          item.isOthers;

        const quantity = item.quantity || 1;
        const unitPrice = item.unit_price || 0;
        const gstRate = item.gst_rate || 0;
        const discount = item.discount || 0;

        const baseAmount = quantity * unitPrice;
        const discountAmount = baseAmount * (discount / 100);
        const taxableAmount = baseAmount - discountAmount;
        const gstAmount = taxableAmount * (gstRate / 100);
        const totalAmount = taxableAmount + gstAmount;

        return {
          category_id: catId,
          product_id: prodId,
          product_name: item.product_name || '',
          quantity: quantity,
          unit_price: unitPrice,
          unit_type: item.unit_type || 'SFT',
          hsn_code: item.hsn_code || '',
          gst_rate: gstRate,
          discount: discount,
          amount: taxableAmount,
          gst_amount: gstAmount,
          total_amount: totalAmount,
          subtotal: totalAmount,
          isOthers: isOthers,
          showFreeText: isOthers && !prodId
        };
      });

      // Set up the editing estimate with customer details
      setEditingEstimate({
        ...fullEstimate,
        _id: null, // Clear ID to indicate this is a new estimate
        id: null,
        estimate_number: nextEstimateNumber, // Use validated next number
        items: itemsWithIds,
        dispatched_from: fullEstimate.dispatched_from || '',
        delivered_to: fullEstimate.delivered_to || '',
        isDuplicate: true // Flag to indicate this is a duplicate
      });

      // Set customer details for editing
      setEditingCustomer({
        name: fullEstimate.customer?.name || fullEstimate.customer_name || '',
        phone: fullEstimate.customer?.phone || fullEstimate.customer_phone || '',
        email: fullEstimate.customer?.email || fullEstimate.customer_email || '',
        address: fullEstimate.customer?.address || fullEstimate.customer_address || '',
        gstNumber: fullEstimate.customer?.gstNumber || fullEstimate.customer_gst || '',
        state: fullEstimate.customer?.state || fullEstimate.customer_state || fullEstimate.party_state_name || ((fullEstimate.customer?.gstNumber || fullEstimate.customer_gst) ? getStateNameFromGST(fullEstimate.customer?.gstNumber || fullEstimate.customer_gst) : '') || ''
      });

      // Set the editing products
      setEditingProducts(recalcItemsAmounts(itemsWithIds));
      setEditingApplyDiscount(itemsWithIds.some(item => (item.discount || 0) > 0));

      toast.dismiss(loadingToast);
      toast.success(`Estimate duplicated with new number: ${nextEstimateNumber}`);

      // Open the edit estimate modal
      setShowEditEstimateModal(true);
    } catch (error) {
      console.error('Failed to load estimate for duplication:', error);
      toast.error(error.response?.data?.error || 'Failed to load estimate for duplication');
    }
  };

  // Delete estimate functionality (Admin only)
  const handleDeleteEstimate = (estimate) => {
    console.log('🗑️ Initiating delete for estimate:', estimate.estimate_number);
    setDeletingEstimate(estimate);
    setShowDeleteModal(true);
  };

  const confirmDeleteEstimate = async () => {
    try {
      const loadingToast = toast.loading('Deleting estimate...');

      console.log('🗑️ Deleting estimate:', deletingEstimate.estimate_number);

      await api.delete(`/sales/estimates/${deletingEstimate.id || deletingEstimate._id}`);

      toast.dismiss(loadingToast);
      toast.success('Estimate deleted successfully');

      // Close modal and reset state
      setShowDeleteModal(false);
      setDeletingEstimate(null);

      // Refresh the estimates list - stay on current page
      await fetchEstimates(0, pagination.currentPage);
    } catch (error) {
      console.error('Failed to delete estimate:', error);
      if (error.response?.status === 403) {
        toast.error('Unauthorized: Admin access required');
      } else {
        toast.error(error.response?.data?.error || 'Failed to delete estimate');
      }
    }
  };

  const handleProductEdit = (index, field, value) => {
    const updatedProducts = [...editingProducts];

    // Handle numeric fields allowing empty string
    let newValue = value;
    if (['quantity', 'unit_price', 'gst_rate', 'discount', 'nos', 'length', 'breadth'].includes(field)) {
      newValue = value === '' ? '' : parseFloat(value);
    }

    updatedProducts[index] = {
      ...updatedProducts[index],
      [field]: newValue
    };

    // Auto-calculate quantity if in WoodWork mode and dimensions changed
    if (isWoodWorkMode && ['nos', 'length', 'breadth'].includes(field)) {
      const qty = computeWoodWorkQty(
        field === 'nos' ? newValue : (updatedProducts[index].nos || 0),
        field === 'length' ? newValue : (updatedProducts[index].length || 0),
        field === 'breadth' ? newValue : (updatedProducts[index].breadth || 0)
      );
      updatedProducts[index].quantity = qty;
    }

    // Handle category change
    if (field === 'category_id') {
      const selectedCategory = categories.find(c => String(c.id || c._id) === String(value));
      const isOthers = selectedCategory?.isOthers || false;
      updatedProducts[index].isOthers = isOthers;
      updatedProducts[index].product_id = '';
      updatedProducts[index].product_name = '';
      updatedProducts[index].unit_type = isOthers ? 'PCS' : 'SFT';
      updatedProducts[index].unit_price = 0;
    }

    // Recalculate amounts when relevant fields change
    if (['quantity', 'unit_price', 'gst_rate', 'discount', 'nos', 'length', 'breadth'].includes(field)) {
      // Use 0 for calculations if value is empty string
      const quantity = updatedProducts[index].quantity === '' ? 0 : (updatedProducts[index].quantity || 0);
      const unitPrice = updatedProducts[index].unit_price === '' ? 0 : (updatedProducts[index].unit_price || 0);
      const gstRate = updatedProducts[index].gst_rate === '' ? 0 : (updatedProducts[index].gst_rate || 0);
      const discount = updatedProducts[index].discount === '' ? 0 : (updatedProducts[index].discount || 0);

      // Calculate amounts correctly: Discount first, then GST
      const baseAmount = quantity * unitPrice;
      const discountAmount = baseAmount * (discount / 100);
      const taxableAmount = baseAmount - discountAmount;
      const gstAmount = taxableAmount * (gstRate / 100);
      const totalAmount = taxableAmount + gstAmount;

      // Update calculated fields
      updatedProducts[index].amount = taxableAmount;
      updatedProducts[index].gst_amount = gstAmount;
      updatedProducts[index].total_amount = totalAmount;
      updatedProducts[index].subtotal = totalAmount;
    }

    setEditingProducts(updatedProducts);
  };

  const handleRemoveProduct = (index) => {
    const updatedProducts = [...editingProducts];
    updatedProducts.splice(index, 1);
    setEditingProducts(updatedProducts);
  };

  const handleAddProduct = () => {
    const newProduct = {
      category_id: '',
      product_id: '',
      product_name: '',
      hsn_code: '',
      product_instructions: '',
      quantity: 1,
      unit_price: 0,
      unit_type: 'SFT',
      gst_rate: 0,
      subtotal: 0,
      tax: 0,
      discount: 0,
      description: '',
      nos: 1,
      length: 1,
      breadth: 1
    };
    setEditingProducts([...editingProducts, newProduct]);
  };

  const calculateInvoiceTotal = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => {
      const baseAmount = roundForCalculation(item.quantity * item.unit_price);
      const discountAmount = roundForCalculation(baseAmount * ((item.discount || 0) / 100));
      const taxableAmount = roundForCalculation(baseAmount - discountAmount);
      const gstAmount = roundForCalculation(taxableAmount * ((item.gst_rate || 0) / 100));
      return roundForCalculation(sum + taxableAmount + gstAmount);
    }, 0);
  };

  const handleSaveProductEdits = () => {
    // Calculate total amount
    const totalAmount = editingProducts.reduce((sum, item) => roundForCalculation(sum + (item.quantity * item.unit_price)), 0);

    // Return to the appropriate modal based on context
    if (editModalContext === 'convert') {
      // If editing from invoice conversion, update convertingEstimate
      const updatedEstimate = {
        ...convertingEstimate,
        items: recalcItemsAmounts(editingProducts),
        totalAmount: totalAmount,
        total_amount: totalAmount
      };
      setConvertingEstimate(updatedEstimate);
      setShowEditProductsModal(false);
      setShowPaymentModal(true);
    } else if (editModalContext === 'edit') {
      // If editing from estimate edit modal, update editingEstimate (preserve all properties!)
      const updatedEstimate = {
        ...editingEstimate,  // Use editingEstimate, not convertingEstimate!
        items: recalcItemsAmounts(editingProducts),
        totalAmount: totalAmount,
        total_amount: totalAmount
      };
      setEditingEstimate(updatedEstimate);
      setShowEditProductsModal(false);
      setShowEditEstimateModal(true);
    } else {
      // If editing from closed order, update convertingEstimate
      const updatedEstimate = {
        ...convertingEstimate,
        items: recalcItemsAmounts(editingProducts),
        totalAmount: totalAmount,
        total_amount: totalAmount
      };
      setConvertingEstimate(updatedEstimate);
      setShowEditProductsModal(false);
      setShowClosedOrderModal(true);
    }
  };

  // Handler for saving estimate edits
  const handleSaveEstimateEdits = async () => {
    try {
      setLoading(true);

      // Calculate total from edited items
      const calculateTotal = (items) => {
        return items.reduce((sum, item) => {
          const baseAmount = roundForCalculation((item.quantity || 0) * (item.unit_price || item.unitPrice || 0));
          const discountAmount = roundForCalculation(baseAmount * ((item.discount || 0) / 100));
          const taxableAmount = roundForCalculation(baseAmount - discountAmount);
          const gstAmount = roundForCalculation(taxableAmount * ((item.gst_rate || 0) / 100));
          return roundForCalculation(sum + taxableAmount + gstAmount);
        }, 0);
      };

      const totalAmount = calculateTotal(editingProducts);

      // Debug salesperson data
      console.log('💾 Salesperson debug:', {
        salespersonId: editingEstimate.salespersonId,
        salesperson_id: editingEstimate.salesperson_id,
        salesperson: editingEstimate.salesperson
      });
      console.log('💾 Salesperson object details:', JSON.stringify(editingEstimate.salesperson, null, 2));

      // Extract salesperson ID - try multiple possible paths
      let salespersonId = null;

      // First check if salesperson object exists and extract its ID
      if (editingEstimate.salesperson && typeof editingEstimate.salesperson === 'object') {
        salespersonId = editingEstimate.salesperson.id || editingEstimate.salesperson._id;
      } else if (editingEstimate.salesperson && typeof editingEstimate.salesperson === 'string') {
        salespersonId = editingEstimate.salesperson;
      } else if (editingEstimate.salespersonId) {
        if (typeof editingEstimate.salespersonId === 'object') {
          salespersonId = editingEstimate.salespersonId._id || editingEstimate.salespersonId.id;
        } else {
          salespersonId = editingEstimate.salespersonId;
        }
      } else if (editingEstimate.salesperson_id) {
        salespersonId = editingEstimate.salesperson_id;
      }

      // Fallback: try to get from selectedEstimate if editingEstimate doesn't have it
      if (!salespersonId && selectedEstimate) {
        if (selectedEstimate.salespersonId) {
          if (typeof selectedEstimate.salespersonId === 'object') {
            salespersonId = selectedEstimate.salespersonId._id || selectedEstimate.salespersonId.id;
          } else {
            salespersonId = selectedEstimate.salespersonId;
          }
        } else if (selectedEstimate.salesperson_id) {
          salespersonId = selectedEstimate.salesperson_id;
        } else if (selectedEstimate.salesperson && typeof selectedEstimate.salesperson === 'object') {
          salespersonId = selectedEstimate.salesperson.id || selectedEstimate.salesperson._id;
        }
      }

      // Final Fallback: Use logged-in user ID as the salesperson
      if (!salespersonId && user) {
        console.log('💾 Using logged-in user as salesperson fallback:', user);
        salespersonId = user.id || user._id;
      }

      console.log('💾 Resolved salesperson ID:', salespersonId);

      // Validate that we have a salesperson ID
      if (!salespersonId) {
        toast.error('Unable to determine salesperson. Please contact support.');
        setLoading(false);
        return;
      }

      // Prepare update data
      // Resolve salesperson name
      let resolvedSalespersonName = user?.name || user?.username || user?.email || 'Unknown';
      if (salespersonId) {
        const selectedSP = salespersons.find(sp => sp.id === salespersonId || sp._id === salespersonId);
        if (selectedSP) {
          resolvedSalespersonName = selectedSP.name;
        } else if (user && (user.id === salespersonId || user._id === salespersonId || user.salespersonId === salespersonId)) {
          resolvedSalespersonName = user.name || user.username || user.email || 'Unknown';
        }
      }

      const updateData = {
        salesperson_id: salespersonId,
        salesperson_name: resolvedSalespersonName,
        customer: {
          name: editingCustomer.name,
          phone: editingCustomer.countryCode
            ? (editingCustomer.countryCode.trim() + editingCustomer.phone.trim())
            : editingCustomer.phone,
          email: editingCustomer.email,
          address: editingCustomer.address,
          gstNumber: editingCustomer.gstNumber || '',
          state: editingCustomer.state || ''
        },
        items: recalcItemsAmounts(editingProducts),
        total_amount: totalAmount,
        last_updated_by_name: user?.name || user?.username || 'Unknown', // Track who updated it
        termsAndConditions: editingEstimate.termsAndConditions || '', // Include terms and conditions
        customer_state: editingCustomer.state || '', // Add customer state to payload
        date: editingEstimate.date
      };

      console.log('💾 Saving estimate edits:', updateData);
      console.log('💾 Full editingEstimate object:', editingEstimate);
      console.log('💾 Estimate ID candidates:', {
        _id: editingEstimate._id,
        id: editingEstimate.id,
        estimate_id: editingEstimate.estimate_id
      });

      // Update the estimate via the API (use api utility which already has /api prefix)
      // Try multiple possible ID properties
      const estimateId = editingEstimate._id || editingEstimate.id || editingEstimate.estimate_id;

      console.log('💾 Resolved Estimate ID:', estimateId);

      // Check if this is a duplicate operation
      if (editingEstimate.isDuplicate || !estimateId) {
        console.log('📋 Creating new estimate from duplicate');

        // Create new estimate instead of updating
        const createData = {
          salesperson_id: salespersonId,
          salesperson_name: resolvedSalespersonName,
          customer_name: editingCustomer.name,
          customer_phone: editingCustomer.countryCode
            ? (editingCustomer.countryCode.trim() + editingCustomer.phone.trim())
            : editingCustomer.phone,
          customer_email: editingCustomer.email,
          customer_address: editingCustomer.address,
          customer_gst: editingEstimate.customer_gst || '',
          dispatched_from: editingEstimate.dispatched_from || '',
          delivered_to: editingEstimate.delivered_to || '',
          customer: {
            name: editingCustomer.name,
            phone: editingCustomer.countryCode
              ? (editingCustomer.countryCode.trim() + editingCustomer.phone.trim())
              : editingCustomer.phone,
            email: editingCustomer.email,
            address: editingCustomer.address,
            gstNumber: editingCustomer.gstNumber || ''
          },
          items: recalcItemsAmounts(editingProducts),
          totalAmount: totalAmount,
          total_amount: totalAmount,
          termsAndConditions: editingEstimate.termsAndConditions || '', // Include terms and conditions
          date: editingEstimate.date
        };

        const response = await api.post('/sales/estimates', createData);

        if (response.data) {
          toast.success(`New estimate created successfully! Estimate: ${response.data.id}`);
          setShowEditEstimateModal(false);
          // Refresh the estimates list - stay on current page
          await fetchEstimates(0, pagination.currentPage);
        }
      } else {
        // Normal update flow
        if (!estimateId) {
          console.error('❌ Estimate object:', editingEstimate);
          throw new Error('Estimate ID is missing - check console for estimate object structure');
        }
        const response = await api.put(`/sales/estimates/${estimateId}`, updateData);

        if (response.data) {
          toast.success('Estimate updated successfully');

          // Update the selected estimate with new data
          // Update the selected estimate with new data
          const updatedEstimate = {
            ...selectedEstimate, // Start with current selected estimate to preserve all fields
            ...editingEstimate,  // Override with edited fields
            customer_name: editingCustomer.name,
            customer_phone: editingCustomer.countryCode
              ? (editingCustomer.countryCode.trim() + editingCustomer.phone.trim())
              : editingCustomer.phone,
            customer_email: editingCustomer.email,
            customer_address: editingCustomer.address,
            // Ensure customer object is also updated for PDF
            customer: {
              ...selectedEstimate?.customer,
              name: editingCustomer.name,
              phone: editingCustomer.phone,
              email: editingCustomer.email,
              address: editingCustomer.address,
              gstNumber: editingCustomer.gstNumber || selectedEstimate?.customer?.gstNumber || ''
            },
            items: editingProducts.map(item => ({
              ...item,
              quantity: parseFloat(item.quantity) || 0,
              unit_price: parseFloat(item.unit_price || item.unitPrice) || 0,
              gst_rate: parseFloat(item.gst_rate) || 0,
              gst_amount: parseFloat(item.gst_amount) || 0,
              amount: parseFloat(item.amount) || 0,
              total_amount: parseFloat(item.total_amount) || 0,
              discount: parseFloat(item.discount) || 0,
              hsn_code: item.hsn_code || '',
              unit_type: item.unit_type || 'SFT'
            })),
            totalAmount: totalAmount,
            total_amount: totalAmount,
            date: editingEstimate.date
          };

          setSelectedEstimate(updatedEstimate);
          setShowEditEstimateModal(false);

          // Refresh the estimates list - stay on current page
          fetchEstimates(0, pagination.currentPage);
        }
      } // Close else block
    } catch (error) {
      console.error('❌ Failed to save estimate edits:', error);
      toast.error(error.response?.data?.message || 'Failed to save changes');
    } finally {
      setLoading(false);
    }
  };



  const handleClosedOrderSubmit = async () => {
    try {
      setLoading(true);

      console.log('🔄 Converting estimate to closed order:', convertingEstimate);
      console.log('📝 Closed order data:', closedOrderData);

      // Validate items before conversion
      const items = recalcItemsAmounts(Array.isArray(convertingEstimate.items) ? convertingEstimate.items : []);

      if (items.length === 0) {
        toast.error('Cannot convert: No products in the estimate');
        setLoading(false);
        return;
      }

      // Proceed with conversion
      await proceedWithClosedOrderConversion();

    } catch (error) {
      console.error('❌ Failed to convert estimate to closed order:', error);
      console.error('❌ Error response:', error.response?.data);

      // Show user-friendly error message
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to convert estimate to closed order';

      toast.error(`Conversion failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Separate function for actual closed order conversion
  const proceedWithClosedOrderConversion = async () => {
    try {
      const items = recalcItemsAmounts(Array.isArray(convertingEstimate.items) ? convertingEstimate.items : []);

      // Prepare closed order data
      const closedOrderRequest = {
        payment_type: closedOrderData.paymentType,
        amount_received: parseFloat(closedOrderData.amountReceived) || 0,
        order_description: closedOrderData.orderDescription,
        customer_type: 'B2C',
        payment_status: closedOrderData.paymentStatus,
        gst_number: closedOrderData.gstNumber || '',
        party_state_name: closedOrderData.state || '',
        customer_state: closedOrderData.state || '', // Add customer_state to match backend schema
        dispatched_from: closedOrderData.dispatched_from || '',
        delivered_to: closedOrderData.delivered_to || '',
        salesperson_name: convertingEstimate.salesperson?.name || user?.name,
        converted_by_name: user?.name, // Explicitly track who performed the conversion
        date: closedOrderData.date,
        // Include updated items if they were edited
        items: items,
        termsAndConditions: closedOrderData.termsAndConditions !== undefined ? closedOrderData.termsAndConditions : (convertingEstimate.termsAndConditions || ''),
        materialDescription: closedOrderData.materialDescription !== undefined ? closedOrderData.materialDescription : (convertingEstimate.materialDescription || '')
      };

      // Send request to convert estimate to closed order
      const response = await axios.put(
        `/sales/estimates/${convertingEstimate.id || convertingEstimate._id}/close`,
        closedOrderRequest
      );

      console.log('✅ Estimate converted to closed order successfully:', response.data);

      toast.success('Estimate converted to closed order successfully!');
      setShowClosedOrderModal(false);
      setConvertingEstimate(null);

      // Refresh estimates to remove the converted one - stay on current page
      await fetchEstimates(0, pagination.currentPage);

      // Navigate to closed orders page
      navigate('/admin/closed-orders');

    } catch (error) {
      console.error('❌ Failed to convert estimate to closed order:', error);
      console.error('❌ Error response:', error.response?.data);

      // Show user-friendly error message
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to convert estimate to closed order';

      toast.error(`Conversion failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (incomingPaymentData) => {
    try {
      setLoading(true);

      console.log('🔄 Converting estimate to invoice:', convertingEstimate);
      console.log('💰 Payment data:', paymentData);
      console.log('📦 Editing products:', editingProducts);
      console.log('📦 Original items:', convertingEstimate.items);

      // Validate items before conversion
      const items = recalcItemsAmounts(Array.isArray(convertingEstimate.items) ? convertingEstimate.items : []);

      if (items.length === 0) {
        toast.error('Cannot convert: No products in the estimate');
        setLoading(false);
        return;
      }



      // Proceed with conversion
      await proceedWithInvoiceConversion(incomingPaymentData);

    } catch (error) {
      console.error('❌ Failed to convert estimate to invoice:', error);
      console.error('❌ Error response:', error.response?.data);

      // Show user-friendly error message
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to convert estimate to invoice';

      toast.error(`Conversion failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Separate function for actual invoice conversion
  const proceedWithInvoiceConversion = async (incomingPaymentData) => {
    // Merge incoming data with state data
    const currentPaymentData = { ...paymentData, ...incomingPaymentData };
    try {
      // Prepare invoice data with proper structure
      const invoiceData = {
        // Basic info
        invoice_number: invoiceNumber, // Add manual/auto invoice number
        estimate_id: convertingEstimate.id || convertingEstimate._id, // Include original estimate ID
        salesperson_id: convertingEstimate.salesperson?.id || convertingEstimate.salesperson_id || user?.id,
        salesperson_name: convertingEstimate.salesperson?.name || user?.name,
        converted_by_name: user?.name, // Explicitly track who performed the conversion
        date: paymentData.date || new Date().toISOString().split('T')[0],

        // Customer info
        customer_name: editingCustomer.name || convertingEstimate.customer_name || 'Walk-in Customer',
        customer_phone: (editingCustomer.countryCode
          ? (editingCustomer.countryCode.trim() + editingCustomer.phone.trim())
          : editingCustomer.phone) || convertingEstimate.customer_phone || '9999999999',
        customer_email: editingCustomer.email || convertingEstimate.customer_email || '',
        customer_address: editingCustomer.address || convertingEstimate.customer_address || '',
        customer_gst: currentPaymentData.gst_number || convertingEstimate.customer_gst || '', // Use GST number from form
        gstNumber: currentPaymentData.gst_number || '', // Add the field backend expects
        party_state_name: currentPaymentData.state || convertingEstimate.party_state_name || convertingEstimate.customer_state || '',
        customer_state: currentPaymentData.state || convertingEstimate.customer_state || convertingEstimate.party_state_name || '', // Add customer_state

        // Items - use edited products if available, otherwise original items
        items: (editingProducts.length > 0 ? editingProducts : convertingEstimate.items || []).map((item, idx) => {
          // Calculate amounts properly. Keep fractional quantities (Wood Work
          // area-based quantities like 15.75 SFT) - parseInt used to truncate them
          // while the amounts below were still computed from the full quantity.
          const qty = parseFloat(item.quantity) || 1;
          const baseAmount = qty * (parseFloat(item.unit_price) || 0);
          const discountAmount = baseAmount * ((item.discount || 0) / 100);
          const taxableAmount = baseAmount - discountAmount;
          const gstRate = parseFloat(item.gst_rate) || 0;
          const gstAmount = taxableAmount * (gstRate / 100);
          const totalAmount = taxableAmount + gstAmount;

          const mappedItem = {
            product_id: item.product_id || item.productId,
            product_name: item.product_name || 'Product',
            quantity: qty,
            unit_price: parseFloat(item.unit_price) || 0,
            unit_type: item.unit_type || 'SFT',
            hsn_code: item.hsn_code || '',
            gst_rate: gstRate,
            gst_amount: gstAmount,
            tax: parseFloat(item.tax) || 0,
            discount: parseFloat(item.discount) || 0,
            subtotal: totalAmount,
            amount: taxableAmount,
            total_amount: totalAmount,
            // Preserve Wood Work dimension fields (previously dropped on conversion)
            nos: item.nos !== undefined ? item.nos : null,
            length: item.length !== undefined ? item.length : null,
            breadth: item.breadth !== undefined ? item.breadth : null,
            isWoodWork: !!(item.isWoodWork),
            categoryLabel: item.categoryLabel || ''
          };
          console.log(`📦 Item ${idx}:`, mappedItem);
          return mappedItem;
        }),

        // Totals - recalculate properly with GST
        totalAmount: calculateInvoiceTotal(editingProducts.length > 0 ? editingProducts : convertingEstimate.items || []),
        total_amount: calculateInvoiceTotal(editingProducts.length > 0 ? editingProducts : convertingEstimate.items || []),

        // Payment info
        payment_type: currentPaymentData.payment_type || 'cash',
        payment_status: currentPaymentData.payment_status || 'pending',
        amount_received: parseFloat(currentPaymentData.amount_received) || 0,

        // Dispatch and delivery info
        dispatched_from: currentPaymentData.dispatched_from || '',
        delivered_to: currentPaymentData.delivered_to || '',

        // Status
        status: 'invoice',

        // Terms & Material Description
        termsAndConditions: includeTermsEstimateModal ? (currentPaymentData.termsAndConditions || '') : '',
        materialDescription: currentPaymentData.materialDescription !== undefined ? currentPaymentData.materialDescription : (convertingEstimate.materialDescription || '')
      };

      console.log('📤 Sending invoice data:', invoiceData);
      console.log('📤 Invoice items details:', invoiceData.items.map((item, idx) => ({
        idx,
        product_name: item.product_name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        gst_rate: item.gst_rate,
        gst_amount: item.gst_amount,
        amount: item.amount,
        total_amount: item.total_amount
      })));

      // Validate required fields
      if (!invoiceData.salesperson_id) {
        throw new Error('Salesperson ID is required');
      }
      if (!invoiceData.items || invoiceData.items.length === 0) {
        throw new Error('Items are required');
      }

      // Convert estimate to invoice by calling the correct endpoint
      const response = await axios.put(`/sales/estimates/${invoiceData.estimate_id}/invoice`, invoiceData);

      console.log('✅ Invoice created successfully:', response.data);

      toast.success('Estimate converted to invoice successfully!');
      setShowPaymentModal(false);
      setConvertingEstimate(null);

      // Refresh estimates to remove the converted one - stay on current page
      await fetchEstimates(0, pagination.currentPage);

      // Navigate to invoices page to show the new invoice
      navigate('/admin/invoices');

    } catch (error) {
      console.error('❌ Failed to convert estimate:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error message:', error.message);

      // Show user-friendly error message
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to convert estimate to invoice';

      toast.error(`Conversion failed: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormTouched(true);

    // Basic validation
    const errors = {};

    console.log('🔍 Form data for validation:', {
      salesperson_id: formData.salesperson_id,
      customer: formData.customer,
      items: items,
      itemsLength: items?.length
    });

    // Check required fields
    if (!formData.salesperson_id) {
      errors.salesperson = 'Please select a salesperson';
    }

    if (!formData.customer.name?.trim()) {
      errors.customerName = 'Customer name is required';
    }

    if (!formData.customer.phone?.trim()) {
      errors.customerPhone = 'Mobile number is required';
    } else {
      const hasCountryCode = formData.customer.countryCode?.trim();
      const cleanPhone = formData.customer.phone.replace(/\D/g, '');
      if (hasCountryCode) {
        // With country code: total combined digits should be 12-13
        const cleanCode = formData.customer.countryCode.replace(/\D/g, '');
        const total = cleanCode.length + cleanPhone.length;
        if (total < 12 || total > 13) {
          errors.customerPhone = 'With country code, total digits must be 12-13 (e.g. +91 9876543210)';
        }
      } else {
        // Without country code: exactly 10 digits
        if (cleanPhone.length !== 10) {
          errors.customerPhone = 'Mobile number must be exactly 10 digits (or add a country code)';
        }
      }
    }

    // Check if at least one item exists and is valid
    if (!items || items.length === 0) {
      errors.items = 'Please add at least one item';
    } else {
      // Wood Work items only need a name and positive quantity — skip category validation
      const invalidItems = items.filter(item => {
        if (item.isWoodWork) {
          return !item.product_name || !item.quantity || item.quantity <= 0;
        }
        const category = categories.find(c => String(c.id || c._id) === String(item.category_id));
        const isOthers = item.isOthers || category?.isOthers;

        if (isOthers) {
          return !item.category_id || !item.product_name || !item.quantity || item.quantity <= 0;
        }
        return !item.category_id || !item.product_id || !item.quantity || item.quantity <= 0;
      });
      if (invalidItems.length > 0) {
        errors.items = 'Please complete all item details (category, product, quantity)';
        console.log('❌ Invalid items found:', invalidItems.map((it, i) => ({
          index: i,
          catId: it.category_id,
          prodId: it.product_id,
          prodName: it.product_name,
          isOthers: it.isOthers,
          isWoodWork: it.isWoodWork,
          qty: it.quantity
        })));
      }
    }

    // If there are validation errors, show them and stop
    if (Object.keys(errors).length > 0) {
      console.log('❌ Form validation failed:', errors);
      Object.values(errors).forEach(error => toast.error(error));
      setLoading(false);
      return;
    }

    // TEMPORARY: Skip validation for debugging
    console.log('⚠️ BYPASSING VALIDATION FOR DEBUGGING');

    try {
      setLoading(true);

      // Prepare estimate data with fallback for empty items
      const validItems = items.filter(item => {
        // Wood Work items: just need a name and a positive quantity
        if (item.isWoodWork) {
          return item.product_name && item.quantity > 0;
        }
        const category = categories.find(c => String(c.id || c._id) === String(item.category_id));
        const isOthers = item.isOthers || category?.isOthers;

        if (isOthers) {
          return item.category_id && item.product_name && item.quantity > 0;
        }
        return item.category_id && item.product_id && item.quantity > 0;
      });

      // If no valid items, create a default item
      const finalItems = validItems.length > 0 ? validItems : [{
        category_id: categories[0]?.id || '',
        product_id: products[0]?.id || '',
        quantity: 1,
        unit_price: 100,
        tax: 0,
        discount: 0
      }];

      // Calculate totals
      let totalAmount = 0;
      const processedItems = finalItems.map(item => {
        const quantity = parseFloat(item.quantity) || 1; // parseFloat: keep fractional Wood Work quantities
        const unitPrice = parseFloat(item.unit_price) || 100; // Default to 100 if 0
        const gstRate = parseFloat(item.gst_rate) || 0; // Use gst_rate, not tax
        const discount = showDiscount ? (parseFloat(item.discount) || 0) : 0;

        // Calculate subtotal for this item - CORRECT SEQUENCE: Discount first, then GST
        const baseAmount = roundForCalculation(quantity * unitPrice);
        const discountAmount = roundForCalculation(baseAmount * (discount / 100));
        const taxableAmount = roundForCalculation(baseAmount - discountAmount);
        const gstAmount = roundForCalculation(taxableAmount * (gstRate / 100));
        const subtotal = roundForCalculation(Math.max(taxableAmount + gstAmount, 0));

        totalAmount = roundForCalculation(totalAmount + subtotal);

        return {
          category_id: item.category_id,
          product_id: item.product_id,
          product_name: item.product_name || '',
          hsn_code: item.hsn_code || '',
          product_instructions: item.product_instructions || '',
          quantity: quantity,
          unit_price: unitPrice,
          unit_type: item.unit_type || 'SFT',
          gst_rate: item.gst_rate || 0,
          gst_amount: gstAmount,
          amount: taxableAmount,
          total_amount: subtotal,
          tax: item.tax || 0,
          discount: discount,
          subtotal: subtotal,
          // Wood Work dimension fields
          nos: item.nos !== undefined ? item.nos : null,
          length: item.length !== undefined ? item.length : null,
          breadth: item.breadth !== undefined ? item.breadth : null,
          isWoodWork: !!(item.isWoodWork),
          categoryLabel: item.categoryLabel || ''
        };
      });

      // Calculate correct total from all items

      // Resolve salesperson name
      let resolvedSalespersonName = user?.name || user?.username || user?.email || 'Unknown';
      if (formData.salesperson_id) {
        const selectedSP = salespersons.find(sp => sp.id === formData.salesperson_id || sp._id === formData.salesperson_id);
        if (selectedSP) {
          resolvedSalespersonName = selectedSP.name;
        } else if (user && (user.id === formData.salesperson_id || user._id === formData.salesperson_id || user.salespersonId === formData.salesperson_id)) {
          resolvedSalespersonName = user.name || user.username || user.email || 'Unknown';
        }
      }

      const estimateData = {
        salesperson_id: formData.salesperson_id || user?.id,
        salesperson_name: resolvedSalespersonName,
        date: formData.date || new Date().toISOString().split('T')[0],
        customer_name: formData.customer.name?.trim() || 'Walk-in Customer',
        customer_phone: formData.customer.countryCode
          ? (formData.customer.countryCode.trim() + formData.customer.phone.trim())
          : (formData.customer.phone?.replace(/\D/g, '') || '9999999999'),
        customer_email: formData.customer.email?.trim() || '',
        customer_address: formData.customer.address?.trim() || '',
        customer_state: formData.customer.state?.trim() || '',
        customer_gst: formData.customer.gstNumber?.trim() || '',
        dispatched_from: formData.dispatched_from || '',
        delivered_to: formData.delivered_to || '',
        termsAndConditions: formData.termsAndConditions || '',
        materialDescription: formData.materialDescription || '',
        isWoodWork: isWoodWorkMode, // Root-level flag to mark this as a wood work / interior project estimate
        items: processedItems,
        totalAmount: totalAmount, // Store exact calculated value without rounding
        total_amount: totalAmount, // Store exact calculated value without rounding
        status: 'estimate'
      };

      console.log('📤 Submitting estimate data:', estimateData);
      console.log('📤 Items being sent:', estimateData.items);
      console.log('📤 Total Amount:', estimateData.totalAmount);
      console.log('📤 Total Amount Type:', typeof estimateData.totalAmount);
      console.log('📤 Full JSON:', JSON.stringify(estimateData, null, 2));

      if (isEditing && id) {
        // Update existing estimate
        await axios.put(`/sales/estimates/${id}`, estimateData);
        toast.success('Estimate updated successfully!');
      } else {
        // Create new estimate
        await axios.post('/sales/estimates', estimateData);
        toast.success('Estimate created successfully!');
      }

      // Refresh history and switch to history tab
      await fetchEstimates();
      setIsWoodWorkMode(false);
      setActiveTab('history');

    } catch (error) {
      console.error('❌ Failed to save estimate:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error message:', error.message);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to save estimate';

      toast.error(`Backend Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-4 md:py-8">
      <div className="mx-auto bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] transition-all duration-300 ease-in-out p-4 md:p-8" style={{ width: '98%', maxWidth: '1400px', margin: '10px auto' }}>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#0A5F38] mb-4">
            Estimates Management
          </h1>
          <p className="text-lg text-slate-600 font-medium">Create new estimates and manage existing ones</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-slate-100 p-1 rounded-2xl w-fit">
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'history'
                ? 'bg-[#0A5F38] text-white shadow-md'
                : 'text-slate-600 hover:text-[#0A5F38]'
                }`}
            >
              <FileText className="h-5 w-5 inline mr-2" />
              Estimates History
            </button>
            <button
              onClick={handleNewEstimate}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${activeTab === 'form' && !isWoodWorkMode
                ? 'bg-[#0A5F38] text-white shadow-md'
                : 'text-slate-600 hover:text-[#0A5F38]'
                }`}
            >
              <Plus className="h-5 w-5 inline mr-2" />
              {isEditing ? 'Edit Estimate' : 'New Estimate'}
            </button>
            <button
              id="woodwork-template-btn"
              onClick={handleWoodWorkClick}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 ${activeTab === 'form' && isWoodWorkMode
                ? 'bg-[#0A5F38] text-white shadow-md'
                : 'text-slate-600 hover:text-[#0A5F38] border border-slate-200 shadow-sm'
                }`}
              title="Open pre-filled Wood Work estimate template"
            >
              <span className="text-base">🪵</span>
              Wood Work
            </button>
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'history' ? (
          <div className="space-y-6">
            {/* Action Bar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center space-x-4">
                {/* Removed duplicate New Estimate button - using tab button instead */}
              </div>

              {/* Search */}
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <div className="relative w-full sm:w-80">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#2D5A27] h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search estimates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 pr-4 py-3 w-full border border-[#E5E5E0] rounded-2xl focus:outline-none focus:border-[#2D5A27] focus:ring-1 focus:ring-[#2D5A27] bg-[#F9F9F7] shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] placeholder-[#A0A090] placeholder:italic transition-all duration-200 text-slate-700"
                  />
                </div>
              </div>
            </div>

            <AdvancedFilters
              filters={filters}
              setFilters={setFilters}
              showSalesperson={true}
              salespersons={salespersons}
              onClear={() => setFilters({
                dateRange: 'all',
                status: 'all',
                startDate: '',
                endDate: '',
                minAmount: '',
                maxAmount: '',
                isWoodWork: 'all',
                salesperson_id: 'all'
              })}
              statusOptions={[
                { value: 'all', label: 'All Status' },
                { value: 'estimates', label: 'Estimates' },
                { value: 'closed_order', label: 'Converted to Closed Orders' },
                { value: 'invoice', label: 'Converted to Invoices' }
              ]}
            />

            {/* Loading State */}
            {historyLoading && !dataFetched && (
              <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-200/60 p-8">
                <TableSkeleton rows={5} columns={6} />
              </div>
            )}

            {/* Estimates Table */}
            {(!historyLoading || dataFetched) && (
              <div className="bg-[#E8DCC4] p-4 md:p-[38px] rounded-3xl">
                <div className="bg-[#F9F6F0] rounded-3xl shadow-lg shadow-slate-200/50 border border-slate-200/60 overflow-hidden">
                  <div className="overflow-x-auto">
                    {filteredEstimates.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-slate-900 mb-2">No Estimates Found</h3>
                        <p className="text-slate-500">
                          {searchTerm
                            ? 'No estimates match your search criteria.'
                            : 'No estimates found. Create your first estimate to get started.'}
                        </p>
                      </div>
                    ) : (
                      <table className="min-w-full border-separate border-spacing-0">
                        <thead className="bg-[#005C2D]">
                          <tr>
                            <th
                              className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:bg-[#2A4533] transition-colors rounded-tl-2xl"
                              onClick={() => handleSort('date')}
                            >
                              <div className="flex items-center justify-center space-x-1">
                                <span>Date</span>
                                {sortConfig.key === 'date' && (
                                  <span className="text-white">
                                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </div>
                            </th>
                            <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide">
                              Estimate Number
                            </th>
                            <th
                              className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:bg-[#2A4533] transition-colors"
                              onClick={() => handleSort('customer')}
                            >
                              <div className="flex items-center justify-center space-x-1">
                                <span>Customer /<br />Company Name</span>
                                {sortConfig.key === 'customer' && (
                                  <span className="text-white">
                                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </div>
                            </th>
                            <th
                              className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide cursor-pointer hover:bg-[#2A4533] transition-colors"
                              onClick={() => handleSort('amount')}
                            >
                              <div className="flex items-center justify-center space-x-1">
                                <span>Amount</span>
                                {sortConfig.key === 'amount' && (
                                  <span className="text-white">
                                    {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                              </div>
                            </th>
                            <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide">
                              Status
                            </th>
                            <th className="px-6 py-4 text-center text-xs font-bold text-white uppercase tracking-wide rounded-tr-2xl">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                          {filteredEstimates.map((estimate, index) => (
                            <tr
                              key={estimate.id}
                              className={`transition-colors duration-200 ${index % 2 === 0
                                ? 'bg-white hover:bg-[#F3EFE0]/50'
                                : 'bg-[#F3EFE0] hover:bg-[#E8DCC4]/50'
                                }`}
                            >
                              <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-slate-600">
                                {estimate.date ?
                                  new Date(estimate.date).toLocaleDateString('en-IN') :
                                  estimate.createdAt ?
                                    new Date(estimate.createdAt).toLocaleDateString('en-IN') :
                                    new Date().toLocaleDateString('en-IN')
                                }
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="text-sm font-semibold text-slate-900 flex items-center justify-center gap-1">
                                  {estimate.isWoodWork && <span title="Wood Work / Interior Project">🪵</span>}
                                  {(estimate.estimate_number || estimate._id?.slice(-8) || 'EST' + estimate.id).replace(/-CANCEL-\d+/g, ' (Cancelled)')}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-normal break-words max-w-[250px] text-center">
                                <div className="text-sm font-medium text-slate-900 break-words">
                                  {estimate.customer_name || 'Walk-in Customer'}
                                </div>
                                <div className="text-sm text-slate-500">
                                  {estimate.customer_phone || 'N/A'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="text-sm font-semibold text-slate-900">
                                  ₹{(estimate.totalAmount || estimate.total_amount || 0).toLocaleString()}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center">
                                {/* Check if related Closed Order or Invoice is cancelled */}
                                {estimate.cancelled_closed_order || estimate.closed_order_cancelled ||
                                  (estimate.status === 'closed_order' && estimate.cancellation_status === 'cancelled') ||
                                  (estimate.status === 'cancelled' && estimate.cancelled_closed_order) ? (
                                  <div className="space-y-1">
                                    <span className="inline-flex px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800 border border-red-200">
                                      Cancelled Closed Order
                                    </span>
                                    {estimate.closed_order_number && (
                                      <div className="text-xs text-slate-600">
                                        → {estimate.closed_order_number}
                                      </div>
                                    )}
                                  </div>
                                ) : estimate.cancelled_invoice || estimate.invoice_cancelled ||
                                  (estimate.status === 'converted_to_invoice' && estimate.cancellation_status === 'cancelled') ? (
                                  <div className="space-y-1">
                                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                      Cancelled Invoice
                                    </span>
                                    {estimate.invoice_number && (
                                      <div className="text-xs text-slate-600">
                                        → {estimate.invoice_number}
                                      </div>
                                    )}
                                  </div>
                                ) : estimate.status === 'cancelled' ? (
                                  <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                                    Cancelled
                                  </span>
                                ) : estimate.status === 'converted_to_invoice' || estimate.status === 'sold' ? (
                                  <div className="space-y-1">
                                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-[#E7F4E9] text-[#005C2D]">
                                      Converted to Invoice
                                    </span>
                                    {(estimate.invoice_number || estimate.invoiceNumber) && estimate.convertedToInvoiceId && (
                                      <div className="text-xs">
                                        → <button
                                          onClick={() => handleInvoicePdfDownload(estimate.convertedToInvoiceId, estimate.invoice_number || estimate.invoiceNumber)}
                                          className="text-[#005C2D] hover:text-green-800 hover:underline font-medium transition-colors"
                                          title="Click to download Invoice PDF"
                                        >
                                          {estimate.invoice_number || estimate.invoiceNumber}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ) : estimate.status === 'closed_order' ? (
                                  <div className="space-y-1">
                                    <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-[#F3E8FF] text-[#6B21A8]">
                                      Converted to Closed Order
                                    </span>
                                    {estimate.closed_order_number && (
                                      <div className="text-xs">
                                        → <button
                                          onClick={() => handleClosedOrderPdfDownload(estimate._id, estimate.closed_order_number)}
                                          className="text-purple-600 hover:text-purple-800 hover:underline font-medium transition-colors"
                                          title="Click to download Closed Order PDF"
                                        >
                                          {estimate.closed_order_number}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-[#E0F2FE] text-[#0369A1]">
                                    Estimate
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                <div className="flex justify-center space-x-1">
                                  <button
                                    onClick={() => handleViewEstimate(estimate)}
                                    className="p-2 bg-[#C5A57D]/10 hover:bg-[#C5A57D]/20 rounded-lg transition-colors"
                                    title="View Details"
                                  >
                                    <Eye className="h-4 w-4 text-[#0A5F38]" />
                                  </button>
                                  <DocumentHistoryButton docId={estimate.id || estimate._id} />
                                  <button
                                    onClick={() => handleDuplicateEstimate(estimate)}
                                    className="p-2 bg-[#C5A57D]/10 hover:bg-[#C5A57D]/20 rounded-lg transition-colors"
                                    title="Duplicate Estimate"
                                  >
                                    <Copy className="h-4 w-4 text-[#0A5F38]" />
                                  </button>
                                  <button
                                    onClick={async () => {
                                      try {
                                        const loadingToast = toast.loading('Generating PDF...');
                                        const response = await api.get(`/sales/estimates/${estimate.id || estimate._id}`);
                                        const completeEstimateData = response.data;

                                        const pdfData = {
                                          ...completeEstimateData,
                                          items: completeEstimateData.items || [],
                                          customer_name: completeEstimateData.customer?.name || completeEstimateData.customer_name || 'Walk-in Customer',
                                          customer_address: completeEstimateData.customer?.address || completeEstimateData.customer_address || '',
                                          customer_city: completeEstimateData.customer?.city || completeEstimateData.customer_city || ''
                                        };

                                        await generatePDF(pdfData, 'estimate', companySettings);
                                        toast.dismiss(loadingToast);
                                        toast.success('PDF downloaded successfully');
                                      } catch (error) {
                                        console.error('Failed to download PDF:', error);
                                        toast.error('Failed to download PDF');
                                      }
                                    }}
                                    className="p-2 bg-[#C5A57D]/10 hover:bg-[#C5A57D]/20 rounded-lg transition-colors"
                                    title="Download PDF"
                                  >
                                    <Download className="h-4 w-4 text-[#0A5F38]" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      if (!estimate?.shareToken) {
                                        toast.error('Share token not available yet.');
                                        return;
                                      }
                                      const guestLink = `${window.location.origin}/view/document/${estimate.shareToken}`;
                                      navigator.clipboard.writeText(guestLink)
                                        .then(() => toast.success('Guest link copied to clipboard!'))
                                        .catch(() => toast.error('Failed to copy link'));
                                    }}
                                    className="p-2 bg-[#C5A57D]/10 hover:bg-[#C5A57D]/20 rounded-lg transition-colors"
                                    title="Copy Guest Link"
                                  >
                                    <Link2 className="h-4 w-4 text-[#0A5F38]" />
                                  </button>
                                  {/* Delete button - Admin only */}
                                  {user?.role === 'admin' && (
                                    <button
                                      onClick={() => handleDeleteEstimate(estimate)}
                                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Delete Estimate"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Pagination */}
                  {pagination.totalItems > 0 && (
                    <div className="bg-white px-6 py-4 flex items-center justify-between border-t border-slate-200">
                      <div className="text-sm text-slate-600">
                        Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
                        {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                        {pagination.totalItems} results
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => fetchEstimates(0, pagination.currentPage - 1)}
                          disabled={!pagination.hasPreviousPage}
                          className="p-2 rounded-lg border border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => fetchEstimates(0, pagination.currentPage + 1)}
                          disabled={!pagination.hasNextPage}
                          className="p-2 rounded-lg border border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

            )}
          </div>
        ) : (
          // Form Content - Estimate Creation Form
          <div className="bg-[#F8F5E6] rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-200/60 p-4 md:p-8">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Loading Form Data...</h3>
                <p className="text-slate-600">Please wait while we fetch categories, products, and employees.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Form Header */}
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-[#0A5F38] mb-2">
                      {isEditing ? 'Edit Estimate' : 'Create New Estimate'}
                    </h3>
                    <p className="text-slate-600">Fill in the details below to create an estimate</p>
                  </div>
                  {!isEditing && (
                    <button
                      type="button"
                      onClick={() => setShowProformaUploadModal(true)}
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-bold rounded-2xl shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-0.5 transition-all duration-300 group"
                    >
                      <Paperclip className="h-5 w-5 mr-2 group-hover:rotate-12 transition-transform" />
                      ✨ Upload Proforma
                      <span className="ml-2 px-2 py-0.5 bg-indigo-500/50 rounded-lg text-[10px] uppercase tracking-wider">Beta</span>
                    </button>
                  )}
                </div>

                {/* Salesperson Selection */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                  <h4 className="text-lg font-bold text-[#0A5F38] uppercase tracking-wide mb-4">Estimate Information</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Employee</label>
                      <div className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 font-medium">
                        {user?.name || 'Not logged in'}
                      </div>
                    </div>
                    <ValidatedInput
                      label="Estimate Date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      validationType="required"
                      required={true}
                      className="input-field border border-slate-300 focus:ring-2 focus:ring-[#0A5F38] focus:border-[#0A5F38] rounded-lg"
                      showErrors={formTouched}
                    />
                  </div>
                </div>

                {/* Customer Information */}
                <div className="bg-white rounded-2xl p-4 md:p-6 border border-slate-200 shadow-sm overflow-hidden">
                  <h4 className="text-lg font-bold text-[#0A5F38] uppercase tracking-wide mb-4">Customer Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ValidatedInput
                      label="Customer/Company Name"
                      type="text"
                      value={formData.customer.name}
                      onChange={(e) => setFormData({
                        ...formData,
                        customer: { ...formData.customer, name: e.target.value }
                      })}
                      validationType="name"
                      required={true}
                      placeholder="Enter customer/company name"
                      className="input-field border border-slate-300 focus:ring-2 focus:ring-[#0A5F38] focus:border-[#0A5F38] rounded-lg"
                      showErrors={formTouched}
                    />
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number <span className="text-red-500">*</span></label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.customer.countryCode}
                          onChange={(e) => setFormData({ ...formData, customer: { ...formData.customer, countryCode: e.target.value } })}
                          placeholder="+91"
                          maxLength={5}
                          className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-[#0A5F38] text-sm"
                        />
                        <input
                          type="tel"
                          value={formData.customer.phone}
                          onChange={(e) => setFormData({ ...formData, customer: { ...formData.customer, phone: e.target.value.replace(/\D/g, '') } })}
                          placeholder={formData.customer.countryCode ? 'Mobile number' : '10-digit mobile number'}
                          maxLength={formData.customer.countryCode ? 11 : 10}
                          className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-[#0A5F38] text-sm"
                        />
                      </div>
                      {formTouched && validationErrors.customerPhone && (
                        <p className="text-red-500 text-xs mt-1">{validationErrors.customerPhone}</p>
                      )}
                    </div>
                    <ValidatedInput
                      label="Email"
                      type="email"
                      value={formData.customer.email}
                      onChange={(e) => setFormData({
                        ...formData,
                        customer: { ...formData.customer, email: e.target.value }
                      })}
                      validationType="email"
                      required={false}
                      placeholder="Enter email address"
                      className="input-field border border-slate-300 focus:ring-2 focus:ring-[#0A5F38] focus:border-[#0A5F38] rounded-lg"
                      showErrors={formTouched}
                    />
                    <ValidatedInput
                      label="Address"
                      type="text"
                      value={formData.customer.address}
                      onChange={(e) => setFormData({
                        ...formData,
                        customer: { ...formData.customer, address: e.target.value }
                      })}
                      validationType="address"
                      required={false}
                      placeholder="Enter address "
                      className="input-field border border-slate-300 focus:ring-2 focus:ring-[#0A5F38] focus:border-[#0A5F38] rounded-lg"
                      showErrors={formTouched}
                    />
                    <ValidatedInput
                      label="GST Number"
                      type="text"
                      value={formData.customer.gstNumber}
                      onChange={(e) => handleGSTFieldChange(e.target.value,
                        (gstValue) => setFormData({
                          ...formData,
                          customer: { ...formData.customer, gstNumber: gstValue }
                        }),
                        (stateName) => setFormData(prev => ({
                          ...prev,
                          customer: { ...prev.customer, state: stateName }
                        }))
                      )}
                      validationType="gstNumber"
                      required={false}
                      placeholder="Enter GST number"
                      className="input-field border border-slate-300 focus:ring-2 focus:ring-[#0A5F38] focus:border-[#0A5F38] rounded-lg"
                      showErrors={formTouched}
                    />
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
                      <select
                        value={formData.customer.state || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          customer: { ...formData.customer, state: e.target.value }
                        })}
                        className="w-full px-4 py-2 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent transition-all outline-none"
                      >
                        <option value="">Select State</option>
                        {stateOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Items Section - Table Layout */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="text-lg font-bold text-[#0A5F38] uppercase tracking-wide flex items-center gap-2">
                      {isWoodWorkMode && <span>🪵</span>}
                      {isWoodWorkMode ? 'Wood Work Items' : 'Items'}
                    </h4>
                    {!isWoodWorkMode && (
                      <div className="flex items-center space-x-3 bg-white px-4 py-2 rounded-lg border border-slate-200 shadow-sm">
                        <span className="text-sm font-medium text-slate-700">Apply Discount</span>
                        <button
                          type="button"
                          onClick={() => setShowDiscount(!showDiscount)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${showDiscount ? 'bg-[#C5A57D]' : 'bg-slate-200'}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showDiscount ? 'translate-x-6' : 'translate-x-1'}`}
                          />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Items Table - Responsive Layout */}
                  <div className="w-full">
                    {isWoodWorkMode ? (
                      /* ══ WOOD WORK TABLE REDESIGN ══ */
                      <div className="space-y-8">
                        {(() => {
                          const CAT_LETTERS = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P'];
                          const categoryOrder = [];
                          items.forEach(item => {
                            const cat = item.categoryLabel || '';
                            if (!categoryOrder.includes(cat)) categoryOrder.push(cat);
                          });

                          return categoryOrder.map((catLabel, catIdx) => {
                            const catEntries = items
                              .map((item, idx) => ({ item, idx }))
                              .filter(({ item }) => (item.categoryLabel || '') === catLabel);

                            return (
                              <div key={'cat-card-' + catLabel} className="bg-white border border-slate-300 rounded-xl overflow-hidden shadow-sm">
                                {/* Section Header Bar (Client Style) */}
                                <div className="bg-[#C5A57D]/90 px-6 py-3 flex justify-between items-center border-b border-slate-300">
                                  <h5 className="text-white font-bold uppercase tracking-wider flex items-center gap-2">
                                    <span className="opacity-70">{CAT_LETTERS[catIdx] || (catIdx + 1)}.</span> {catLabel}
                                  </h5>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (window.confirm(`Are you sure you want to delete the entire section "${catLabel}"?`)) {
                                        setItems(items.filter(i => (i.categoryLabel || '') !== catLabel));
                                      }
                                    }}
                                    className="text-white hover:text-red-200 transition-colors p-1"
                                    title={`Delete section: ${catLabel}`}
                                  >
                                    <Trash2 className="h-5 w-5" />
                                  </button>
                                </div>

                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm border-collapse">
                                    <thead>
                                      <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="border-r border-slate-200 px-3 py-3 text-center font-bold text-slate-700 uppercase" style={{width:'50px'}}>SI</th>
                                        <th className="border-r border-slate-200 px-3 py-3 text-center font-bold text-slate-700 uppercase" style={{width:'200px'}}>Type</th>
                                        <th className="border-r border-slate-200 px-3 py-3 text-center font-bold text-slate-700 uppercase" style={{width:'100px'}}>Rate (₹)</th>
                                        <th className="border-r border-slate-200 px-3 py-3 text-center font-bold text-slate-700 uppercase" style={{width:'65px'}}>Nos</th>
                                        <th className="border-r border-slate-200 px-3 py-3 text-center font-bold text-slate-700 uppercase" style={{width:'80px'}}>L</th>
                                        <th className="border-r border-slate-200 px-3 py-3 text-center font-bold text-slate-700 uppercase" style={{width:'80px'}}>B</th>
                                        <th className="border-r border-slate-200 px-3 py-3 text-center font-bold text-slate-700 uppercase" style={{width:'90px'}}>Qty</th>
                                        <th className="border-r border-slate-200 px-3 py-3 text-center font-bold text-slate-700 uppercase" style={{width:'80px'}}>GST %</th>
                                        <th className="border-r border-slate-200 px-3 py-3 text-right font-bold text-slate-700 uppercase" style={{width:'120px'}}>Amount (₹)</th>
                                        <th className="px-3 py-3 text-center font-bold text-slate-700 uppercase" style={{width:'55px'}}>Del</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {catEntries.map(({ item, idx: index }, siNo) => {
                                        const qty = computeWoodWorkQty(item.nos, item.length, item.breadth);
                                        const rate = parseFloat(item.unit_price) || 0;
                                        const taxableAmount = parseFloat((qty * rate).toFixed(2));
                                        const gstRate = parseFloat(item.gst_rate) || 0;
                                        const gstAmount = parseFloat(((taxableAmount * gstRate) / 100).toFixed(2));
                                        const amount = taxableAmount + gstAmount;

                                        return (
                                          <tr key={'item-' + index} className={`border-b border-slate-100 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-slate-100/50 transition-colors`}>
                                            <td className="border-r border-slate-200 px-2 py-2 text-center text-slate-500 font-medium">{siNo + 1}</td>
                                            <td className="border-r border-slate-200 px-2 py-2">
                                              <input type="text" value={item.product_name || ''}
                                                onChange={(e) => { const u = [...items]; u[index].product_name = e.target.value; setItems(u); }}
                                                className="w-full px-2 py-1 border border-slate-200 rounded focus:ring-1 focus:ring-[#C5A57D] text-sm"
                                                placeholder="Item description" />
                                            </td>
                                            <td className="border-r border-slate-200 px-2 py-2">
                                              <input type="number" step="0.01" min="0"
                                                value={item.unit_price === '' ? '' : item.unit_price}
                                                onChange={(e) => {
                                                  const u = [...items];
                                                  const r = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                  u[index].unit_price = e.target.value === '' ? '' : r;
                                                  const gst = parseFloat(u[index].gst_rate) || 0;
                                                  const q = computeWoodWorkQty(u[index].nos, u[index].length, u[index].breadth);
                                                  const taxable = parseFloat((q * r).toFixed(2));
                                                  const gstAmt = parseFloat(((taxable * gst) / 100).toFixed(2));
                                                  u[index].quantity = q; 
                                                  u[index].amount = taxable;
                                                  u[index].gst_amount = gstAmt;
                                                  u[index].total_amount = taxable + gstAmt;
                                                  u[index].subtotal = u[index].total_amount;
                                                  setItems(u);
                                                }}
                                                onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                                onWheel={(e) => e.target.blur()}
                                                className="w-full px-2 py-1 text-center border border-slate-200 rounded focus:ring-1 focus:ring-[#C5A57D] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                            </td>
                                            <td className="border-r border-slate-200 px-2 py-2">
                                              <input type="number" step="1" min="0"
                                                value={item.nos === null || item.nos === undefined ? '' : item.nos}
                                                onChange={(e) => {
                                                  const u = [...items];
                                                  const n = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                  u[index].nos = n;
                                                  const gst = parseFloat(u[index].gst_rate) || 0;
                                                  const q = computeWoodWorkQty(n, u[index].length, u[index].breadth);
                                                  const taxable = parseFloat((q * (parseFloat(u[index].unit_price) || 0)).toFixed(2));
                                                  const gstAmt = parseFloat(((taxable * gst) / 100).toFixed(2));
                                                  u[index].quantity = q; 
                                                  u[index].amount = taxable;
                                                  u[index].gst_amount = gstAmt;
                                                  u[index].total_amount = taxable + gstAmt;
                                                  u[index].subtotal = u[index].total_amount;
                                                  setItems(u);
                                                }}
                                                onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                                onWheel={(e) => e.target.blur()}
                                                className="w-full px-2 py-1 text-center border border-slate-200 rounded focus:ring-1 focus:ring-[#C5A57D] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                            </td>
                                            <td className="border-r border-slate-200 px-2 py-2">
                                              <input type="number" step="0.01" min="0"
                                                value={item.length === null || item.length === undefined ? '' : item.length}
                                                onChange={(e) => {
                                                  const u = [...items];
                                                  const l = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                  u[index].length = l;
                                                  const gst = parseFloat(u[index].gst_rate) || 0;
                                                  const q = computeWoodWorkQty(u[index].nos, l, u[index].breadth);
                                                  const taxable = parseFloat((q * (parseFloat(u[index].unit_price) || 0)).toFixed(2));
                                                  const gstAmt = parseFloat(((taxable * gst) / 100).toFixed(2));
                                                  u[index].quantity = q; 
                                                  u[index].amount = taxable;
                                                  u[index].gst_amount = gstAmt;
                                                  u[index].total_amount = taxable + gstAmt;
                                                  u[index].subtotal = u[index].total_amount;
                                                  setItems(u);
                                                }}
                                                onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                                onWheel={(e) => e.target.blur()}
                                                className="w-full px-2 py-1 text-center border border-slate-200 rounded focus:ring-1 focus:ring-[#C5A57D] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                            </td>
                                            <td className="border-r border-slate-200 px-2 py-2">
                                              <input type="number" step="0.01" min="0"
                                                value={item.breadth === null || item.breadth === undefined ? '' : item.breadth}
                                                onChange={(e) => {
                                                  const u = [...items];
                                                  const b = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                                  u[index].breadth = b;
                                                  const gst = parseFloat(u[index].gst_rate) || 0;
                                                  const q = computeWoodWorkQty(u[index].nos, u[index].length, b);
                                                  const taxable = parseFloat((q * (parseFloat(u[index].unit_price) || 0)).toFixed(2));
                                                  const gstAmt = parseFloat(((taxable * gst) / 100).toFixed(2));
                                                  u[index].quantity = q; 
                                                  u[index].amount = taxable;
                                                  u[index].gst_amount = gstAmt;
                                                  u[index].total_amount = taxable + gstAmt;
                                                  u[index].subtotal = u[index].total_amount;
                                                  setItems(u);
                                                }}
                                                onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                                onWheel={(e) => e.target.blur()}
                                                className="w-full px-2 py-1 text-center border border-slate-200 rounded focus:ring-1 focus:ring-[#C5A57D] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                            </td>
                                            <td className="border-r border-slate-200 px-2 py-2 text-center font-bold text-slate-800 bg-slate-50/50">{qty}</td>
                                            <td className="border-r border-slate-200 px-2 py-2">
                                              <input type="number" step="0.01" min="0"
                                                value={item.gst_rate === null || item.gst_rate === undefined || item.gst_rate === '' ? '' : item.gst_rate}
                                                onChange={(e) => {
                                                  const u = [...items];
                                                  const g = e.target.value === '' ? '' : parseFloat(e.target.value);
                                                  u[index].gst_rate = g;
                                                  const q = computeWoodWorkQty(u[index].nos, u[index].length, u[index].breadth);
                                                  const taxable = parseFloat((q * (parseFloat(u[index].unit_price) || 0)).toFixed(2));
                                                  const gstAmt = parseFloat(((taxable * (parseFloat(g) || 0)) / 100).toFixed(2));
                                                  u[index].quantity = q; 
                                                  u[index].amount = taxable;
                                                  u[index].gst_amount = gstAmt;
                                                  u[index].total_amount = taxable + gstAmt;
                                                  u[index].subtotal = u[index].total_amount;
                                                  setItems(u);
                                                }}
                                                onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                                onWheel={(e) => e.target.blur()}
                                                className="w-full px-2 py-1 text-center border border-slate-200 rounded focus:ring-1 focus:ring-[#C5A57D] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                                            </td>
                                            <td className="border-r border-slate-200 px-2 py-2 text-right font-bold text-slate-800 bg-slate-50/50">
                                              ₹{amount.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                            </td>
                                            <td className="px-2 py-2 text-center">
                                              <button type="button" onClick={() => setItems(items.filter((_, i) => i !== index))}
                                                className="text-red-400 hover:text-red-600 p-1.5 rounded-full hover:bg-red-50 transition-all" title="Remove">
                                                <Trash2 className="h-4 w-4" />
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                      {/* "Add Item" button row for this category */}
                                      <tr>
                                        <td colSpan="10" className="px-4 py-3 bg-white">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const lastIdxInCat = items.reduce((acc, item, i) => (
                                                (item.categoryLabel || '') === catLabel ? i : acc
                                              ), -1);
                                              const newItem = {
                                                category_id: '', product_id: '', product_name: '',
                                                hsn_code: '', product_instructions: '',
                                                quantity: 0, unit_price: 0, unit_type: 'SFT',
                                                gst_rate: 0, gst_amount: 0, amount: 0,
                                                total_amount: 0, tax: 0, discount: 0, subtotal: 0,
                                                isOthers: true, showFreeText: true, isWoodWork: true,
                                                categoryLabel: catLabel, nos: 1, length: 0, breadth: 0
                                              };
                                              const updated = [...items];
                                              updated.splice(lastIdxInCat + 1, 0, newItem);
                                              setItems(updated);
                                            }}
                                            className="flex items-center gap-2 text-sm font-bold text-[#0A5F38] hover:text-[#084d2d] transition-colors group"
                                          >
                                            <Plus className="h-4 w-4 group-hover:scale-125 transition-transform" />
                                            Add more items to <span className="uppercase text-[#C5A57D]">{catLabel}</span>
                                          </button>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          });
                        })()}

                        {/* Grand Total Footer for Wood Work */}
                        <div className="bg-[#0A5F38] rounded-xl p-6 shadow-md flex flex-col sm:flex-row justify-between items-center gap-6">
                           <div className="flex items-center gap-3">
                              <div className="bg-white/10 px-4 py-2 rounded-lg border border-white/20">
                                <span className="text-white/70 text-[10px] font-bold uppercase block">New Section</span>
                                <div className="flex gap-2 items-center mt-1">
                                    <input
                                      type="text"
                                      value={newSectionName}
                                      onChange={(e) => setNewSectionName(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          const name = newSectionName.trim().toUpperCase();
                                          if (!name) return;
                                          setItems([...items, {
                                            category_id: '', product_id: '', product_name: '',
                                            hsn_code: '', product_instructions: '',
                                            quantity: 0, unit_price: 0, unit_type: 'SFT',
                                            gst_rate: 0, gst_amount: 0, amount: 0,
                                            total_amount: 0, tax: 0, discount: 0, subtotal: 0,
                                            isOthers: true, showFreeText: true, isWoodWork: true,
                                            categoryLabel: name, nos: 1, length: 0, breadth: 0
                                          }]);
                                          setNewSectionName('');
                                        }
                                      }}
                                      placeholder="e.g. MASTER BEDROOM"
                                      className="bg-white px-3 py-1.5 text-xs font-bold rounded-lg w-48 border-none outline-none focus:ring-2 focus:ring-[#C5A57D]"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const name = newSectionName.trim().toUpperCase();
                                        if (!name) return;
                                        setItems([...items, {
                                          category_id: '', product_id: '', product_name: '',
                                          hsn_code: '', product_instructions: '',
                                          quantity: 0, unit_price: 0, unit_type: 'SFT',
                                          gst_rate: 0, gst_amount: 0, amount: 0,
                                          total_amount: 0, tax: 0, discount: 0, subtotal: 0,
                                          isOthers: true, showFreeText: true, isWoodWork: true,
                                          categoryLabel: name, nos: 1, length: 0, breadth: 0
                                        }]);
                                        setNewSectionName('');
                                      }}
                                      className="p-1 px-3 bg-[#C5A57D] text-white rounded-lg hover:bg-[#b8985e] transition-colors text-xs font-bold"
                                    >
                                      ADD
                                    </button>
                                </div>
                              </div>
                           </div>
                           
                           <div className="bg-white/10 px-8 py-3 rounded-2xl border border-white/20 backdrop-blur-md">
                              <span className="text-white/60 text-xs font-bold uppercase tracking-widest mr-4">Project Total Estimate:</span>
                              <span className="text-3xl font-black text-white italic">
                                ₹{items.reduce((sum, item) => {
                                  const q = computeWoodWorkQty(item.nos, item.length, item.breadth);
                                  const taxable = q * (parseFloat(item.unit_price) || 0);
                                  const gst = (taxable * (parseFloat(item.gst_rate) || 0)) / 100;
                                  return sum + taxable + gst;
                                }, 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                              </span>
                           </div>
                        </div>
                      </div>
                    ) : (
                    /* ══ NORMAL TABLE ══ */
                    <div>
                    {/* Desktop & Tablet Table View */}
                    <div className="hidden md:block">
                      <div className="overflow-x-auto border-2 border-black rounded-t-lg bg-white">
                        <table className="border-collapse min-w-[800px] w-full">
                          <thead>
                            <tr className="bg-[#0A5F38] border-b-2 border-black">
                              <th className="border-r-2 border-black px-3 py-3 text-center text-sm font-bold text-white uppercase" style={{ width: '50px', minWidth: '50px' }}>S.No.</th>
                              <th className="border-r-2 border-black px-3 py-3 text-center text-sm font-bold text-white uppercase" style={{ width: '150px', minWidth: '150px' }}>CATEGORY*</th>
                              <th className="border-r-2 border-black px-3 py-3 text-center text-sm font-bold text-white uppercase" style={{ width: '170px', minWidth: '170px' }}>PRODUCT*</th>
                              <th className="border-r-2 border-black px-3 py-3 text-center text-sm font-bold text-white uppercase" style={{ width: '100px', minWidth: '100px' }}>HSN CODE</th>
                              <th className="border-r-2 border-black px-3 py-3 text-center text-sm font-bold text-white uppercase" style={{ width: '90px', minWidth: '90px' }}>UNIT</th>
                              <th className="border-r-2 border-black px-3 py-3 text-center text-sm font-bold text-white uppercase" style={{ width: '80px', minWidth: '80px' }}>QTY*</th>
                              <th className="border-r-2 border-black px-3 py-3 text-right text-sm font-bold text-white uppercase" style={{ width: '110px', minWidth: '110px' }}>PRICE*</th>
                              {showDiscount && (
                                <th className="border-r-2 border-black px-3 py-3 text-center text-sm font-bold text-white uppercase" style={{ width: '80px', minWidth: '80px' }}>DISC%</th>
                              )}
                              <th className="border-r-2 border-black px-3 py-3 text-center text-sm font-bold text-white uppercase" style={{ width: '80px', minWidth: '80px' }}>GST%</th>
                              <th className="border-r-2 border-black px-3 py-3 text-right text-sm font-bold text-white uppercase" style={{ width: '120px', minWidth: '120px' }}>AMOUNT</th>
                              <th className="px-3 py-3 text-center text-sm font-bold text-white uppercase" style={{ width: '80px', minWidth: '80px' }}>ACTION</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, index) => {
                              // Calculate per-item amounts
                              const rawAmount = roundForCalculation((item.quantity || 0) * (item.unit_price || 0));
                              const discountAmount = showDiscount ? roundForCalculation((rawAmount * (item.discount || 0)) / 100) : 0;
                              const taxableAmount = roundForCalculation(rawAmount - discountAmount);
                              const gstAmount = roundForCalculation((taxableAmount * (item.gst_rate || 0)) / 100);
                              const itemTotal = roundForCalculation(taxableAmount + gstAmount);

                              return (
                                <tr key={index} className="border-b border-slate-200 hover:bg-white">
                                  <td className="border-r-2 border-black px-3 py-3 text-center text-sm font-medium text-slate-700" style={{ width: '50px', minWidth: '50px' }}>
                                    {index + 1}
                                  </td>
                                  <td className="border-r-2 border-black px-3 py-3 text-center" style={{ width: '150px', minWidth: '150px' }}>
                                    <select
                                      value={item.category_id}
                                      onChange={(e) => {
                                        const catId = e.target.value;
                                        const selectedCategory = categories.find(c => String(c.id || c._id) === String(catId));
                                        const isOthers = selectedCategory?.isOthers || false;
                                        const updatedItems = [...items];
                                        updatedItems[index].category_id = catId;
                                        updatedItems[index].isOthers = isOthers;
                                        updatedItems[index].product_id = '';
                                        updatedItems[index].product_name = '';
                                        updatedItems[index].unit_type = isOthers ? 'PCS' : 'SFT';
                                        updatedItems[index].unit_price = 0;
                                        updatedItems[index].gst_rate = '';
                                        setItems(updatedItems);
                                      }}
                                      className="w-full px-3 py-2 text-sm border-2 border-[#C5A57D] rounded-md focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white select-no-arrow"
                                      required
                                    >
                                      <option value="">Select </option>
                                      {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>
                                          {cat.name}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="border-r-2 border-black px-3 py-3 text-center" style={{ width: '170px', minWidth: '170px' }}>
                                    {item.isOthers ? (
                                      (() => {
                                        const productsInCategory = products.filter(prod => {
                                          const selectedCat = String(item.category_id);
                                          const productCat = String(prod.categoryId || prod.category_id || '');
                                          return productCat === selectedCat;
                                        });

                                        if (productsInCategory.length === 0 || item.showFreeText) {
                                          return (
                                            <div className="flex items-center gap-1">
                                              <input
                                                type="text"
                                                value={item.product_name || ''}
                                                onChange={(e) => {
                                                  const updatedItems = [...items];
                                                  updatedItems[index].product_name = e.target.value;
                                                  setItems(updatedItems);
                                                }}
                                                className="flex-1 px-3 py-2 text-sm border-2 border-[#C5A57D] rounded-md focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent"
                                                placeholder="Enter product name"
                                                required
                                              />
                                              {productsInCategory.length > 0 && (
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const updatedItems = [...items];
                                                    updatedItems[index].showFreeText = false;
                                                    updatedItems[index].product_id = '';
                                                    updatedItems[index].product_name = '';
                                                    setItems(updatedItems);
                                                  }}
                                                  className="px-2 py-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                                  title="Show existing products"
                                                >
                                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                                  </svg>
                                                </button>
                                              )}
                                            </div>
                                          );
                                        } else {
                                          return (
                                            <select
                                              value={item.product_id}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                const updatedItems = [...items];
                                                if (val === 'ADD_NEW_OTHER') {
                                                  updatedItems[index].showFreeText = true;
                                                  updatedItems[index].product_id = '';
                                                  updatedItems[index].product_name = '';
                                                } else {
                                                  const selectedProduct = products.find(p => String(p.id) === String(val));
                                                  updatedItems[index].product_id = val;
                                                  updatedItems[index].product_name = selectedProduct?.name || '';
                                                  updatedItems[index].unit_price = selectedProduct?.sellingPrice || 0;
                                                  updatedItems[index].unit_type = selectedProduct?.unit_type || 'PCS';
                                                  updatedItems[index].gst_rate = (selectedProduct?.gstPercentage !== undefined && selectedProduct?.gstPercentage !== null) ? selectedProduct.gstPercentage : 18;
                                                  updatedItems[index].hsn_code = selectedProduct?.product?.hsnCode || selectedProduct?.hsnCode || '';
                                                }
                                                setItems(updatedItems);
                                              }}
                                              className="w-full px-3 py-2 text-sm border-2 border-[#C5A57D] rounded-md focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white select-no-arrow"
                                              required
                                            >
                                              <option value="">Select Option</option>
                                              <option value="ADD_NEW_OTHER" className="font-bold text-indigo-600">+ Add Other</option>
                                              {productsInCategory.map(prod => (
                                                <option key={prod.id} value={prod.id}>
                                                  {prod.name}
                                                </option>
                                              ))}
                                            </select>
                                          );
                                        }
                                      })()
                                    ) : item.product_id ? (
                                      <div className="flex items-center gap-1">
                                        <input
                                          type="text"
                                          value={item.product_name || ''}
                                          onChange={(e) => {
                                            const updatedItems = [...items];
                                            updatedItems[index].product_name = e.target.value;
                                            setItems(updatedItems);
                                          }}
                                          className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                          placeholder="Edit product name"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updatedItems = [...items];
                                            updatedItems[index].product_id = '';
                                            updatedItems[index].product_name = '';
                                            setItems(updatedItems);
                                          }}
                                          className="px-2 py-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                          title="Change product"
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                          </svg>
                                        </button>
                                      </div>
                                    ) : (
                                      <select
                                        value={item.product_id}
                                        onChange={(e) => {
                                          const productId = e.target.value;
                                          const selectedProduct = products.find(p => String(p.id) === String(productId));
                                          const updatedItems = [...items];
                                          updatedItems[index].product_id = productId;
                                          updatedItems[index].product_name = selectedProduct?.name || '';
                                          updatedItems[index].unit_price = selectedProduct?.sellingPrice || 0;
                                          updatedItems[index].unit_type = selectedProduct?.unit_type || 'SFT';
                                          updatedItems[index].gst_rate = (selectedProduct?.gstPercentage !== undefined && selectedProduct?.gstPercentage !== null) ? selectedProduct.gstPercentage : '';
                                          updatedItems[index].hsn_code = selectedProduct?.product?.hsnCode || selectedProduct?.hsnCode || '';
                                          setItems(updatedItems);
                                        }}
                                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white select-no-arrow"
                                        required
                                        disabled={!item.category_id}
                                      >
                                        <option value="">
                                          {!item.category_id ? 'Select Category ▼' : 'Select ▼'}
                                        </option>
                                        {item.category_id && products
                                          .filter(prod => {
                                            const selectedCat = String(item.category_id);
                                            const productCat = String(prod.categoryId || prod.category_id || '');
                                            return productCat === selectedCat;
                                          })
                                          .map(prod => (
                                            <option key={prod.id} value={prod.id}>
                                              {prod.name}
                                            </option>
                                          ))}
                                      </select>
                                    )}
                                  </td>
                                  <td className="border-r-2 border-black px-3 py-3 text-center" style={{ width: '100px', minWidth: '100px' }}>
                                    <input
                                      type="text"
                                      value={item.hsn_code || ''}
                                      onChange={(e) => {
                                        const updatedItems = [...items];
                                        updatedItems[index].hsn_code = e.target.value;
                                        setItems(updatedItems);
                                      }}
                                      className="w-full px-2 py-1 text-sm text-center border-2 border-[#C5A57D] rounded focus:ring-1 focus:ring-[#0A5F38] focus:border-transparent"
                                      placeholder="HSN"
                                    />
                                  </td>
                                  <td className="border-r-2 border-black px-3 py-3 text-center" style={{ width: '90px', minWidth: '90px' }}>
                                    <select
                                      value={item.unit_type || 'SFT'}
                                      onChange={(e) => {
                                        const updatedItems = [...items];
                                        updatedItems[index].unit_type = e.target.value;
                                        setItems(updatedItems);
                                      }}
                                      className="w-full px-3 py-2 text-sm border-2 border-[#C5A57D] rounded-md focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent bg-white select-no-arrow"
                                    >
                                      <option value="SFT">SFT</option>
                                      <option value="RFT">RFT</option>
                                      <option value="PCS">PCS</option>
                                      <option value="KG">KG</option>
                                      <option value="LTR">LTR</option>
                                      <option value="MTR">MTR</option>
                                      <option value="LUMPSUM">LUMPSUM</option>
                                      <option value="NOS">NOS</option>
                                      <option value="SQ.M">SQ.M</option>
                                    </select>
                                  </td>
                                  <td className="border-r-2 border-black px-3 py-3 text-center" style={{ width: '80px', minWidth: '80px' }}>
                                    <input
                                      type="number"
                                      min="1"
                                      step="1"
                                      value={item.quantity === '' ? '' : item.quantity}
                                      onChange={(e) => {
                                        const updatedItems = [...items];
                                        const val = e.target.value;
                                        updatedItems[index].quantity = val === '' ? '' : parseInt(val);
                                        setItems(updatedItems);
                                      }}
                                      onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                      onWheel={(e) => e.target.blur()}
                                      className="w-full px-3 py-2 text-sm text-center border-2 border-[#C5A57D] rounded-md focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                  </td>
                                  <td className="border-r-2 border-black px-3 py-3 text-right" style={{ width: '110px', minWidth: '110px' }}>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={item.unit_price === '' ? '' : item.unit_price}
                                      onChange={(e) => {
                                        const updatedItems = [...items];
                                        const val = e.target.value;
                                        updatedItems[index].unit_price = val === '' ? '' : parseFloat(val);
                                        setItems(updatedItems);
                                      }}
                                      onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                      onWheel={(e) => e.target.blur()}
                                      className="w-full px-3 py-2 text-sm text-right border-2 border-[#C5A57D] rounded-md focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                  </td>
                                  {showDiscount && (
                                    <td className="border-r-2 border-black px-3 py-3 text-center" style={{ width: '80px', minWidth: '80px' }}>
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        value={item.discount === '' ? '' : item.discount}
                                        onChange={(e) => {
                                          const updatedItems = [...items];
                                          const val = e.target.value;
                                          updatedItems[index].discount = val === '' ? '' : parseFloat(val);
                                          setItems(updatedItems);
                                        }}
                                        onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                        onWheel={(e) => e.target.blur()}
                                        className="w-full px-3 py-2 text-sm text-center border-2 border-[#C5A57D] rounded-md focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                      />
                                    </td>
                                  )}
                                  <td className="border-r-2 border-black px-3 py-3 text-center" style={{ width: '80px', minWidth: '80px' }}>
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="0.01"
                                      value={item.gst_rate === '' ? '' : item.gst_rate}
                                      onChange={(e) => {
                                        const updatedItems = [...items];
                                        const val = e.target.value;
                                        updatedItems[index].gst_rate = val === '' ? '' : parseFloat(val);
                                        setItems(updatedItems);
                                      }}
                                      onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                      onWheel={(e) => e.target.blur()}
                                      className="w-full px-3 py-2 text-sm text-center border-2 border-[#C5A57D] rounded-md focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                  </td>
                                  <td className="border-r-2 border-black px-3 py-3 text-center font-semibold text-slate-900 text-sm" style={{ width: '120px', minWidth: '120px' }}>
                                    ₹{itemTotal.toFixed(1)}
                                  </td>
                                  <td className="border-r-2 border-black px-3 py-3 text-center" style={{ width: '80px', minWidth: '80px' }}>
                                    <button
                                      type="button"
                                      onClick={() => setItems(items.filter((_, i) => i !== index))}
                                      className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                                      title="Remove item"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            {/* Add New Item Row */}
                            <tr className="border-t-2 border-black bg-white">
                              <td colSpan="11" className="px-3 py-4">
                                <button
                                  type="button"
                                  onClick={() => setItems([...items, {
                                    category_id: '',
                                    product_id: '',
                                    product_name: '',
                                    hsn_code: '',
                                    product_instructions: '',
                                    quantity: 1,
                                    unit_price: 0,
                                    unit_type: 'SFT',
                                    gst_rate: '',
                                    gst_amount: 0,
                                    amount: 0,
                                    total_amount: 0,
                                    tax: 0,
                                    discount: '',
                                    subtotal: 0
                                  }])}
                                  className="flex items-center px-4 py-2 text-indigo-600 hover:text-indigo-700 font-medium text-sm"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add New Item
                                </button>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Mobile Card Layout */}
                    <div className="md:hidden space-y-4">
                      {items.map((item, index) => {
                        // Calculate per-item amounts
                        const rawAmount = roundForCalculation((item.quantity || 0) * (item.unit_price || 0));
                        const discountAmount = showDiscount ? roundForCalculation((rawAmount * (item.discount || 0)) / 100) : 0;
                        const taxableAmount = roundForCalculation(rawAmount - discountAmount);
                        const gstAmount = roundForCalculation((taxableAmount * (item.gst_rate || 0)) / 100);
                        const itemTotal = roundForCalculation(taxableAmount + gstAmount);

                        return (
                          <div key={index} className="bg-white border border-slate-300 rounded-lg p-4 shadow-sm">
                            <div className="flex justify-between items-center mb-4">
                              <h4 className="font-semibold text-slate-900">Item {index + 1}</h4>
                              <button
                                type="button"
                                onClick={() => setItems(items.filter((_, i) => i !== index))}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                                title="Remove item"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>

                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                                <select
                                  value={item.category_id}
                                  onChange={(e) => {
                                    const catId = e.target.value;
                                    const selectedCategory = categories.find(c => String(c.id || c._id) === String(catId));
                                    const isOthers = selectedCategory?.isOthers || false;
                                    const updatedItems = [...items];
                                    updatedItems[index].category_id = catId;
                                    updatedItems[index].isOthers = isOthers;
                                    updatedItems[index].product_id = '';
                                    updatedItems[index].product_name = '';
                                    updatedItems[index].unit_type = isOthers ? 'PCS' : 'SFT';
                                    updatedItems[index].unit_price = 0;
                                    updatedItems[index].gst_rate = '';
                                    updatedItems[index].product_instructions = '';
                                    setItems(updatedItems);
                                  }}
                                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white select-no-arrow"
                                  required
                                >
                                  <option value="">Select ▼</option>
                                  {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                      {cat.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Product *</label>
                                {item.isOthers ? (
                                  (() => {
                                    const productsInCategory = products.filter(prod => {
                                      const selectedCat = String(item.category_id);
                                      const productCat = String(prod.categoryId || prod.category_id || '');
                                      return productCat === selectedCat;
                                    });

                                    if (productsInCategory.length === 0 || item.showFreeText) {
                                      return (
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="text"
                                            value={item.product_name || ''}
                                            onChange={(e) => {
                                              const updatedItems = [...items];
                                              updatedItems[index].product_name = e.target.value;
                                              setItems(updatedItems);
                                            }}
                                            className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                            placeholder="Enter product name"
                                            required
                                          />
                                          {productsInCategory.length > 0 && (
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const updatedItems = [...items];
                                                updatedItems[index].showFreeText = false;
                                                updatedItems[index].product_id = '';
                                                updatedItems[index].product_name = '';
                                                setItems(updatedItems);
                                              }}
                                              className="px-3 py-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                              title="Show existing products"
                                            >
                                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                              </svg>
                                            </button>
                                          )}
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <select
                                          value={item.product_id}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            const updatedItems = [...items];
                                            if (val === 'ADD_NEW_OTHER') {
                                              updatedItems[index].showFreeText = true;
                                              updatedItems[index].product_id = '';
                                              updatedItems[index].product_name = '';
                                            } else {
                                              const selectedProduct = products.find(p => String(p.id) === String(val));
                                              updatedItems[index].product_id = val;
                                              updatedItems[index].product_name = selectedProduct?.name || '';
                                              updatedItems[index].unit_price = selectedProduct?.sellingPrice || 0;
                                              updatedItems[index].unit_type = selectedProduct?.unit_type || 'PCS';
                                              updatedItems[index].gst_rate = (selectedProduct?.gstPercentage !== undefined && selectedProduct?.gstPercentage !== null) ? selectedProduct.gstPercentage : 0;
                                              updatedItems[index].hsn_code = selectedProduct?.product?.hsnCode || selectedProduct?.hsnCode || '';
                                              updatedItems[index].product_instructions = selectedProduct?.product?.description || selectedProduct?.description || '';
                                            }
                                            setItems(updatedItems);
                                          }}
                                          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white select-no-arrow"
                                          required
                                        >
                                          <option value="">Select Existing or Add New</option>
                                          <option value="ADD_NEW_OTHER" className="font-bold text-indigo-600">+ Add Other</option>
                                          {productsInCategory.map(prod => (
                                            <option key={prod.id} value={prod.id}>
                                              {prod.name}
                                            </option>
                                          ))}
                                        </select>
                                      );
                                    }
                                  })()
                                ) : item.product_id ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={item.product_name || ''}
                                      onChange={(e) => {
                                        const updatedItems = [...items];
                                        updatedItems[index].product_name = e.target.value;
                                        setItems(updatedItems);
                                      }}
                                      className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                      placeholder="Edit product name"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updatedItems = [...items];
                                        updatedItems[index].product_id = '';
                                        updatedItems[index].product_name = '';
                                        setItems(updatedItems);
                                      }}
                                      className="px-3 py-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                      title="Change product"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                      </svg>
                                    </button>
                                  </div>
                                ) : (
                                  <select
                                    value={item.product_id}
                                    onChange={(e) => {
                                      const productId = e.target.value;
                                      const selectedProduct = products.find(p => String(p.id) === String(productId));
                                      const updatedItems = [...items];
                                      updatedItems[index].product_id = productId;
                                      updatedItems[index].product_name = selectedProduct?.name || '';
                                      updatedItems[index].unit_price = selectedProduct?.sellingPrice || 0;
                                      updatedItems[index].unit_type = selectedProduct?.unit_type || 'SFT';
                                      updatedItems[index].gst_rate = selectedProduct?.gstPercentage || 0;
                                      updatedItems[index].hsn_code = selectedProduct?.product?.hsnCode || selectedProduct?.hsnCode || '';
                                      updatedItems[index].product_instructions = selectedProduct?.product?.description || selectedProduct?.description || '';
                                      setItems(updatedItems);
                                    }}
                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white select-no-arrow"
                                    required
                                    disabled={!item.category_id}
                                  >
                                    <option value="">
                                      {!item.category_id ? 'Select Category ▼' : 'Select ▼'}
                                    </option>
                                    {item.category_id && products
                                      .filter(prod => {
                                        const selectedCat = String(item.category_id);
                                        const productCat = String(prod.categoryId || prod.category_id || '');
                                        return productCat === selectedCat;
                                      })
                                      .map(prod => (
                                        <option key={prod.id} value={prod.id}>
                                          {prod.name}
                                        </option>
                                      ))}
                                  </select>
                                )}
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">HSN Code</label>
                                <input
                                  type="text"
                                  value={item.hsn_code || ''}
                                  onChange={(e) => {
                                    const updatedItems = [...items];
                                    updatedItems[index].hsn_code = e.target.value;
                                    setItems(updatedItems);
                                  }}
                                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                  placeholder="Enter HSN code"
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">Unit</label>
                                  <select
                                    value={item.unit_type || 'SFT'}
                                    onChange={(e) => {
                                      const updatedItems = [...items];
                                      updatedItems[index].unit_type = e.target.value;
                                      setItems(updatedItems);
                                    }}
                                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white select-no-arrow"
                                  >
                                    <option value="SFT">SFT</option>
                                    <option value="RFT">RFT</option>
                                    <option value="PCS">PCS</option>
                                    <option value="KG">KG</option>
                                    <option value="LTR">LTR</option>
                                    <option value="MTR">MTR</option>
                                    <option value="LUMPSUM">LUMPSUM</option>
                                    <option value="NOS">NOS</option>
                                    <option value="SQ.M">SQ.M</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">Qty *</label>
                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={item.quantity === '' ? '' : item.quantity}
                                    onChange={(e) => {
                                      const updatedItems = [...items];
                                      const val = e.target.value;
                                      updatedItems[index].quantity = val === '' ? '' : parseInt(val);
                                      setItems(updatedItems);
                                    }}
                                    onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                    onWheel={(e) => e.target.blur()}
                                    className="w-full px-3 py-2 text-sm text-center border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Price *</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={item.unit_price === '' ? '' : item.unit_price}
                                  onChange={(e) => {
                                    const updatedItems = [...items];
                                    const val = e.target.value;
                                    updatedItems[index].unit_price = val === '' ? '' : parseFloat(val);
                                    setItems(updatedItems);
                                  }}
                                  onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                  onWheel={(e) => e.target.blur()}
                                  className="w-full px-3 py-2 text-sm text-right border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                {showDiscount && (
                                  <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Disc %</label>
                                    <input
                                      type="number"
                                      min="0"
                                      max="100"
                                      step="0.01"
                                      value={item.discount === '' ? '' : item.discount}
                                      onChange={(e) => {
                                        const updatedItems = [...items];
                                        const val = e.target.value;
                                        updatedItems[index].discount = val === '' ? '' : parseFloat(val);
                                        setItems(updatedItems);
                                      }}
                                      onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                      onWheel={(e) => e.target.blur()}
                                      className="w-full px-3 py-2 text-sm text-center border-2 border-[#C5A57D] rounded-md focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    />
                                  </div>
                                )}
                                <div className={showDiscount ? "" : "col-span-2"}>
                                  <label className="block text-sm font-medium text-slate-700 mb-1">GST %</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={item.gst_rate === '' ? '' : item.gst_rate}
                                    onChange={(e) => {
                                      const updatedItems = [...items];
                                      const val = e.target.value;
                                      updatedItems[index].gst_rate = val === '' ? '' : parseFloat(val);
                                      setItems(updatedItems);
                                    }}
                                    onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                    onWheel={(e) => e.target.blur()}
                                    className="w-full px-3 py-2 text-sm text-center border border-slate-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  />
                                </div>
                              </div>

                              <div className="bg-white rounded-md p-3">
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-slate-700">Amount:</span>
                                  <span className="font-bold text-slate-900">₹{itemTotal}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Add New Item Button - Mobile */}
                      <div className="bg-white border border-slate-300 rounded-lg p-4 shadow-sm border-dashed">
                        <button
                          type="button"
                          onClick={() => setItems([...items, {
                            category_id: '',
                            product_id: '',
                            product_name: '',
                            hsn_code: '',
                            product_instructions: '',
                            quantity: 1,
                            unit_price: 0,
                            unit_type: 'SFT',
                            gst_rate: '',
                            gst_amount: 0,
                            amount: 0,
                            total_amount: 0,
                            tax: 0,
                            discount: '',
                            subtotal: 0
                          }])}
                          className="w-full flex items-center justify-center px-4 py-3 text-indigo-600 hover:text-indigo-700 font-medium text-sm border border-indigo-200 rounded-md hover:bg-indigo-50 transition-colors"
                        >
                          <Plus className="h-5 w-5 mr-2" />
                          Add New Item
                        </button>
                      </div>
                    </div>
                    </div>
                    )}
                  </div>

                  {/* Estimate Summary Section */}
                  <div className="bg-white border-2 border-black rounded-lg p-6 mt-8">
                    <h4 className="text-lg font-bold text-[#0A5F38] uppercase tracking-wide border-b-2 border-black pb-2 mb-6">ESTIMATE SUMMARY</h4>



                    <div className={`grid grid-cols-1 ${(isWoodWorkMode || items.some(i => i.isWoodWork)) ? 'md:grid-cols-1' : 'md:grid-cols-2'} gap-8`}>
                      {/* Left Column - Summary */}
                      <div className="space-y-3">
                        {(() => {
                          // Calculate totals based on effective items (handling showDiscount toggle)
                          const effectiveItems = items.map(item => ({
                            ...item,
                            discount: showDiscount ? (parseFloat(item.discount) || 0) : 0
                          }));

                          const subtotal = effectiveItems.reduce((sum, item) => {
                            const rawAmount = (item.quantity || 0) * (item.unit_price || 0);
                            return sum + rawAmount;
                          }, 0);

                          const totalDiscount = effectiveItems.reduce((sum, item) => {
                            const rawAmount = (item.quantity || 0) * (item.unit_price || 0);
                            const discountAmount = (rawAmount * (item.discount || 0)) / 100;
                            return sum + discountAmount;
                          }, 0);

                          const taxableAmount = subtotal - totalDiscount;

                          // Group GST by rate
                          const gstByRate = {};
                          effectiveItems.forEach(item => {
                            const rawAmount = (item.quantity || 0) * (item.unit_price || 0);
                            const discountAmount = (rawAmount * (item.discount || 0)) / 100;
                            const itemTaxableAmount = rawAmount - discountAmount;
                            const gstRate = item.gst_rate || 0;
                            const gstAmount = (itemTaxableAmount * gstRate) / 100;

                            if (gstRate > 0) {
                              if (!gstByRate[gstRate]) {
                                gstByRate[gstRate] = 0;
                              }
                              gstByRate[gstRate] += gstAmount;
                            }
                          });

                          const totalGst = Object.values(gstByRate).reduce((a, b) => a + b, 0);
                          const grandTotal = taxableAmount + totalGst;

                          // Use new GST breakdown calculation
                          const gstBreakdown = calculateTotalGSTBreakdown(effectiveItems, formData.customer.gstNumber, companySettings);

                          return (
                            <>
                              <div className="flex justify-between py-2 border-b border-slate-200">
                                <span className="text-slate-700">Subtotal:</span>
                                <span className="font-semibold text-slate-900">₹{gstBreakdown.totalTaxableAmount}</span>
                              </div>
                              {showDiscount && (
                                <div className="flex justify-between py-2 border-b border-slate-200">
                                  <span className="text-slate-700">Discount Applied:</span>
                                  <span className="font-semibold text-red-600">-₹{totalDiscount}</span>
                                </div>
                              )}

                              {/* GST Breakdown using new component */}
                              <div className="py-2">
                                <GSTBreakdown
                                  items={effectiveItems}
                                  buyerGST={formData.customer.gstNumber}
                                  companySettings={companySettings}
                                  showTitle={true}
                                  className="text-sm"
                                />
                              </div>

                              <div className="flex justify-between py-3 border-t-2 border-slate-300 font-bold text-lg">
                                <span className="text-slate-900">Grand Total:</span>
                                <span className="text-[#0A5F38]">₹{(gstBreakdown.totalTaxableAmount + gstBreakdown.totalGST).toFixed(1)}</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      {/* Right Column - Calculation Details */}
                      {!(isWoodWorkMode || items.some(i => i.isWoodWork)) && (
                        <div className="bg-white rounded-lg p-4 space-y-3 text-sm border-2 border-slate-100">
                          <div className="font-bold text-[#0A5F38] mb-3 uppercase tracking-wide">CALCULATION DETAILS</div>
                          {items.length > 0 ? (
                            items.map((item, idx) => {
                              const rawAmount = roundForCalculation((item.quantity || 0) * (item.unit_price || 0));
                              const discountAmount = showDiscount ? roundForCalculation((rawAmount * (item.discount || 0)) / 100) : 0;
                              const taxableAmount = roundForCalculation(rawAmount - discountAmount);

                              // Calculate GST breakdown for this item
                              const itemGSTBreakdown = calculateTotalGSTBreakdown([{ ...item, discount: showDiscount ? item.discount : 0 }], formData.customer.gstNumber, companySettings);
                              const itemTotal = roundForCalculation(taxableAmount + itemGSTBreakdown.totalGST);

                              return (
                                <div key={idx} className="border-b border-slate-200 pb-2">
                                  <div className="font-medium text-slate-700">Item {idx + 1}: {item.product_name || 'Unnamed'}</div>
                                  {item.product_instructions && (
                                    <div className="text-xs text-slate-500 mt-0.5 italic">
                                      Note: {item.product_instructions}
                                    </div>
                                  )}
                                  <div className="text-xs text-slate-600 space-y-1 mt-1">
                                    <div>Qty: {item.quantity} × ₹{item.unit_price || 0} = ₹{rawAmount.toFixed(1)}</div>
                                    {item.discount > 0 && <div>Discount ({item.discount}%): -₹{discountAmount.toFixed(1)}</div>}
                                    <div>Taxable: ₹{taxableAmount.toFixed(1)}</div>

                                    {/* Product-level GST breakdown */}
                                    {item.gst_rate > 0 && (
                                      <div className="ml-2 space-y-0.5">
                                        <div className="font-medium text-slate-700">GST Breakdown:</div>
                                        {itemGSTBreakdown.isSameState ? (
                                          <>
                                            <div>• CGST @{(item.gst_rate / 2)}%: +₹{(itemGSTBreakdown.totalGST / 2).toFixed(1)}</div>
                                            <div>• SGST @{(item.gst_rate / 2)}%: +₹{(itemGSTBreakdown.totalGST / 2).toFixed(1)}</div>
                                          </>
                                        ) : (
                                          <div>• IGST @{item.gst_rate}%: +₹{itemGSTBreakdown.totalGST}</div>
                                        )}
                                        <div className="font-medium">Total GST: +₹{itemGSTBreakdown.totalGST}</div>
                                      </div>
                                    )}

                                    <div className="font-semibold text-slate-700">Total: ₹{itemTotal.toFixed(1)}</div>
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="text-slate-500">No items added yet</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Terms and Conditions & Material Description */}
                  <div className="mt-8 space-y-6">
                    <TermsAndConditionsInput
                      value={formData.termsAndConditions}
                      onChange={(value) => setFormData({ ...formData, termsAndConditions: value })}
                      label="Terms and Conditions"
                    />
                    
                    {isWoodWorkMode && (
                      <TermsAndConditionsInput
                        value={formData.materialDescription}
                        onChange={(value) => setFormData({ ...formData, materialDescription: value })}
                        label="Material Description"
                        placeholder="Enter material specifications (e.g., 1. Plywood: 18mm Greenply...)"
                      />
                    )}
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t-2 border-black">
                  <button
                    type="button"
                    onClick={() => setActiveTab('history')}
                    className="px-6 py-3 border-2 border-[#C5A57D] text-slate-700 rounded-2xl hover:bg-[#F8F5E6] hover:text-[#0A5F38] transition-colors font-bold uppercase tracking-wide"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-3 bg-[#0A5F38] text-white rounded-2xl hover:bg-[#084B2C] transition-all font-bold shadow-lg shadow-green-900/20 disabled:opacity-50 uppercase tracking-wide"
                  >
                    <Save className="h-4 w-4 mr-2 inline" />
                    {loading
                      ? (isEditing ? 'Updating...' : 'Creating...')
                      : (isEditing ? 'Update Estimate' : 'Create Estimate')
                    }
                  </button>
                </div>
              </form>
            )}
          </div>
        )
        }

        {/* View Modal */}
        {
          showViewModal && selectedEstimate && (
            <AppModal
              open
              onClose={() => setShowViewModal(false)}
              size="lg" compactFooter
              title={
                    selectedEstimate.cancelled_closed_order || selectedEstimate.closed_order_cancelled ||
                      (selectedEstimate.status === 'closed_order' && selectedEstimate.cancellation_status === 'cancelled') ||
                      (selectedEstimate.status === 'cancelled' && selectedEstimate.cancelled_closed_order) ? 'Cancelled Closed Order Details' :
                      selectedEstimate.cancelled_invoice || selectedEstimate.invoice_cancelled || (selectedEstimate.status === 'converted_to_invoice' && selectedEstimate.cancellation_status === 'cancelled') ? 'Cancelled Invoice Details' :
                        selectedEstimate.status === 'converted_to_invoice' || selectedEstimate.status === 'sold' ? 'Invoice Details' :
                          selectedEstimate.status === 'closed_order' ? 'Closed Order Details' : 'Estimate Details'
              }
              footer={
                <>
                      <button
                        onClick={() => setShowViewModal(false)}
                        className={modalButton.secondary}
                      >
                        Close
                      </button>
                      {selectedEstimate?.status === 'converted_to_invoice' || selectedEstimate?.status === 'sold' || selectedEstimate?.status === 'closed_order' ? (
                        <div className="flex flex-wrap items-center justify-end gap-3">
                        {(selectedEstimate.invoice_number || selectedEstimate.invoiceNumber) && selectedEstimate.convertedToInvoiceId && (
                          <button
                            onClick={() => handleInvoicePdfDownload(selectedEstimate.convertedToInvoiceId, selectedEstimate.invoice_number || selectedEstimate.invoiceNumber)}
                            className="px-6 py-2 bg-[#2980B9] text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Download Invoice {selectedEstimate.invoice_number || selectedEstimate.invoiceNumber}
                          </button>
                        )}
                        {/* Copy Guest Link Button */}
                        <button
                          onClick={() => {
                            if (!selectedEstimate.shareToken) {
                              toast.error('Share token not available yet. Please close and reopen this modal.');
                              return;
                            }
                            const guestLink = `${window.location.origin}/view/document/${selectedEstimate.shareToken}`;
                            navigator.clipboard.writeText(guestLink)
                              .then(() => toast.success('Guest link copied to clipboard!'))
                              .catch(() => toast.error('Failed to copy link'));
                          }}
                          className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          <span>Copy Guest Link</span>
                        </button>
                        <div className="text-sm text-slate-600">
                          <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                            {selectedEstimate?.status === 'closed_order' ? 'Already Converted to Closed Order' : 'Already Converted to Invoice'}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        {/* Edit Button - Only show if NOT already converted/cancelled */}
                        {(!selectedEstimate.status || (selectedEstimate.status !== 'converted_to_invoice' && selectedEstimate.status !== 'closed_order' && selectedEstimate.status !== 'cancelled')) && (
                          <button
                            onClick={() => {
                              setShowViewModal(false);
                              setEditingEstimate(selectedEstimate);
                              setEditingCustomer({
                                name: selectedEstimate.customer_name || '',
                                phone: selectedEstimate.customer_phone || '',
                                email: selectedEstimate.customer_email || '',
                                address: selectedEstimate.customer_address || '',
                                gstNumber: selectedEstimate.customer_gst || selectedEstimate.customer?.gstNumber || '',
                                state: selectedEstimate.customer_state || selectedEstimate.party_state_name || (selectedEstimate.customer_gst ? getStateNameFromGST(selectedEstimate.customer_gst) : '') || ''
                              });
                              setEditingProducts(recalcItemsAmounts(selectedEstimate.items || []));
                              setEditingApplyDiscount((selectedEstimate.items || []).some(item => (item.discount || 0) > 0));
                              setShowEditEstimateModal(true);
                            }}
                            className="px-6 py-2 bg-[#E67E22] text-white rounded-lg hover:bg-[#d5731e] transition-colors flex items-center space-x-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>Edit</span>
                          </button>
                        )}
                        {/* Convert to Invoice Button - Only show if NOT already converted/cancelled */}
                        {(!selectedEstimate.status || (selectedEstimate.status !== 'converted_to_invoice' && selectedEstimate.status !== 'closed_order' && selectedEstimate.status !== 'cancelled')) && (
                          <button
                            onClick={() => {
                              handleConvertToInvoice(selectedEstimate);
                            }}
                            className="px-6 py-2 bg-[#4D4DFF] text-white rounded-lg hover:bg-[#3d3ddb] transition-colors"
                          >
                            Convert to Invoice
                          </button>
                        )}

                        {/* Convert to Closed Order Button - Only show if NOT already converted/cancelled */}
                        {(!selectedEstimate.status || (selectedEstimate.status !== 'converted_to_invoice' && selectedEstimate.status !== 'closed_order' && selectedEstimate.status !== 'cancelled')) && (
                          <button
                            onClick={() => {
                              handleConvertToClosedOrder(selectedEstimate);
                            }}
                            className="px-6 py-2 bg-[#27AE60] text-white rounded-lg hover:bg-[#219150] transition-colors"
                          >
                            Convert to Closed Order
                          </button>
                        )}
                        {/* Copy Guest Link Button */}
                        <button
                          onClick={() => {
                            if (!selectedEstimate.shareToken) {
                              toast.error('Share token not available yet. Please close and reopen this modal.');
                              return;
                            }
                            const guestLink = `${window.location.origin}/view/document/${selectedEstimate.shareToken}`;
                            navigator.clipboard.writeText(guestLink)
                              .then(() => toast.success('Guest link copied to clipboard!'))
                              .catch(() => toast.error('Failed to copy link'));
                          }}
                          className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          <span>Copy Guest Link</span>
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const loadingToast = toast.loading('Generating PDF...');
                              // Fetch fresh data before generating PDF
                              const response = await api.get(`/sales/estimates/${selectedEstimate.id || selectedEstimate._id}`);
                              const freshData = response.data;

                              // Ensure items are array and customer data is present
                              const pdfData = {
                                ...freshData,
                                items: freshData.items || [],
                                customer_name: freshData.customer?.name || freshData.customer_name || 'Walk-in Customer',
                                customer_address: freshData.customer?.address || freshData.customer_address || '',
                                customer_city: freshData.customer?.city || freshData.customer_city || ''
                              };

                              await generatePDF(pdfData, 'estimate', companySettings);
                              toast.dismiss(loadingToast);
                              toast.success('PDF downloaded successfully');
                              setShowViewModal(false);
                            } catch (error) {
                              console.error('Failed to generate PDF:', error);
                              toast.error('Failed to generate PDF');
                            }
                          }}
                          className="px-6 py-2 bg-[#2980B9] text-white rounded-lg hover:bg-[#206694] transition-colors"
                        >
                          Download PDF
                        </button>
                      </div>
                    )}
                </>
              }
            >

                  {/* Cancellation Status Badge - Show prominently if cancelled */}
                  {(selectedEstimate.cancelled_closed_order || selectedEstimate.closed_order_cancelled ||
                    (selectedEstimate.status === 'closed_order' && selectedEstimate.cancellation_status === 'cancelled') ||
                    (selectedEstimate.status === 'cancelled' && selectedEstimate.cancelled_closed_order) ||
                    selectedEstimate.cancelled_invoice || selectedEstimate.invoice_cancelled ||
                    (selectedEstimate.status === 'converted_to_invoice' && selectedEstimate.cancellation_status === 'cancelled')) && (
                      <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div>
                            <div className="text-lg font-bold text-red-800">
                              {selectedEstimate.cancelled_closed_order || selectedEstimate.closed_order_cancelled ||
                                (selectedEstimate.status === 'closed_order' && selectedEstimate.cancellation_status === 'cancelled') ||
                                (selectedEstimate.status === 'cancelled' && selectedEstimate.cancelled_closed_order)
                                ? 'CANCELLED CLOSED ORDER'
                                : 'CANCELLED INVOICE'}
                            </div>
                            {selectedEstimate.cancellation_reason && (
                              <div className="text-sm text-red-700 mt-1">
                                Reason: {selectedEstimate.cancellation_reason}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                  {/* PDF-Style Layout */}
                  <div className="overflow-x-auto">
                  <div className="border-2 border-black bg-white rounded-xl min-w-[760px]">
                    {/* Header Section */}
                    <div className="border-b-2 border-black p-6 bg-white">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center space-x-4">
                          <div className="w-16 h-16 bg-[#C5A57D] rounded-lg flex items-center justify-center overflow-hidden">
                            <img
                              src="https://5.imimg.com/data5/TQ/IX/FZ/SELLER-66617534/logo-90x90.jpg"
                              alt="Company Logo"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <span className="text-white font-bold text-xl" style={{ display: 'none' }} id="logo-fallback">IT</span>
                          </div>
                          <div>
                            <h1 className="text-2xl font-bold text-slate-900">{companySettings?.companyName || 'IDEAL TRENDZ INTERIORS'}</h1>
                            <div className="text-sm text-slate-600 mt-1">
                              <div>{companySettings?.addressLine1 || '# Vertex Privilege, Villa No. 32, Nizampet Road'}</div>
                              {companySettings?.addressLine2 && <div>{companySettings.addressLine2}</div>}
                              <div>{companySettings?.city || 'HYDERNAGAR'}, {companySettings?.state || 'TELANGANA'} - {companySettings?.pincode || '500072'}</div>
                              <div>Email: {companySettings?.emailId || 'idealtrendz.int@gmail.com'}</div>
                              <div>Contact No. {companySettings?.contactNumber || '9014814901'}</div>
                            </div>
                          </div>
                        </div>
                        <div className="text-right text-sm">
                          <div><strong>GST:</strong> {companySettings?.gstNumber || 'N/A'}</div>
                          <div><strong>PAN:</strong> {companySettings?.panNumber || 'N/A'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Document Title */}
                    <div className="border-b-2 border-black p-4 text-center bg-white">
                      <h2 className="text-xl font-bold text-[#0A5F38]">
                        {selectedEstimate.status === 'converted_to_invoice' || selectedEstimate.status === 'sold' ? 'TAX INVOICE' : (selectedEstimate.is_proforma || selectedEstimate.isProforma) ? 'PROFORMA INVOICE' : 'ESTIMATE'}
                      </h2>
                    </div>

                    {/* Customer and Document Info */}
                    <div className="border-b-2 border-black p-6 bg-white">
                      <div className="grid grid-cols-2 gap-8">
                        {/* Left Side - Customer Info */}
                        <div>
                          <div className="mb-4">
                            <div className="font-semibold text-slate-900 mb-2">To</div>
                            <div className="font-bold text-lg">{selectedEstimate.customer_name || 'WALK-IN CUSTOMER'}</div>
                            <div className="text-slate-600">{selectedEstimate.customer_address || selectedEstimate.customer_city || ''}</div>
                            {selectedEstimate.customer_phone && (
                              <div className="text-slate-600 mt-1">Phone: {selectedEstimate.customer_phone}</div>
                            )}
                            {selectedEstimate.customer_email && (
                              <div className="text-slate-600 mt-1">Email: {selectedEstimate.customer_email}</div>
                            )}
                          </div>
                        </div>

                        {/* Right Side - Document Info with Fixed Alignment - Shifted Right */}
                        <div className="ml-auto pr-0">
                          <div className="space-y-2">
                            <div className="flex gap-4">
                              <span className="font-semibold w-40">DATE:</span>
                              <span className="flex-1 break-words">{selectedEstimate.date ? new Date(selectedEstimate.date).toLocaleDateString('en-GB') : selectedEstimate.createdAt ? new Date(selectedEstimate.createdAt).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}</span>
                            </div>
                            <div className="flex gap-4">
                              <span className="font-semibold w-40">{selectedEstimate.status === 'converted_to_invoice' || selectedEstimate.status === 'sold' ? 'INVOICE No.:' : (selectedEstimate.is_proforma || selectedEstimate.isProforma) ? 'PROFORMA No.:' : 'ESTIMATE No.:'}</span>
                              <span className="flex-1 break-words">{(selectedEstimate.estimate_number || selectedEstimate._id?.slice(-8) || 'EST001').replace(/-CANCEL-\d+/g, ' (Cancelled)')}</span>
                            </div>
                            <div className="flex gap-4">
                              <span className="font-semibold w-40">PARTY PAN No.:</span>
                              <span className="flex-1 break-words">{selectedEstimate.party_pan_no || ''}</span>
                            </div>
                            <div className="flex gap-4">
                              <span className="font-semibold w-40">PARTY GST No.:</span>
                              <span className="flex-1 break-words">{selectedEstimate.customer_gst || selectedEstimate.party_gst_no || ''}</span>
                            </div>
                            <div className="flex gap-4">
                              <span className="font-semibold w-40">PARTY STATE NAME:</span>
                              <span className="flex-1 break-words">
                                {selectedEstimate.customer_state ||
                                  selectedEstimate.party_state_name ||
                                  (selectedEstimate.customer_gst ? getStateNameFromGST(selectedEstimate.customer_gst) : '') ||
                                  'N/A'}
                              </span>
                            </div>

                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Product Table */}
                    <div className="border-b-2 border-black overflow-x-auto">
                      <table className="w-full">
                        {selectedEstimate.isWoodWork ? (
                          /* ══ WOOD WORK VIEW MODAL TABLE ══ */
                          <>
                            <thead>
                              <tr className="bg-[#0A5F38] border-b border-black">
                                <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '50px' }}>SI</th>
                                <th className="border-r border-black p-3 text-left text-xs font-bold text-white uppercase">Type</th>
                                <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '100px' }}>Rate (₹)</th>
                                <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '65px' }}>Nos</th>
                                <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '80px' }}>L</th>
                                <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '80px' }}>B</th>
                                <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '90px' }}>Qty</th>
                                <th className="border-r border-black p-3 text-center text-xs font-bold text-white uppercase" style={{ width: '80px' }}>GST %</th>
                                <th className="p-3 text-right text-xs font-bold text-white uppercase" style={{ width: '120px' }}>Amount (₹)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const CAT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];
                                const rows = [];
                                const items = selectedEstimate.items || [];

                                // Build ordered list of unique categories
                                const categoryOrder = [];
                                items.forEach(item => {
                                  const cat = item.categoryLabel || '';
                                  if (!categoryOrder.includes(cat)) categoryOrder.push(cat);
                                });

                                categoryOrder.forEach((catLabel) => {
                                  const catIdx = categoryOrder.indexOf(catLabel);
                                  const catEntries = items.filter(item => (item.categoryLabel || '') === catLabel);

                                  // Category header row
                                  rows.push(
                                    <tr key={'cat-view-' + catLabel} style={{ backgroundColor: '#BDD7EE' }}>
                                      <td className="border-r border-black px-3 py-2 text-center font-bold text-slate-900 border-b border-black">
                                        {CAT_LETTERS[catIdx] || (catIdx + 1)}
                                      </td>
                                      <td colSpan="7" className="border-r border-black px-3 py-2 font-bold text-slate-900 uppercase tracking-wider border-b border-black">
                                        {catLabel}
                                      </td>
                                      <td className="px-3 py-2 border-b border-black"></td>
                                    </tr>
                                  );

                                  // Item rows
                                  catEntries.forEach((item, siNo) => {
                                    const qty = computeWoodWorkQty(item.nos, item.length, item.breadth);
                                    const rate = item.unit_price || 0;
                                    const totalAmount = item.total_amount || (qty * rate * (1 + (item.gst_rate || 0) / 100));

                                    rows.push(
                                      <tr key={'item-view-' + (item._id || siNo)} className="border-b border-black hover:bg-slate-50 transition-colors">
                                        <td className="border-r border-black px-2 py-2 text-center text-slate-600">{siNo + 1}</td>
                                        <td className="border-r border-black px-3 py-2 font-medium break-words max-w-[300px]">
                                          {item.product_name || 'Unnamed Item'}
                                        </td>
                                        <td className="border-r border-black px-2 py-2 text-center">
                                          {rate.toLocaleString('en-IN', { minimumFractionDigits: 1 })}
                                        </td>
                                        <td className="border-r border-black px-2 py-2 text-center">{item.nos || 0}</td>
                                        <td className="border-r border-black px-2 py-2 text-center">{item.length || 0}</td>
                                        <td className="border-r border-black px-2 py-2 text-center">{item.breadth || 0}</td>
                                        <td className="border-r border-black px-2 py-2 text-center font-semibold bg-slate-50">{qty}</td>
                                        <td className="border-r border-black px-2 py-2 text-center font-medium">{item.gst_rate || 0}%</td>
                                        <td className="px-3 py-2 text-right font-bold text-slate-800 bg-slate-50">
                                          ₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                      </tr>
                                    );
                                  });
                                });

                                return rows;
                              })()}
                            </tbody>
                          </>
                        ) : (
                          /* ══ NORMAL VIEW MODAL TABLE ══ */
                          <>
                            <thead>
                              <tr className="bg-[#0A5F38] border-b border-black">
                                <th className="border-r border-black p-3 text-left text-xs font-semibold text-white uppercase">S.No</th>
                                <th className="border-r border-black p-3 text-left text-xs font-semibold text-white uppercase">Product Description</th>
                                <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">HSN Code</th>
                                <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">Unit</th>
                                <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">QTY</th>
                                <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">Rate</th>
                                {selectedEstimate?.items?.some(item => (item.discount || 0) > 0) && (
                                  <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">Discount %</th>
                                )}
                                <th className="border-r border-black p-3 text-right text-xs font-semibold text-white uppercase">Amount</th>
                                <th className="border-r border-black p-3 text-center text-xs font-semibold text-white uppercase">GST %</th>
                                <th className="border-r border-black p-3 text-right text-xs font-semibold text-white uppercase">GST Amount</th>
                                <th className="p-3 text-right text-xs font-semibold text-white uppercase">Total Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedEstimate.items && selectedEstimate.items.length > 0 ? (
                                selectedEstimate.items.map((item, index) => {
                                  const baseAmount = roundForCalculation((item.quantity || 0) * (item.unit_price || 0));
                                  const discountAmount = roundForCalculation((baseAmount * (item.discount || 0)) / 100);
                                  const taxableAmount = item.amount || (baseAmount - discountAmount);
                                  const gstRate = item.gst_rate || 0;
                                  const gstAmount = item.gst_amount || roundForCalculation((taxableAmount * gstRate) / 100);
                                  const totalAmount = item.total_amount || roundForCalculation(taxableAmount + gstAmount);

                                  return (
                                    <tr key={index} className="border-b border-black">
                                      <td className="border-r border-black p-3 text-center break-words">{index + 1}</td>
                                      <td className="border-r border-black p-3 break-words">
                                        <div className="font-medium whitespace-pre-wrap break-words">{item.product_name || `Product #${index + 1}`}</div>
                                      </td>
                                      <td className="border-r border-black p-3 text-center break-words">{item.hsn_code || '-'}</td>
                                      <td className="border-r border-black p-3 text-center break-words">{item.unit_type || 'SFT'}</td>
                                      <td className="border-r border-black p-3 text-center break-words">{item.quantity || 0}</td>
                                      <td className="border-r border-black p-3 text-center break-words">{(item.unit_price || 0).toFixed(1)}</td>
                                      {selectedEstimate?.items?.some(i => (i.discount || 0) > 0) && (
                                        <td className="border-r border-black p-3 text-center break-words">{item.discount || 0}</td>
                                      )}
                                      <td className="border-r border-black p-3 text-right break-words">₹{taxableAmount.toFixed(1)}</td>
                                      <td className="border-r border-black p-3 text-center break-words">{gstRate}</td>
                                      <td className="border-r border-black p-3 text-right break-words">₹{gstAmount.toFixed(1)}</td>
                                      <td className="p-3 text-right font-semibold break-words">₹{totalAmount.toFixed(1)}</td>
                                    </tr>
                                  );
                                })
                              ) : (
                                <tr>
                                  <td colSpan="11" className="p-6 text-center text-slate-500">No items found</td>
                                </tr>
                              )}
                            </tbody>
                          </>
                        )}
                      </table>
                    </div>

                    {/* Summary Section */}
                    <div className="p-6">
                      <div className="flex justify-between">
                        {/* Left Side - Bank Details */}
                        <div className="w-1/2">
                          <h3 className="font-semibold text-slate-900 mb-3">OUR BANK DETAILS</h3>
                          <div className="text-sm text-slate-600 space-y-1">
                            <div><strong>Bank Name:</strong> {companySettings?.bankName || 'Not Configured'}</div>
                            <div><strong>A/C No:</strong> {companySettings?.accountNumber || 'Not Configured'}</div>
                            <div><strong>IFSC:</strong> {companySettings?.ifscCode || 'Not Configured'}</div>
                            <div><strong>Branch:</strong> {companySettings?.branchName || 'Not Configured'}</div>
                          </div>
                        </div>

                        {/* Right Side - Totals */}
                        <div className="w-1/3">
                          <div className="space-y-3">
                            {(() => {
                              // Use stored calculated values instead of recalculating
                              const subtotal = selectedEstimate.items?.reduce((sum, item) =>
                                sum + (item.amount || 0), 0) || 0; // Sum of amounts after discount
                              const totalDiscount = selectedEstimate.items?.reduce((sum, item) => {
                                const rawAmount = (item.quantity || 0) * (item.unit_price || 0);
                                return sum + (rawAmount * (item.discount || 0)) / 100;
                              }, 0) || 0;
                              const taxableAmount = subtotal;
                              const totalGst = selectedEstimate.items?.reduce((sum, item) => {
                                return sum + (item.gst_amount || 0); // Use stored GST amounts
                              }, 0) || 0;
                              const grandTotal = selectedEstimate.items?.reduce((sum, item) => {
                                return sum + (item.total_amount || 0); // Use stored total amounts
                              }, 0) || 0;
                              const outstanding = grandTotal - (selectedEstimate.amountReceived || 0);

                              // Use new GST breakdown for view modal
                              const gstBreakdown = calculateTotalGSTBreakdown(selectedEstimate.items || [], selectedEstimate.customer_gst || '', companySettings);
                              const calculatedGrandTotal = gstBreakdown.totalTaxableAmount + gstBreakdown.totalGST;
                              const calculatedOutstanding = calculatedGrandTotal - (selectedEstimate.amountReceived || 0);

                              return (
                                <>
                                  <GSTSummary
                                    items={selectedEstimate.items || []}
                                    buyerGST={selectedEstimate.customer_gst || ''}
                                    companySettings={companySettings}
                                    subtotal={gstBreakdown.totalTaxableAmount}
                                    className="space-y-2"
                                  />

                                  {/* Outstanding Amount */}
                                  <div className="flex justify-between py-2 font-bold text-lg mt-4">
                                    <span>Total Outstanding:</span>
                                    <span>₹{calculatedOutstanding.toFixed(1)}</span>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>


                    </div>

                    {/* Terms and Conditions & Material Description */}
                    <div className="border-t-2 border-black bg-white rounded-b-xl">
                      {selectedEstimate.termsAndConditions && (
                        <div className="p-6 border-b border-black/10">
                          <div className="font-bold text-slate-900 mb-2">Terms and Conditions:</div>
                          <div className="text-sm text-slate-700 whitespace-pre-wrap break-words">
                            {selectedEstimate.termsAndConditions}
                          </div>
                        </div>
                      )}

                      {selectedEstimate.materialDescription && (
                        <div className="p-6">
                          <div className="font-bold text-slate-900 mb-2">Material Description:</div>
                          <div className="text-sm text-slate-700 whitespace-pre-wrap break-words">
                            {selectedEstimate.materialDescription}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  </div>
            </AppModal>
          )
        }

        {/* Convert to Invoice Modal */}
        {
          showPaymentModal && convertingEstimate && (
            <AppModal
              open
              onClose={() => setShowPaymentModal(false)}
              size="xl"
              closeOnEsc={!showEditProductsModal}
              title="Convert to Invoice"
              footer={
                <>
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className={modalButton.secondary}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePaymentSubmit}
                    disabled={loading || (paymentData.gst_number && paymentData.gst_number.length > 0 && paymentData.gst_number.length < 15)}
                    className={modalButton.primary}
                  >
                    {loading ? 'Converting...' : 'Create Invoice'}
                  </button>
                </>
              }
            >

                {/* Estimate Summary */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Estimate Summary</h3>
                  <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Estimate Number</div>
                      <div className="font-semibold">
                        {convertingEstimate.estimate_number || convertingEstimate._id?.slice(-8) || 'EST' + convertingEstimate.id}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Estimate Date</div>
                      <div className="font-semibold">
                        {convertingEstimate.date ? new Date(convertingEstimate.date).toLocaleDateString('en-IN') : convertingEstimate.createdAt ? new Date(convertingEstimate.createdAt).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Total Amount</div>
                      <div className="font-semibold">
                        ₹{(convertingEstimate.totalAmount || convertingEstimate.total_amount || 0).toFixed(1)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Invoice Date</label>
                      <input
                        type="date"
                        value={paymentData.date || ''}
                        onChange={(e) => setPaymentData({ ...paymentData, date: e.target.value })}
                        className="w-full px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent text-sm font-semibold"
                      />
                    </div>
                  </div>
                </div>

                {/* Invoice Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Invoice Information</h3>
                  <div className="bg-white rounded-lg p-4 border-2 border-black">
                    <div className="max-w-md">
                      <InvoiceNumberField
                        value={invoiceNumber || ''}
                        onChange={setInvoiceNumber}
                        label="Invoice Number"
                        placeholder="Enter invoice number"
                      />
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-slate-900">Customer Information</h3>
                    <button
                      onClick={() => {
                        if (!editMode) {
                          setEditingCustomer({
                            name: convertingEstimate.customer_name || '',
                            phone: convertingEstimate.customer_phone || '',
                            email: convertingEstimate.customer_email || '',
                            address: convertingEstimate.customer_address || ''
                          });
                        }
                        setEditMode(!editMode);
                      }}
                      className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                    >
                      {editMode ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  {editMode ? (
                    <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                        <input
                          type="text"
                          value={editingCustomer.name || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={editingCustomer.countryCode || ''}
                            onChange={(e) => setEditingCustomer({ ...editingCustomer, countryCode: e.target.value })}
                            placeholder="+91"
                            maxLength={5}
                            className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                          <input
                            type="tel"
                            value={editingCustomer.phone || ''}
                            onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value.replace(/\D/g, '') })}
                            placeholder={editingCustomer.countryCode ? 'Mobile number' : '10-digit mobile number'}
                            maxLength={editingCustomer.countryCode ? 11 : 10}
                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                        <input
                          type="email"
                          value={editingCustomer.email || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                        <textarea
                          value={editingCustomer.address || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          rows="2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                        <select
                          value={paymentData.state || ''}
                          onChange={(e) => setPaymentData({ ...paymentData, state: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        >
                          <option value="">Select State</option>
                          {stateOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">GST Number</label>
                        <input
                          type="text"
                          value={paymentData.gst_number || ''}
                          onChange={(e) => {
                            const newGst = e.target.value;
                            const newState = getStateNameFromGST(newGst);
                            setPaymentData(prev => ({
                              ...prev,
                              gst_number: newGst,
                              state: newState || prev.state
                            }));
                          }}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          placeholder="Enter GST Number"
                          maxLength="15"
                        />
                        {paymentData.gst_number && paymentData.gst_number.length > 0 && paymentData.gst_number.length < 15 && (
                          <div className="text-orange-500 text-sm mt-1">
                            GST number should be 15 characters long if provided
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Name</div>
                        <div className="font-semibold">{editingCustomer.name || convertingEstimate.customer_name || 'Walk-in Customer'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Phone</div>
                        <div className="font-semibold">{editingCustomer.phone || convertingEstimate.customer_phone || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Email</div>
                        <div className="font-semibold">{editingCustomer.email || convertingEstimate.customer_email || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Address</div>
                        <div className="font-semibold">{editingCustomer.address || convertingEstimate.customer_address || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">State</div>
                        <div className="font-semibold">{paymentData.state || convertingEstimate.party_state_name || convertingEstimate.customer_state || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">GST Number</div>
                        <div className="font-semibold">{paymentData.gst_number || 'N/A'}</div>
                        {paymentData.gst_number && paymentData.gst_number.length > 0 && paymentData.gst_number.length < 15 && (
                          <div className="text-red-500 text-xs mt-1 font-bold animate-pulse">
                            ⚠️ Invalid GST Number ({paymentData.gst_number.length}/15 chars). Please Click Edit to Fix.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-slate-900">Product Details</h3>
                    <div className="space-x-2">
                      <button
                        onClick={async () => {
                          if (!categories.length || !products.length) {
                            const data = await getFormData();
                            setCategories(data.categories || []);
                            setProducts(data.products || []);
                          }
                          setIsWoodWorkMode(!!convertingEstimate.isWoodWork);
                          setEditModalContext('convert');
                          const itemsWithIds = (convertingEstimate.items || []).map(item => {
                            const quantity = parseFloat(item.quantity) || 0;
                            const unitPrice = parseFloat(item.unit_price) || 0;
                            const gstRate = parseFloat(item.gst_rate) || 0;
                            const discount = parseFloat(item.discount) || 0;

                            const baseAmount = quantity * unitPrice;
                            const discountAmount = baseAmount * (discount / 100);
                            const taxableAmount = baseAmount - discountAmount;
                            const gstAmount = taxableAmount * (gstRate / 100);
                            const totalAmount = taxableAmount + gstAmount;

                            return {
                              ...item,
                              category_id: item.category_id || '',
                              product_id: item.product_id || '',
                              hsn_code: item.hsn_code || item.productId?.product?.hsnCode || '',
                              amount: taxableAmount,
                              gst_amount: gstAmount,
                              total_amount: totalAmount,
                              subtotal: totalAmount
                            };
                          });
                          setEditingProducts(recalcItemsAmounts(itemsWithIds));
                          setEditingApplyDiscount(itemsWithIds.some(item => (item.discount || 0) > 0));
                          setShowEditProductsModal(true);
                        }}
                        className="px-3 py-1 text-sm bg-blue-50 text-[#0A5F38] rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={async () => {
                          if (!categories.length || !products.length) {
                            const data = await getFormData();
                            setCategories(data.categories || []);
                            setProducts(data.products || []);
                          }
                          setIsWoodWorkMode(!!convertingEstimate.isWoodWork);
                          setEditModalContext('convert');
                          setShowEditProductsModal(true);
                          const itemsWithIds = (convertingEstimate.items || []).map(item => {
                            const quantity = parseFloat(item.quantity) || 0;
                            const unitPrice = parseFloat(item.unit_price) || 0;
                            const gstRate = parseFloat(item.gst_rate) || 0;
                            const discount = parseFloat(item.discount) || 0;

                            const baseAmount = quantity * unitPrice;
                            const discountAmount = baseAmount * (discount / 100);
                            const taxableAmount = baseAmount - discountAmount;
                            const gstAmount = taxableAmount * (gstRate / 100);
                            const totalAmount = taxableAmount + gstAmount;

                            return {
                              ...item,
                              category_id: item.category_id || '',
                              product_id: item.product_id || '',
                              hsn_code: item.hsn_code || item.productId?.product?.hsnCode || '',
                              amount: taxableAmount,
                              gst_amount: gstAmount,
                              total_amount: totalAmount,
                              subtotal: totalAmount
                            };
                          });
                          setEditingProducts(recalcItemsAmounts(itemsWithIds));
                          setEditingApplyDiscount(itemsWithIds.some(item => (item.discount || 0) > 0));
                        }}
                        className="px-3 py-1 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        Add Product
                      </button>
                    </div>
                  </div>
                  <div className={modalTable.wrap}>
                    <table className={modalTable.table}>
                      {convertingEstimate.isWoodWork ? (
                        /* ══ WOOD WORK CONVERSION SUMMARY TABLE ══ */
                        <>
                          <thead className={modalTable.thead}>
                            <tr>
                              <th className="px-3 py-2 text-center text-xs font-bold uppercase" style={{ width: '50px' }}>SI</th>
                              <th className="px-3 py-2 text-left text-xs font-bold uppercase">Type</th>
                              <th className="px-3 py-2 text-center text-xs font-bold uppercase" style={{ width: '100px' }}>Rate (₹)</th>
                              <th className="px-3 py-2 text-center text-xs font-bold uppercase" style={{ width: '60px' }}>Nos</th>
                              <th className="px-3 py-2 text-center text-xs font-bold uppercase" style={{ width: '70px' }}>L</th>
                              <th className="px-3 py-2 text-center text-xs font-bold uppercase" style={{ width: '70px' }}>B</th>
                              <th className="px-3 py-2 text-center text-xs font-bold uppercase" style={{ width: '80px' }}>Qty</th>
                              <th className="px-3 py-2 text-right text-xs font-bold uppercase" style={{ width: '120px' }}>Amount (₹)</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-slate-100">
                            {(() => {
                              const CAT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];
                              const rows = [];
                              const items = convertingEstimate.items || [];
                              const categoryOrder = [];
                              items.forEach(item => {
                                const cat = item.categoryLabel || '';
                                if (!categoryOrder.includes(cat)) categoryOrder.push(cat);
                              });

                              categoryOrder.forEach((catLabel, catIdx) => {
                                const catEntries = items.filter(item => (item.categoryLabel || '') === catLabel);

                                // Section Header Row
                                rows.push(
                                  <tr key={'conv-cat-' + catLabel} style={{ backgroundColor: '#BDD7EE' }}>
                                    <td className="px-3 py-1 text-center font-bold text-slate-900 border-b border-white">
                                      {CAT_LETTERS[catIdx] || (catIdx + 1)}
                                    </td>
                                    <td colSpan="7" className="px-3 py-1 font-bold text-slate-900 uppercase tracking-wider text-sm border-b border-white">
                                      {catLabel}
                                    </td>
                                  </tr>
                                );

                                // Item Rows
                                catEntries.forEach((item, siNo) => {
                                  const qty = computeWoodWorkQty(item.nos, item.length, item.breadth);
                                  const rate = item.unit_price || 0;
                                  const amount = (item.amount !== undefined) ? item.amount : (qty * rate);

                                  rows.push(
                                    <tr key={'conv-item-' + siNo} className="hover:bg-slate-50">
                                      <td className="px-3 py-2 text-center text-xs text-slate-500">{siNo + 1}</td>
                                      <td className="px-3 py-2 text-sm font-medium text-slate-900 break-words">{item.product_name}</td>
                                      <td className="px-3 py-2 text-center text-sm">{rate.toLocaleString('en-IN', { minimumFractionDigits: 1 })}</td>
                                      <td className="px-3 py-2 text-center text-sm">{item.nos || 0}</td>
                                      <td className="px-3 py-2 text-center text-sm">{item.length || 0}</td>
                                      <td className="px-3 py-2 text-center text-sm">{item.breadth || 0}</td>
                                      <td className="px-3 py-2 text-center text-sm font-semibold bg-slate-50">{qty}</td>
                                      <td className="px-3 py-2 text-right text-sm font-bold bg-slate-50">
                                        ₹{amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </td>
                                    </tr>
                                  );
                                });
                              });
                              return rows;
                            })()}
                          </tbody>
                          <tfoot className="bg-[#F8F9F9] border-t-2 border-[#0A5F38]">
                            <tr>
                              <td colSpan="7" className="px-4 py-3 text-right font-bold text-slate-800 text-lg">Grand Total:</td>
                              <td className="px-4 py-3 text-right font-black text-[#0A5F38] text-xl">
                                ₹{(convertingEstimate.totalAmount || convertingEstimate.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                          </tfoot>
                        </>
                      ) : (
                        /* ══ NORMAL RETAIL CONVERSION SUMMARY TABLE ══ */
                        <>
                          <thead className={modalTable.thead}>
                            <tr>
                              <th className={modalTable.th}>Category</th>
                              <th className={modalTable.th}>Product</th>
                              <th className={modalTable.thCenter}>Units</th>
                              <th className={modalTable.thCenter}>Quantity</th>
                              <th className={modalTable.thRight}>Unit Price</th>
                              {/* Only show Discount column if any item has a discount */}
                              {(convertingEstimate.items && convertingEstimate.items.some(item => (item.discount || 0) > 0)) && (
                                <th className={modalTable.thCenter}>Disc %</th>
                              )}
                              <th className={modalTable.thRight}>Amount</th>
                              <th className={modalTable.thCenter}>GST %</th>
                              <th className={modalTable.thRight}>GST Amount</th>
                              <th className={modalTable.thRight}>Total</th>
                            </tr>
                          </thead>
                          <tbody className={modalTable.tbody}>
                            {convertingEstimate.items && convertingEstimate.items.map((item, index) => {
                              const category = categories.find(cat => String(cat.id || cat._id) === String(item.category_id));
                              const categoryName = category?.name || 'N/A';
                              const baseAmount = item.quantity * item.unit_price;
                              const discountAmount = baseAmount * ((item.discount || 0) / 100);
                              const taxableAmount = baseAmount - discountAmount;
                              const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                              const totalAmount = taxableAmount + gstAmount;

                              return (
                                <tr key={index} className={modalTable.tr}>
                                  <td className={`${modalTable.td} whitespace-nowrap`}>
                                    <div className="text-slate-900">{categoryName}</div>
                                  </td>
                                  <td className={modalTable.td}>
                                    <div className="font-medium text-slate-900 whitespace-pre-wrap">{item.product_name}</div>
                                  </td>
                                  <td className={modalTable.tdCenter}>
                                    <div className="text-slate-900">{item.unit_type || 'SFT'}</div>
                                  </td>
                                  <td className={modalTable.tdCenter}>
                                    <div className="text-slate-900">{item.quantity}</div>
                                  </td>
                                  <td className={modalTable.tdRight}>
                                    <div className="text-slate-900">₹{parseFloat(item.unit_price).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                  </td>
                                  {(convertingEstimate.items && convertingEstimate.items.some(i => (i.discount || 0) > 0)) && (
                                    <td className={modalTable.tdCenter}>
                                      <div className="text-slate-900">{item.discount || 0}%</div>
                                    </td>
                                  )}
                                  <td className={modalTable.tdRight}>
                                    <div className="text-slate-900">₹{taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                  </td>
                                  <td className={modalTable.tdCenter}>
                                    <div className="font-medium text-slate-900">{item.gst_rate || 0}%</div>
                                  </td>
                                  <td className={modalTable.tdRight}>
                                    <div className="text-slate-900">₹{gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                  </td>
                                  <td className={modalTable.tdRight}>
                                    <div className="font-semibold text-slate-900">₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          {convertingEstimate.items && convertingEstimate.items.some(i => (i.discount || 0) > 0) ? (
                            <tfoot className={modalTable.tfoot}>
                              <tr>
                                <td colSpan="9" className="px-4 py-3 text-right font-semibold text-slate-700">Total:</td>
                                <td className="px-4 py-3 text-right font-bold text-slate-900">
                                  ₹{(convertingEstimate.items && convertingEstimate.items.length > 0
                                    ? convertingEstimate.items.reduce((sum, item) => {
                                      const baseAmount = item.quantity * item.unit_price;
                                      const discountAmount = baseAmount * ((item.discount || 0) / 100);
                                      const taxableAmount = baseAmount - discountAmount;
                                      const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                                      return sum + (taxableAmount + gstAmount);
                                    }, 0)
                                    : 0
                                  ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                              </tr>
                            </tfoot>
                          ) : (
                            <tfoot className={modalTable.tfoot}>
                              <tr>
                                <td colSpan="8" className="px-4 py-3 text-right font-semibold text-slate-700">Total:</td>
                                <td className="px-4 py-3 text-right font-bold text-slate-900">
                                  ₹{(convertingEstimate.items && convertingEstimate.items.length > 0
                                    ? convertingEstimate.items.reduce((sum, item) => {
                                      const baseAmount = item.quantity * item.unit_price;
                                      const discountAmount = baseAmount * ((item.discount || 0) / 100);
                                      const taxableAmount = baseAmount - discountAmount;
                                      const gstAmount = taxableAmount * ((item.gst_rate || 0) / 100);
                                      return sum + (taxableAmount + gstAmount);
                                    }, 0)
                                    : 0
                                  ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </>
                      )}
                    </table>
                  </div>

                  {/* GST Breakdown */}
                  <div className="mt-4 p-3 bg-white rounded-lg">
                    <GSTBreakdown
                      items={convertingEstimate?.items || []}
                      buyerGST={paymentData.gst_number}
                      buyerStateName={paymentData.state}
                      companySettings={companySettings}
                      showTitle={true}
                      className="text-sm"
                    />
                  </div>
                </div>

                {/* Invoice Details */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Billing Details</h3>
                  <div className="space-y-4">
                    {/* Payment Details */}
                    <div className="bg-[#FDFCEB] p-5 rounded-xl border border-[#0A5F38]/20 shadow-sm space-y-6">
                      <h3 className="text-lg font-bold text-[#0A5F38] flex items-center">
                        <Calculator className="w-5 h-5 mr-2" />
                        Payment Details
                      </h3>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Left Card: Payment Summary */}
                        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                          <h4 className="text-[#0A5F38] font-bold mb-4 flex items-center text-sm">
                            <DollarSign className="w-4 h-4 mr-2" /> Payment Summary
                          </h4>
                          <div className="space-y-3">
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-600">Total Amount:</span>
                              <span className="font-semibold text-slate-900">
                                ₹{calculateInvoiceTotal(convertingEstimate.items).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-[#16a34a] font-medium">Previously Paid:</span>
                              <span className="text-[#16a34a]">
                                - ₹{(convertingEstimate.amountReceived || convertingEstimate.amount_received || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            <div className="border-t border-slate-100 pt-3 flex justify-between text-sm">
                              <span className="font-bold text-slate-900">Remaining Balance:</span>
                              <span className="font-bold text-slate-900">
                                ₹{(() => {
                                  const total = calculateInvoiceTotal(convertingEstimate.items);
                                  const paid = convertingEstimate.amountReceived || convertingEstimate.amount_received || 0;
                                  return Math.max(0, total - paid).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                })()}
                              </span>
                            </div>
                            <div className="flex justify-between text-base pt-1">
                              <span className="font-bold text-slate-900">Final Balance:</span>
                              <span className="font-bold text-[#dc2626]">
                                ₹{(() => {
                                  const total = calculateInvoiceTotal(convertingEstimate.items);
                                  const previouslyPaid = convertingEstimate.amountReceived || convertingEstimate.amount_received || 0;
                                  const amountReceivedNow = parseFloat(paymentData.amount_received) || 0;
                                  const finalBalance = Math.max(0, total - previouslyPaid - amountReceivedNow);
                                  return finalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                                })()}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right Card: New Payment Entry */}
                        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                          <h4 className="text-slate-900 font-bold mb-4 text-sm">New Payment Entry</h4>
                          <div className="space-y-4">

                            <div>
                              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Payment Mode</label>
                              <select
                                value={paymentData.payment_type}
                                onChange={(e) => setPaymentData({ ...paymentData, payment_type: e.target.value })}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm"
                              >
                                <option value="cash">Cash</option>
                                <option value="upi">UPI</option>
                                <option value="phonepe">PhonePe</option>
                                <option value="google_pay">Google Pay</option>
                                <option value="paytm">Paytm</option>
                                <option value="net_banking">Net Banking</option>
                                <option value="card">Card</option>
                                <option value="cheque">Cheque</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Amount</label>
                              <div className="relative">
                                <span className="absolute left-3 top-2 text-slate-400 text-sm">₹</span>
                                <input
                                  type="number"
                                  value={paymentData.amount_received}
                                  onChange={(e) => {
                                    const amount = parseFloat(e.target.value) || 0;
                                    const totalAmount = calculateInvoiceTotal(convertingEstimate.items);
                                    const previouslyPaid = convertingEstimate.amountReceived || convertingEstimate.amount_received || 0;
                                    const limit = totalAmount - previouslyPaid;

                                    // Auto-calculate payment status
                                    let status = 'pending';
                                    const cumulative = previouslyPaid + amount;

                                    if (cumulative <= 0) {
                                      status = 'pending';
                                    } else if (Math.abs(cumulative - totalAmount) < 0.01 || cumulative > totalAmount) {
                                      status = 'completed';
                                    } else {
                                      status = 'partial';
                                    }

                                    // Validation: Cannot exceed remaining balance
                                    if (amount > limit) {
                                      toast.error(`Amount cannot exceed the remaining balance (₹${limit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`);
                                      return;
                                    }

                                    setPaymentData({
                                      ...paymentData,
                                      amount_received: e.target.value,
                                      payment_status: status
                                    });
                                  }}
                                  onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                  onWheel={(e) => e.target.blur()}
                                  className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  placeholder="Enter payment amount"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                            </div>

                            <div className="pt-1">
                              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Payment Status</label>
                              <span className={`inline-flex px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${paymentData.payment_status === 'completed' ? 'bg-green-100 text-green-700' :
                                paymentData.payment_status === 'partial' ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                {paymentData.payment_status === 'completed' ? 'Paid' :
                                  paymentData.payment_status === 'partial' ? 'Partial Payment' :
                                    'Pending'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Transaction Date & Delivery Information */}
                    <div className="bg-white p-4 rounded-lg mt-4 border border-slate-200">
                      <h3 className="text-lg font-semibold text-slate-900 mb-3">Delivery Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Dispatched from</label>
                          <input
                            type="text"
                            value={paymentData.dispatched_from}
                            onChange={(e) => setPaymentData({ ...paymentData, dispatched_from: e.target.value })}
                            placeholder="Enter dispatch location"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Delivered to</label>
                          <input
                            type="text"
                            value={paymentData.delivered_to}
                            onChange={(e) => setPaymentData({ ...paymentData, delivered_to: e.target.value })}
                            placeholder="Enter delivery location"
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Terms and Conditions & Material Description */}
                <div className="bg-white p-4 rounded-lg mt-4 mb-6 space-y-6">
                  <div className="flex items-center justify-between pb-2">
                    <div>
                      <h4 className="text-sm font-bold text-[#0A5F38]">Include Terms & Conditions</h4>
                      <p className="text-xs text-slate-500">Enable if you wish to add terms and conditions to this invoice</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const nextState = !includeTermsEstimateModal;
                        setIncludeTermsEstimateModal(nextState);
                        if (nextState) {
                          setPaymentData(prev => ({ ...prev, termsAndConditions: convertingEstimate?.termsAndConditions || '' }));
                        } else {
                          setPaymentData(prev => ({ ...prev, termsAndConditions: '' }));
                        }
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                        includeTermsEstimateModal ? 'bg-[#0A5F38]' : 'bg-slate-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          includeTermsEstimateModal ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {includeTermsEstimateModal && (
                    <TermsAndConditionsInput
                      value={paymentData.termsAndConditions}
                      onChange={(value) => setPaymentData({ ...paymentData, termsAndConditions: value })}
                      label="Terms and Conditions"
                    />
                  )}

                  {convertingEstimate?.isWoodWork && (
                    <TermsAndConditionsInput
                      value={paymentData.materialDescription !== undefined ? paymentData.materialDescription : (convertingEstimate?.materialDescription || '')}
                      onChange={(value) => setPaymentData({ ...paymentData, materialDescription: value })}
                      label="Material Description"
                      placeholder="Enter material specifications..."
                    />
                  )}
                </div>

            </AppModal>
          )
        }

        {/* Edit Products Modal - Standardized WoodWork Implementation */}
        {
          showEditProductsModal && (
            <AppModal
              open
              onClose={() => {
                      setShowEditProductsModal(false);
                      // Return to the appropriate modal based on context
                      if (editModalContext === 'convert') {
                        setShowPaymentModal(true);
                      } else if (editModalContext === 'convert_closed_order') {
                        setShowClosedOrderModal(true);
                      } else if (editModalContext === 'edit') {
                        setShowEditEstimateModal(true);
                      }
                    }}
              size="full"
              layer="stacked"
              title="Edit Products"
              footer={
                <>
                  {!isWoodWorkMode && editingProducts.length > 0 && (
                    <div className="flex-1 min-w-[280px] flex flex-wrap items-center justify-between gap-3 bg-[#0A5F38] rounded-xl px-4 py-2">
                      <div className="flex gap-6">
                         <div className="flex flex-col">
                            <span className="text-[10px] font-bold text-white/70 uppercase">Total Items</span>
                            <span className="text-lg font-black text-white">{editingProducts.length}</span>
                         </div>
                         <div className="flex flex-col border-l border-white/20 pl-6">
                            <span className="text-[10px] font-bold text-white/70 uppercase">Total Taxable</span>
                            <span className="text-lg font-black text-white">₹{editingProducts.reduce((sum, item) => sum + (item.amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
                         </div>
                      </div>
                      <div className="bg-white/10 px-4 py-1.5 rounded-xl border border-white/20">
                        <span className="text-xs font-bold text-white/80 mr-3 uppercase tracking-wider">Estimated Total:</span>
                        <span className="text-xl font-black text-white italic">
                          ₹{editingProducts.reduce((sum, item) => sum + (item.total_amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                        </span>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setShowEditProductsModal(false);
                      if (editModalContext === 'convert') {
                        setShowPaymentModal(true);
                      } else if (editModalContext === 'convert_closed_order') {
                        setShowClosedOrderModal(true);
                      } else if (editModalContext === 'edit') {
                        setShowEditEstimateModal(true);
                      }
                    }}
                    className={modalButton.secondary}
                  >
                    DISCARD
                  </button>
                  <button
                    onClick={handleSaveProductEdits}
                    className={modalButton.primary}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                    SAVE PRODUCT EDITS
                  </button>
                </>
              }
            >

                {/* Apply Discount Toggle - Hide for Wood Work */}
                {!isWoodWorkMode && (
                  <div className="mb-4 flex items-center justify-end">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-slate-700">Apply Discount</span>
                      <button
                        onClick={() => {
                          const newDiscountState = !editingApplyDiscount;
                          setEditingApplyDiscount(newDiscountState);
                          // If turning off, reset all discounts to 0
                          if (!newDiscountState) {
                            const updatedProducts = editingProducts.map(item => ({
                              ...item,
                              discount: 0
                            }));
                            setEditingProducts(updatedProducts);
                          }
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${editingApplyDiscount ? 'bg-[#C5A57D]' : 'bg-slate-200'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editingApplyDiscount ? 'translate-x-6' : 'translate-x-1'}`}
                        />
                      </button>
                    </div>
                  </div>
                )}

                <div className="mb-6">
                  {isWoodWorkMode ? (
                    /* ══ WOOD WORK PREMIUM CONTINUOUS TABLE EDIT LAYOUT ══ */
                    <div className="bg-white rounded-xl border border-[#E6DFC6] overflow-x-auto mb-6">
                      <table className="w-full min-w-[900px] text-sm border-collapse">
                        <thead>
                          <tr className="bg-[#0A5F38] text-white">
                            <th className="p-3 text-xs font-bold uppercase tracking-wider text-center border-r border-white/20" style={{ width: '50px' }}>SI</th>
                            <th className="p-3 text-xs font-bold uppercase tracking-wider text-left border-r border-white/20">Type</th>
                            <th className="p-3 text-xs font-bold uppercase tracking-wider text-center border-r border-white/20" style={{ width: '100px' }}>Rate (₹)</th>
                            <th className="p-3 text-xs font-bold uppercase tracking-wider text-center border-r border-white/20" style={{ width: '60px' }}>Nos</th>
                            <th className="p-3 text-xs font-bold uppercase tracking-wider text-center border-r border-white/20" style={{ width: '70px' }}>L</th>
                            <th className="p-3 text-xs font-bold uppercase tracking-wider text-center border-r border-white/20" style={{ width: '70px' }}>B</th>
                            <th className="p-3 text-xs font-bold uppercase tracking-wider text-center border-r border-white/20" style={{ width: '80px' }}>Qty</th>
                            <th className="p-3 text-xs font-bold uppercase tracking-wider text-center border-r border-white/20" style={{ width: '80px' }}>GST %</th>
                            <th className="p-3 text-xs font-bold uppercase tracking-wider text-right border-r border-white/20" style={{ width: '120px' }}>Amount (₹)</th>
                            <th className="p-3 text-xs font-bold uppercase tracking-wider text-center" style={{ width: '50px' }}>Act</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const CAT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];
                            const categoryOrder = [];
                            editingProducts.forEach(item => {
                              const cat = item.categoryLabel || 'Section';
                              if (!categoryOrder.includes(cat)) categoryOrder.push(cat);
                            });

                            const rows = [];
                            categoryOrder.forEach((catLabel, catIdx) => {
                              const catEntries = editingProducts
                                .map((item, idx) => ({ item, idx }))
                                .filter(({ item }) => (item.categoryLabel || 'Section') === catLabel);

                              // Category Header Row
                              rows.push(
                                <tr key={'edit-cat-header-' + catLabel} className="bg-[#BDD7EE] border-b border-black">
                                  <td className="p-3 text-center font-bold border-r border-black">{CAT_LETTERS[catIdx] || (catIdx + 1)}</td>
                                  <td colSpan="8" className="p-3 font-bold uppercase tracking-widest text-[#0A5F38] border-r border-black">
                                    <div className="flex justify-between items-center">
                                      <span>{catLabel}</span>
                                      <div className="flex gap-2">
                                         <button 
                                           onClick={() => {
                                             const newLabel = prompt("Enter new section name:", catLabel);
                                             if (newLabel) {
                                               const updated = editingProducts.map(item => 
                                                 (item.categoryLabel || 'Section') === catLabel ? { ...item, categoryLabel: newLabel } : item
                                               );
                                               setEditingProducts(updated);
                                             }
                                           }}
                                           className="text-[10px] bg-white/50 px-2 py-1 rounded hover:bg-white transition-colors"
                                         >
                                           Rename
                                         </button>
                                         <button 
                                           onClick={() => {
                                             if (window.confirm(`Delete entire section "${catLabel}"?`)) {
                                               const updated = editingProducts.filter(item => (item.categoryLabel || 'Section') !== catLabel);
                                               setEditingProducts(updated);
                                             }
                                           }}
                                           className="text-[10px] bg-red-100 text-red-600 px-2 py-1 rounded hover:bg-red-200 transition-colors"
                                         >
                                           Delete Section
                                         </button>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="p-3 border-b border-black"></td>
                                </tr>
                              );

                              // Item Rows
                              catEntries.forEach(({ item, idx }, siNo) => {
                                rows.push(
                                  <tr key={'edit-row-' + idx} className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                                    <td className="p-2 text-center text-xs font-medium text-slate-400 border-r border-slate-100">{siNo + 1}</td>
                                    <td className="p-2 border-r border-slate-100">
                                      <input
                                        type="text"
                                        value={item.product_name}
                                        onChange={(e) => handleProductEdit(idx, 'product_name', e.target.value)}
                                        className="w-full bg-transparent border-0 focus:ring-0 text-sm font-medium py-1 px-0"
                                        placeholder="Enter item description..."
                                      />
                                    </td>
                                    <td className="p-2 border-r border-slate-100">
                                      <input
                                        type="number"
                                        value={item.unit_price}
                                        onChange={(e) => handleProductEdit(idx, 'unit_price', e.target.value)}
                                        className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm font-semibold focus:ring-1 focus:ring-[#C5A57D]"
                                        onWheel={(e) => e.target.blur()}
                                      />
                                    </td>
                                    <td className="p-2 border-r border-slate-100">
                                      <input
                                        type="number"
                                        value={item.nos}
                                        onChange={(e) => handleProductEdit(idx, 'nos', e.target.value)}
                                        className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm focus:ring-1 focus:ring-[#C5A57D]"
                                        onWheel={(e) => e.target.blur()}
                                      />
                                    </td>
                                    <td className="p-2 border-r border-slate-100">
                                      <input
                                        type="number"
                                        value={item.length}
                                        onChange={(e) => handleProductEdit(idx, 'length', e.target.value)}
                                        className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm focus:ring-1 focus:ring-[#C5A57D]"
                                        onWheel={(e) => e.target.blur()}
                                      />
                                    </td>
                                    <td className="p-2 border-r border-slate-100">
                                      <input
                                        type="number"
                                        value={item.breadth}
                                        onChange={(e) => handleProductEdit(idx, 'breadth', e.target.value)}
                                        className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm focus:ring-1 focus:ring-[#C5A57D]"
                                        onWheel={(e) => e.target.blur()}
                                      />
                                    </td>
                                    <td className="p-2 text-center text-sm font-bold text-slate-700 bg-slate-50 border-r border-slate-100">
                                      {item.quantity}
                                    </td>
                                    <td className="p-2 border-r border-slate-100">
                                      <input
                                        type="text"
                                        value={item.gst_rate === 0 ? '' : item.gst_rate}
                                        onChange={(e) => handleProductEdit(idx, 'gst_rate', e.target.value)}
                                        className="w-full text-center bg-slate-50 border border-slate-200 rounded py-1 px-1 text-sm focus:ring-1 focus:ring-[#C5A57D]"
                                        placeholder="0"
                                      />
                                    </td>
                                    <td className="p-2 text-right text-sm font-black text-[#0A5F38] bg-slate-50 pr-4 border-r border-slate-100">
                                      ₹{item.total_amount?.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                    </td>
                                    <td className="p-2 text-center">
                                      <button
                                        onClick={() => handleRemoveProduct(idx)}
                                        className="text-slate-300 hover:text-red-500 transition-colors p-1"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              });

                              // Add Row for this Category
                              rows.push(
                                <tr key={'add-row-' + catLabel} className="bg-white border-b border-black">
                                   <td colSpan="10" className="p-2">
                                      <button
                                        onClick={() => {
                                          const newItem = {
                                            categoryLabel: catLabel,
                                            product_name: '',
                                            unit_price: 1000,
                                            nos: 1,
                                            length: 1,
                                            breadth: 1,
                                            quantity: 1,
                                            gst_rate: 0,
                                            amount: 1000,
                                            total_amount: 1000,
                                            unit_type: 'SFT'
                                          };
                                          setEditingProducts([...editingProducts, newItem]);
                                        }}
                                        className="flex items-center gap-2 text-[10px] font-bold text-[#0A5F38] hover:bg-[#0A5F38] hover:text-white transition-all px-4 py-1 rounded border border-[#0A5F38]"
                                      >
                                        <Plus className="w-3 h-3" />
                                        ADD ITEM TO {catLabel}
                                      </button>
                                   </td>
                                </tr>
                              );
                            });

                            return rows;
                          })()}
                        </tbody>
                        <tfoot className="bg-slate-50">
                          <tr className="border-t-2 border-black">
                             <td colSpan="8" className="p-4 text-right font-bold text-slate-700 uppercase">Grand Total:</td>
                             <td className="p-4 text-right font-black text-[#0A5F38] text-xl italic">
                               ₹{editingProducts.reduce((sum, item) => sum + (item.total_amount || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                             </td>
                             <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    /* ══ NORMAL RETAIL TABLE EDIT LAYOUT ══ */
                    editingProducts.length > 0 ? (
                      <div className={modalTable.wrap}>
                        <table className="w-full min-w-[1000px] table-fixed text-sm border-collapse">
                          <thead className={modalTable.thead}>
                            <tr>
                              <th className={`${modalTable.th} w-[180px]`}>Category</th>
                              <th className={`${modalTable.th} min-w-[240px]`}>Product</th>
                              <th className={`${modalTable.thCenter} w-[90px]`}>Units</th>
                              <th className={`${modalTable.thCenter} w-[90px]`}>Qty</th>
                              <th className={`${modalTable.thRight} w-[110px]`}>Price</th>
                              {editingApplyDiscount && (
                                <th className={`${modalTable.thCenter} w-[80px]`}>Disc %</th>
                              )}
                              <th className={`${modalTable.thCenter} w-[80px]`}>GST %</th>
                              <th className={`${modalTable.thRight} w-[150px]`}>Total (Inc. GST)</th>
                              <th className={`${modalTable.thCenter} w-[48px]`}><span className="sr-only">Actions</span></th>
                            </tr>
                          </thead>
                          <tbody className={modalTable.tbody}>
                              {editingProducts.map((item, index) => (
                                <tr key={index} className={modalTable.tr}>
                                  {/* Category */}
                                  <td className={modalTable.td}>
                                    <select
                                      value={item.category_id || ''}
                                      onChange={(e) => handleProductEdit(index, 'category_id', e.target.value)}
                                      className="w-full px-2 py-1.5 border border-slate-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-transparent text-xs"
                                    >
                                      <option value="">Select Category</option>
                                      {categories.map(cat => (
                                        <option key={cat.id || cat._id} value={cat.id || cat._id}>
                                          {cat.name}
                                        </option>
                                      ))}
                                    </select>
                                  </td>

                                  {/* Product */}
                                  <td className={modalTable.td}>
                                    {item.isOthers ? (
                                      (() => {
                                        const productsInCategory = products.filter(prod => {
                                          const selectedCat = String(item.category_id);
                                          const productCat = String(prod.categoryId || prod.category_id || '');
                                          return productCat === selectedCat;
                                        });

                                        if (productsInCategory.length === 0 || item.showFreeText) {
                                          return (
                                            <div className="flex items-center gap-1">
                                              <input
                                                type="text"
                                                value={item.product_name || ''}
                                                onChange={(e) => {
                                                  const updatedProducts = [...editingProducts];
                                                  updatedProducts[index].product_name = e.target.value;
                                                  setEditingProducts(updatedProducts);
                                                }}
                                                className="flex-1 px-2 py-1.5 border border-slate-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-transparent text-xs font-medium"
                                                placeholder="Enter product name"
                                                required
                                              />
                                              {productsInCategory.length > 0 && (
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const updatedProducts = [...editingProducts];
                                                    updatedProducts[index].showFreeText = false;
                                                    updatedProducts[index].product_id = '';
                                                    updatedProducts[index].product_name = '';
                                                    setEditingProducts(updatedProducts);
                                                  }}
                                                  className="p-1 px-2 text-slate-400 hover:text-[#0A5F38] transition-colors text-[10px] font-bold"
                                                >
                                                  LIST
                                                </button>
                                              )}
                                            </div>
                                          );
                                        } else {
                                          return (
                                            <select
                                              value={item.product_id || ''}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                const updatedProducts = [...editingProducts];
                                                const currentItem = { ...updatedProducts[index] }; 

                                                if (val === 'ADD_NEW_OTHER') {
                                                  currentItem.showFreeText = true;
                                                  currentItem.product_id = '';
                                                  currentItem.product_name = '';
                                                } else {
                                                  const product = products.find(p => String(p.id) === String(val));
                                                  if (!product) return;

                                                  const productName = product?.name || '';
                                                  const unitPrice = product?.sellingPrice != null ? product.sellingPrice : 0;
                                                  const gstRate = (product?.product?.gstPercentage !== undefined && product?.product?.gstPercentage !== null) ? product.product.gstPercentage :
                                                                  (product?.gstPercentage !== undefined && product?.gstPercentage !== null) ? product.gstPercentage : 18;
                                                  const hsnCode = product?.product?.hsnCode || product?.hsnCode || '';
                                                  const unitType = product?.product?.dimensions?.unit || product?.unit_type || 'PCS';

                                                  currentItem.product_id = val;
                                                  currentItem.product_name = productName;
                                                  currentItem.unit_price = unitPrice;
                                                  currentItem.gst_rate = gstRate;
                                                  currentItem.hsn_code = hsnCode;
                                                  currentItem.unit_type = unitType;

                                                  // WoodWork default dimensions
                                                  if (isWoodWorkMode) {
                                                    currentItem.nos = currentItem.nos || 1;
                                                    currentItem.length = currentItem.length || 1;
                                                    currentItem.breadth = currentItem.breadth || 1;
                                                    currentItem.quantity = computeWoodWorkQty(currentItem.nos, currentItem.length, currentItem.breadth);
                                                  }

                                                  // Recalculation logic
                                                  const quantity = parseFloat(currentItem.quantity) || 1;
                                                  const discount = parseFloat(currentItem.discount) || 0;
                                                  const baseAmount = quantity * unitPrice;
                                                  const discountAmount = (baseAmount * discount) / 100;
                                                  const taxableAmount = baseAmount - discountAmount;
                                                  const gstAmount = (taxableAmount * gstRate) / 100;
                                                  const totalAmount = taxableAmount + gstAmount;

                                                  currentItem.amount = taxableAmount;
                                                  currentItem.gst_amount = gstAmount;
                                                  currentItem.total_amount = totalAmount;
                                                  currentItem.subtotal = totalAmount;
                                                }
                                                updatedProducts[index] = currentItem;
                                                setEditingProducts(updatedProducts);
                                              }}
                                              className="w-full px-2 py-1.5 border border-slate-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-transparent text-xs font-medium"
                                            >
                                              <option value="">Select Product/Service</option>
                                              <option value="ADD_NEW_OTHER" className="font-bold text-[#0A5F38]">+ Add Free Text</option>
                                              {productsInCategory.map(prod => (
                                                <option key={prod.id} value={prod.id}>
                                                  {prod.name}
                                                </option>
                                              ))}
                                            </select>
                                          );
                                        }
                                      })()
                                    ) : item.product_id ? (
                                       <div className="flex items-center gap-1">
                                         <input
                                           type="text"
                                           value={item.product_name || ''}
                                           onChange={(e) => handleProductEdit(index, 'product_name', e.target.value)}
                                           className="flex-1 px-2 py-1.5 border border-slate-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-transparent text-xs font-medium"
                                           placeholder="Enter product name"
                                         />
                                         <button
                                           type="button"
                                           onClick={() => {
                                             handleProductEdit(index, 'product_id', '');
                                             handleProductEdit(index, 'product_name', '');
                                           }}
                                           className="p-1 text-slate-400 hover:text-red-500"
                                           title="Change product"
                                         >
                                           <X className="h-4 w-4" />
                                         </button>
                                       </div>
                                     ) : (
                                      <select
                                        value={item.product_id || ''}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          const product = products.find(p => String(p.id) === String(val));
                                          const updatedProducts = [...editingProducts];
                                          
                                          updatedProducts[index].product_id = val;
                                          updatedProducts[index].product_name = product?.name || '';
                                          updatedProducts[index].unit_price = product?.sellingPrice || 0;
                                          updatedProducts[index].gst_rate = product?.product?.gstPercentage || product?.gstPercentage || 18;
                                          updatedProducts[index].hsn_code = product?.product?.hsnCode || product?.hsnCode || '';
                                          updatedProducts[index].unit_type = product?.product?.dimensions?.unit || product?.unit_type || 'SFT';

                                          if (isWoodWorkMode) {
                                            updatedProducts[index].nos = 1;
                                            updatedProducts[index].length = 1;
                                            updatedProducts[index].breadth = 1;
                                            updatedProducts[index].quantity = 1;
                                          }

                                          // Trigger amounts update
                                          handleProductEdit(index, 'product_id', val);
                                        }}
                                        className="w-full px-2 py-1.5 border border-slate-300 rounded-lg focus:ring-1 focus:ring-indigo-500 focus:border-transparent text-xs"
                                        disabled={!item.category_id}
                                      >
                                        <option value="">{!item.category_id ? 'Select Category' : 'Select Product'}</option>
                                        {item.category_id && products
                                          .filter(prod => {
                                            const selectedCat = String(item.category_id);
                                            const productCat = String(prod.categoryId || prod.category_id || '');
                                            return productCat === selectedCat;
                                          })
                                          .map(prod => (
                                            <option key={prod.id} value={prod.id}>
                                              {prod.name}
                                            </option>
                                          ))}
                                      </select>
                                    )}
                                  </td>

                                  {/* Units */}
                                  <td className={modalTable.td}>
                                    <select
                                      value={item.unit_type || 'SFT'}
                                      onChange={(e) => handleProductEdit(index, 'unit_type', e.target.value)}
                                      className="w-full px-1 py-1.5 border border-slate-300 rounded-lg text-xs"
                                    >
                                      {['SFT', 'RFT', 'PCS', 'KG', 'LTR', 'MTR', 'LUMPSUM', 'NOS', 'SQ.M'].map(u => (
                                        <option key={u} value={u}>{u}</option>
                                      ))}
                                    </select>
                                  </td>

                                  {/* Quantity */}
                                  <td className={modalTable.td}>
                                    <input
                                      type="number"
                                      value={item.quantity === '' ? '' : item.quantity}
                                      onChange={(e) => handleProductEdit(index, 'quantity', e.target.value)}
                                      className={`w-full px-2 py-1.5 border rounded-lg text-center text-xs font-bold border-slate-300 text-slate-900 focus:ring-1 focus:ring-indigo-500`}
                                    />
                                  </td>

                                  {/* Price */}
                                  <td className={modalTable.td}>
                                    <div className="relative">
                                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">₹</span>
                                      <input
                                        type="number"
                                        value={item.unit_price === '' ? '' : item.unit_price}
                                        onChange={(e) => handleProductEdit(index, 'unit_price', e.target.value)}
                                        className="w-full pl-5 pr-2 py-1.5 border border-slate-300 rounded-lg text-right text-xs font-semibold"
                                        placeholder="0.00"
                                      />
                                    </div>
                                  </td>

                                  {/* Discount */}
                                  {editingApplyDiscount && (
                                    <td className={modalTable.td}>
                                      <input
                                        type="number"
                                        value={item.discount === '' ? '' : item.discount}
                                        onChange={(e) => handleProductEdit(index, 'discount', e.target.value)}
                                        className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-center text-xs"
                                        placeholder="%"
                                      />
                                    </td>
                                  )}

                                  {/* GST % */}
                                  <td className={modalTable.td}>
                                    <select
                                      value={item.gst_rate || 0}
                                      onChange={(e) => handleProductEdit(index, 'gst_rate', e.target.value)}
                                      className="w-full px-1 py-1.5 border border-slate-300 rounded-lg text-xs"
                                    >
                                      <option value="0">0%</option>
                                      <option value="5">5%</option>
                                      <option value="12">12%</option>
                                      <option value="18">18%</option>
                                      <option value="28">28%</option>
                                    </select>
                                  </td>

                                  {/* Total Row (Calculated) */}
                                  <td className={modalTable.tdRight}>
                                    <div className="text-[13px] font-black text-slate-900 leading-none">
                                      ₹{(item.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                    </div>
                                    <div className="text-[9px] text-slate-400 font-bold mt-1">
                                      GST: ₹{(item.gst_amount || 0).toFixed(1)}
                                    </div>
                                  </td>

                                  {/* Actions */}
                                  <td className={modalTable.tdCenter}>
                                    <button
                                      onClick={() => handleRemoveProduct(index)}
                                      className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-white rounded-2xl border-2 border-dashed border-slate-200">
                        <div className="bg-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Plus className="h-8 w-8 text-slate-300" />
                        </div>
                        <p className="text-slate-500 font-medium">No products added to this estimate yet.</p>
                        <button onClick={handleAddProduct} className="mt-4 text-[#0A5F38] font-bold hover:underline">Add your first product</button>
                      </div>
                    )
                  )}

                  {!isWoodWorkMode && (
                    <button
                      onClick={handleAddProduct}
                      className="w-full px-4 py-4 mt-4 border-2 border-dashed border-[#0A5F38]/20 bg-[#0A5F38]/5 rounded-xl text-[#0A5F38] hover:border-[#0A5F38] hover:bg-[#0A5F38]/10 hover:shadow-md transition-all font-bold flex items-center justify-center gap-2 group"
                    >
                      <Plus className="h-5 w-5 group-hover:scale-110 transition-transform" />
                      ADD PROJECT ITEM
                    </button>
                  )}
                </div>

            </AppModal>
          )
        }

        {/* Closed Order Modal */}
        {/* Closed Order Modal */}
        {
          showClosedOrderModal && convertingEstimate && (
            <AppModal
              open
              onClose={() => setShowClosedOrderModal(false)}
              size="xl"
              closeOnEsc={!showEditProductsModal}
              title="Convert to Closed Order"
              footer={
                <>
                  <button
                    onClick={() => setShowClosedOrderModal(false)}
                    className={modalButton.secondary}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleClosedOrderSubmit}
                    disabled={loading}
                    className={modalButton.primary}
                  >
                    {loading ? 'Converting...' : 'Create Closed Order'}
                  </button>
                </>
              }
            >

                {/* Estimate Summary */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">Estimate Summary</h3>
                  <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Estimate Number</div>
                      <div className="font-semibold">
                        {convertingEstimate.estimate_number || convertingEstimate._id?.slice(-8) || 'EST' + convertingEstimate.id}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Estimate Date</div>
                      <div className="font-semibold">
                        {convertingEstimate.date ? new Date(convertingEstimate.date).toLocaleDateString('en-IN') : convertingEstimate.createdAt ? new Date(convertingEstimate.createdAt).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Total Amount</div>
                      <div className="font-semibold">
                        ₹{(convertingEstimate.totalAmount || convertingEstimate.total_amount || 0).toFixed(1)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600 mb-1">Created By</div>
                      <div className="font-semibold">
                        {convertingEstimate.salesperson?.name || convertingEstimate.salesperson_name || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1">Closed Order Date</label>
                      <input
                        type="date"
                        value={closedOrderData.date || ''}
                        onChange={(e) => setClosedOrderData({ ...closedOrderData, date: e.target.value })}
                        className="w-full px-2 py-1 border border-slate-300 rounded focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm font-semibold"
                      />
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-slate-900">Customer Information</h3>
                    <button
                      onClick={() => {
                        if (!editMode) {
                          setEditingCustomer({
                            name: convertingEstimate.customer_name || '',
                            phone: convertingEstimate.customer_phone || '',
                            email: convertingEstimate.customer_email || '',
                            address: convertingEstimate.customer_address || ''
                          });
                        }
                        setEditMode(!editMode);
                      }}
                      className="text-green-600 hover:text-green-800 text-sm font-medium"
                    >
                      {editMode ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  {editMode ? (
                    <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                        <input
                          type="text"
                          value={editingCustomer.name || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={editingCustomer.countryCode || ''}
                            onChange={(e) => setEditingCustomer({ ...editingCustomer, countryCode: e.target.value })}
                            placeholder="+91"
                            maxLength={5}
                            className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                          />
                          <input
                            type="tel"
                            value={editingCustomer.phone || ''}
                            onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value.replace(/\D/g, '') })}
                            placeholder={editingCustomer.countryCode ? 'Mobile number' : '10-digit mobile number'}
                            maxLength={editingCustomer.countryCode ? 11 : 10}
                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                        <input
                          type="email"
                          value={editingCustomer.email || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                        <textarea
                          value={editingCustomer.address || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                          rows="2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                        <select
                          value={closedOrderData.state || ''}
                          onChange={(e) => setClosedOrderData({ ...closedOrderData, state: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                        >
                          <option value="">Select State</option>
                          {stateOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">GST Number</label>
                        <input
                          type="text"
                          value={closedOrderData.gstNumber || ''}
                          onChange={(e) => {
                            const newGst = e.target.value;
                            const newState = getStateNameFromGST(newGst);
                            setClosedOrderData(prev => ({
                              ...prev,
                              gstNumber: newGst,
                              state: newState || prev.state
                            }));
                          }}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                          placeholder="Enter GST Number"
                          maxLength="15"
                        />
                        {closedOrderData.gstNumber && closedOrderData.gstNumber.length > 0 && closedOrderData.gstNumber.length < 15 && (
                          <div className="text-orange-500 text-sm mt-1">
                            GST number should be 15 characters long if provided
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Name</div>
                        <div className="font-semibold">{editingCustomer.name || convertingEstimate.customer_name || 'Walk-in Customer'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Phone</div>
                        <div className="font-semibold">{editingCustomer.phone || convertingEstimate.customer_phone || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Email</div>
                        <div className="font-semibold">{editingCustomer.email || convertingEstimate.customer_email || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Address</div>
                        <div className="font-semibold">{editingCustomer.address || convertingEstimate.customer_address || 'N/A'}</div>
                      </div>

                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">State</div>
                        <div className="font-semibold">{closedOrderData.state || convertingEstimate.party_state_name || convertingEstimate.customer_state || 'N/A'}</div>
                      </div>

                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">GST Number</div>
                        <div className="font-semibold">{closedOrderData.gstNumber || 'N/A'}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-slate-900">Product Details</h3>
                    <div className="space-x-2">
                      <button
                        onClick={async () => {
                          if (!categories.length || !products.length) {
                            const data = await getFormData();
                            setCategories(data.categories || []);
                            setProducts(data.products || []);
                          }
                          setIsWoodWorkMode(!!convertingEstimate.isWoodWork);
                          setEditModalContext('convert_closed_order');
                          const itemsWithIds = (convertingEstimate.items || []).map((item, idx) => {
                            // Robust category ID extraction
                            let catId = item.category_id ||
                              item.categoryId ||
                              item.category_name ||
                              item.categoryName ||
                              (typeof item.category === 'object' ? item.category?.id || item.category?._id || item.category?.name || item.category?.categoryName : item.category) ||
                              '';

                            // Robust product ID extraction
                            let prodId = item.product_id ||
                              item.productId ||
                              (typeof item.product === 'object' ? item.product?.id || item.product?._id : item.product) ||
                              '';

                            // If catId is a name or missing, try to find the actual ID
                            if (!catId || !/^[0-9a-fA-F]{24}$/.test(String(catId))) {
                              const foundCat = categories.find(c =>
                                (c.name || '').toLowerCase() === String(catId || '').toLowerCase() ||
                                (c.categoryName || '').toLowerCase() === String(catId || '').toLowerCase() ||
                                (c.id || c._id) === String(catId)
                              );
                              if (foundCat) {
                                catId = foundCat.id || foundCat._id;
                              } else if (item.product_name && !prodId) {
                                // Infer Others if name exists but no ID
                                const othersCat = categories.find(c =>
                                  c.isOthers ||
                                  (c.name || '').toLowerCase() === 'others' ||
                                  (c.name || '').toLowerCase() === 'other'
                                );
                                if (othersCat) {
                                  catId = othersCat.id || othersCat._id;
                                }
                              }
                            }

                            const selectedCategory = categories.find(c => String(c.id || c._id) === String(catId));
                            const isOthers = !!(
                              selectedCategory?.isOthers ||
                              (selectedCategory?.name || '').toLowerCase() === 'others' ||
                              (selectedCategory?.name || '').toLowerCase() === 'other' ||
                              item.isOthers ||
                              (item.product_name && !prodId)
                            );

                            return {
                              ...item,
                              category_id: catId,
                              product_id: prodId,
                              isOthers: isOthers,
                              showFreeText: isOthers && !prodId,
                              hsn_code: item.hsn_code || (item.productId?.product?.hsnCode) || ''
                            };
                          });
                          setEditingProducts(recalcItemsAmounts(itemsWithIds));
                          setEditingApplyDiscount(itemsWithIds.some(item => (item.discount || 0) > 0));
                          setShowEditProductsModal(true);
                          // Don't close the Closed Order modal - keep it open in background
                        }}
                        className="px-3 py-1 text-sm bg-blue-50 text-[#0A5F38] rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={async () => {
                          if (!categories.length || !products.length) {
                            const data = await getFormData();
                            setCategories(data.categories || []);
                            setProducts(data.products || []);
                          }
                          setIsWoodWorkMode(!!convertingEstimate.isWoodWork);
                          setEditModalContext('convert_closed_order');
                          setShowEditProductsModal(true);
                          const itemsWithIds = (convertingEstimate.items || []).map((item, idx) => {
                            // Robust category ID extraction
                            let catId = item.category_id ||
                              item.categoryId ||
                              item.category_name ||
                              item.categoryName ||
                              (typeof item.category === 'object' ? item.category?.id || item.category?._id || item.category?.name || item.category?.categoryName : item.category) ||
                              '';

                            // Robust product ID extraction
                            let prodId = item.product_id ||
                              item.productId ||
                              (typeof item.product === 'object' ? item.product?.id || item.product?._id : item.product) ||
                              '';

                            // If catId is a name or missing, try to find the actual ID
                            if (!catId || !/^[0-9a-fA-F]{24}$/.test(String(catId))) {
                              const foundCat = categories.find(c =>
                                (c.name || '').toLowerCase() === String(catId || '').toLowerCase() ||
                                (c.categoryName || '').toLowerCase() === String(catId || '').toLowerCase() ||
                                (c.id || c._id) === String(catId)
                              );
                              if (foundCat) {
                                catId = foundCat.id || foundCat._id;
                              } else if (item.product_name && !prodId) {
                                // Infer Others if name exists but no ID
                                const othersCat = categories.find(c =>
                                  c.isOthers ||
                                  (c.name || '').toLowerCase() === 'others' ||
                                  (c.name || '').toLowerCase() === 'other'
                                );
                                if (othersCat) {
                                  catId = othersCat.id || othersCat._id;
                                }
                              }
                            }

                            const selectedCategory = categories.find(c => String(c.id || c._id) === String(catId));
                            const isOthers = !!(
                              selectedCategory?.isOthers ||
                              (selectedCategory?.name || '').toLowerCase() === 'others' ||
                              (selectedCategory?.name || '').toLowerCase() === 'other' ||
                              item.isOthers ||
                              (item.product_name && !prodId)
                            );

                            return {
                              ...item,
                              category_id: catId,
                              product_id: prodId,
                              isOthers: isOthers,
                              showFreeText: isOthers && !prodId,
                              hsn_code: item.hsn_code || (item.productId?.product?.hsnCode) || ''
                            };
                          });
                          setEditingProducts(recalcItemsAmounts(itemsWithIds));
                          setEditingApplyDiscount(itemsWithIds.some(item => (item.discount || 0) > 0));
                        }}
                        className="px-3 py-1 text-sm bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        Add Product
                      </button>
                    </div>
                  </div>
                  <div className={modalTable.wrap}>
                    {isWoodWorkMode ? (
                      <table className="w-full min-w-[900px] text-sm divide-y divide-black border-collapse">
                        <thead className="bg-[#2D5A27] sticky top-0 z-10">
                          <tr>
                            <th className="border-r border-black px-2 py-3 text-center text-xs font-bold text-white uppercase tracking-wider w-10">SI</th>
                            <th className="border-r border-black px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Type</th>
                            <th className="border-r border-black px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider w-24">Rate (₹)</th>
                            <th className="border-r border-black px-2 py-3 text-center text-xs font-bold text-white uppercase tracking-wider w-16">Nos</th>
                            <th className="border-r border-black px-2 py-3 text-center text-xs font-bold text-white uppercase tracking-wider w-20">L</th>
                            <th className="border-r border-black px-2 py-3 text-center text-xs font-bold text-white uppercase tracking-wider w-20">B</th>
                            <th className="border-r border-black px-2 py-3 text-center text-xs font-bold text-white uppercase tracking-wider w-24">Qty</th>
                            <th className="border-r border-black px-2 py-3 text-center text-xs font-bold text-white uppercase tracking-wider w-20">GST %</th>
                            <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase tracking-wider w-32">Total (₹)</th>
                          </tr>
                        </thead>
                        {(() => {
                          const items = convertingEstimate.items || [];
                          const CAT_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P'];
                          const rows = [];

                          // Build ordered list of unique categories
                          const categoryOrder = [];
                          items.forEach(item => {
                            const cat = item.categoryLabel || item.category_name || item.template_category || '';
                            if (!categoryOrder.includes(cat)) categoryOrder.push(cat);
                          });

                          categoryOrder.forEach((catLabel, catIdx) => {
                            const catEntries = items.filter(item => (item.categoryLabel || item.category_name || item.template_category || '') === catLabel);

                            // Category header row
                            rows.push(
                              <tr key={'cat-' + catLabel} className="bg-[#BDD7EE]">
                                <td className="border-r border-black px-2 py-2 text-center font-bold text-slate-900 border-b border-black">
                                  {CAT_LETTERS[catIdx] || (catIdx + 1)}
                                </td>
                                <td colSpan="7" className="border-r border-black px-4 py-2 font-bold text-slate-900 uppercase tracking-wider border-b border-black">
                                  {catLabel || 'Section'}
                                </td>
                                <td className="px-4 py-2 border-b border-black"></td>
                              </tr>
                            );

                            // Item rows
                            catEntries.forEach((item, siNo) => {
                              const qty = computeWoodWorkQty(item.nos, item.length, item.breadth);
                              const rate = item.unit_price || 0;
                              const discount = item.discount || 0;
                              const baseAmount = roundForCalculation(qty * rate);
                              const discountAmount = roundForCalculation(baseAmount * (discount / 100));
                              const taxableAmount = roundForCalculation(baseAmount - discountAmount);
                              const gstRate = item.gst_rate || 0;
                              const gstAmount = roundForCalculation(taxableAmount * (gstRate / 100));
                              const total = roundForCalculation(taxableAmount + gstAmount);

                              rows.push(
                                <tr key={'item-' + catIdx + '-' + siNo} className="hover:bg-slate-50 transition-colors">
                                  <td className="border-r border-black px-2 py-2 text-center text-slate-600 border-b border-black">{siNo + 1}</td>
                                  <td className="border-r border-black px-4 py-2 font-medium break-words border-b border-black">
                                    {item.product_name}
                                  </td>
                                  <td className="border-r border-black px-4 py-2 text-center text-slate-900 font-semibold border-b border-black">
                                    ₹{rate.toLocaleString()}
                                  </td>
                                  <td className="border-r border-black px-2 py-2 text-center text-slate-600 border-b border-black">{item.nos || 0}</td>
                                  <td className="border-r border-black px-2 py-2 text-center text-slate-600 border-b border-black">{item.length || 0}</td>
                                  <td className="border-r border-black px-2 py-2 text-center text-slate-600 border-b border-black">{item.breadth || 0}</td>
                                  <td className="border-r border-black px-2 py-2 text-center text-slate-900 font-bold border-b border-black">{qty}</td>
                                  <td className="border-r border-black px-2 py-2 text-center font-bold bg-slate-50 border-b border-black">{gstRate}%</td>
                                  <td className="px-4 py-2 text-right text-slate-900 font-bold italic border-b border-black">
                                    ₹{total.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                  </td>
                                </tr>
                              );
                            });
                          });

                          return (
                            <>
                              <tbody className="bg-white divide-y divide-black">
                                {rows}
                              </tbody>
                              <tfoot className="bg-slate-50">
                                <tr>
                                  <td colSpan="8" className="px-4 py-3 text-right font-bold text-slate-700 uppercase">Grand Total:</td>
                                  <td className="px-4 py-3 text-right font-black text-[#0A5F38] text-lg italic">
                                    ₹{calculateInvoiceTotal(items).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                  </td>
                                </tr>
                              </tfoot>
                            </>
                          );
                        })()}
                      </table>
                    ) : (
                      <table className={modalTable.table}>
                        <thead className={modalTable.thead}>
                          <tr>
                            <th className={modalTable.th}>Category</th>
                            <th className={modalTable.th}>Product</th>
                            <th className={modalTable.thCenter}>Units</th>
                            <th className={modalTable.thCenter}>Quantity</th>
                            <th className={modalTable.thRight}>Unit Price</th>
                            {/* Only show Discount column if any item has a discount */}
                            {(convertingEstimate.items && convertingEstimate.items.some(item => (item.discount || 0) > 0)) && (
                              <th className={modalTable.thCenter}>Disc %</th>
                            )}
                            <th className={modalTable.thRight}>Amount</th>
                            <th className={modalTable.thCenter}>GST %</th>
                            <th className={modalTable.thRight}>GST Amount</th>
                            <th className={modalTable.thRight}>Total</th>
                          </tr>
                        </thead>
                        <tbody className={modalTable.tbody}>
                          {convertingEstimate.items && convertingEstimate.items.map((item, index) => {
                            const category = categories.find(cat => String(cat.id || cat._id) === String(item.category_id));
                            const categoryName = category?.name || 'N/A';
                            const baseAmount = roundForCalculation(item.quantity * item.unit_price);
                            const discountAmount = roundForCalculation(baseAmount * ((item.discount || 0) / 100));
                            const taxableAmount = roundForCalculation(baseAmount - discountAmount);
                            const gstAmount = roundForCalculation(taxableAmount * ((item.gst_rate || 0) / 100));
                            const totalAmount = roundForCalculation(taxableAmount + gstAmount);

                            return (
                              <tr key={index} className={modalTable.tr}>
                                <td className={`${modalTable.td} whitespace-nowrap`}>
                                  <div className="text-slate-900">{categoryName}</div>
                                </td>
                                <td className={modalTable.td}>
                                  <div className="font-medium text-slate-900 whitespace-pre-wrap">{item.product_name}</div>
                                </td>
                                <td className={modalTable.tdCenter}>
                                  <div className="text-slate-900">{item.unit_type || 'SFT'}</div>
                                </td>
                                <td className={modalTable.tdCenter}>
                                  <div className="text-slate-900">{item.quantity}</div>
                                </td>
                                <td className={modalTable.tdRight}>
                                  <div className="text-slate-900">₹{parseFloat(item.unit_price).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                </td>
                                {(convertingEstimate.items && convertingEstimate.items.some(i => (i.discount || 0) > 0)) && (
                                  <td className={modalTable.tdCenter}>
                                    <div className="text-slate-900">{item.discount || 0}%</div>
                                  </td>
                                )}
                                <td className={modalTable.tdRight}>
                                  <div className="text-slate-900">₹{taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                </td>
                                <td className={modalTable.tdCenter}>
                                  <div className="font-medium text-slate-900">{item.gst_rate || 0}%</div>
                                </td>
                                <td className={modalTable.tdRight}>
                                  <div className="text-slate-900">₹{gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                </td>
                                <td className={modalTable.tdRight}>
                                  <div className="font-semibold text-slate-900">₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        {convertingEstimate.items && convertingEstimate.items.some(i => (i.discount || 0) > 0) ? (
                          <tfoot className={modalTable.tfoot}>
                            <tr>
                              <td colSpan="9" className="px-4 py-3 text-right font-semibold text-slate-700">Total:</td>
                              <td className="px-4 py-3 text-right font-bold text-slate-900">
                                ₹{(convertingEstimate.items && convertingEstimate.items.length > 0
                                  ? convertingEstimate.items.reduce((sum, item) => {
                                    const baseAmount = roundForCalculation(item.quantity * item.unit_price);
                                    const discountAmount = roundForCalculation(baseAmount * ((item.discount || 0) / 100));
                                    const taxableAmount = roundForCalculation(baseAmount - discountAmount);
                                    const gstAmount = roundForCalculation(taxableAmount * ((item.gst_rate || 0) / 100));
                                    return roundForCalculation(sum + taxableAmount + gstAmount);
                                  }, 0)
                                  : 0
                                ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                          </tfoot>
                        ) : (
                          <tfoot className={modalTable.tfoot}>
                            <tr>
                              <td colSpan="8" className="px-4 py-3 text-right font-semibold text-slate-700">Total:</td>
                              <td className="px-4 py-3 text-right font-bold text-slate-900">
                                ₹{(convertingEstimate.items && convertingEstimate.items.length > 0
                                  ? convertingEstimate.items.reduce((sum, item) => {
                                    const baseAmount = roundForCalculation(item.quantity * item.unit_price);
                                    const discountAmount = roundForCalculation(baseAmount * ((item.discount || 0) / 100));
                                    const taxableAmount = roundForCalculation(baseAmount - discountAmount);
                                    const gstAmount = roundForCalculation(taxableAmount * ((item.gst_rate || 0) / 100));
                                    return roundForCalculation(sum + taxableAmount + gstAmount);
                                  }, 0)
                                  : 0
                                ).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    )}
                  </div>

                  {/* GST Breakdown */}
                  <div className="mt-4 p-3 bg-white rounded-lg">
                    <GSTBreakdown
                      items={convertingEstimate?.items || []}
                      buyerGST={closedOrderData.gstNumber}
                      buyerStateName={closedOrderData.state} // Pass state name for accurate GST type
                      companySettings={companySettings}
                      showTitle={true}
                      className="text-sm"
                    />
                  </div>
                </div>



                {/* Payment Module */}
                <div className="mb-6">
                  {/* Payment Details */}
                  <div className="bg-[#FDFCEB] p-5 rounded-xl border border-[#0A5F38]/20 shadow-sm space-y-6">
                    <h3 className="text-lg font-bold text-[#0A5F38] flex items-center">
                      <Calculator className="w-5 h-5 mr-2" />
                      Payment Details
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left Card: Payment Summary */}
                      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                        <h4 className="text-[#0A5F38] font-bold mb-4 flex items-center text-sm">
                          <DollarSign className="w-4 h-4 mr-2" /> Payment Summary
                        </h4>
                        <div className="space-y-3">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Total Amount:</span>
                            <span className="font-semibold text-slate-900">
                              ₹{calculateInvoiceTotal(convertingEstimate.items).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-[#16a34a] font-medium">Previously Paid:</span>
                            <span className="text-[#16a34a]">
                              - ₹{(convertingEstimate.amountReceived || convertingEstimate.amount_received || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          <div className="border-t border-slate-100 pt-3 flex justify-between text-sm">
                            <span className="font-bold text-slate-900">Remaining Balance:</span>
                            <span className="font-bold text-slate-900">
                              ₹{(() => {
                                const total = calculateInvoiceTotal(convertingEstimate.items);
                                const paid = convertingEstimate.amountReceived || convertingEstimate.amount_received || 0;
                                return Math.max(0, total - paid).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                              })()}
                            </span>
                          </div>
                          <div className="flex justify-between text-base pt-1">
                            <span className="font-bold text-slate-900">Final Balance:</span>
                            <span className="font-bold text-[#dc2626]">
                              ₹{(() => {
                                const total = calculateInvoiceTotal(convertingEstimate.items);
                                const previouslyPaid = convertingEstimate.amountReceived || convertingEstimate.amount_received || 0;
                                const amountReceivedNow = parseFloat(closedOrderData.amountReceived) || 0;
                                const finalBalance = Math.max(0, total - previouslyPaid - amountReceivedNow);
                                return finalBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Right Card: New Payment Entry */}
                      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                        <h4 className="text-slate-900 font-bold mb-4 text-sm">New Payment Entry</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Payment Mode</label>
                            <select
                              value={closedOrderData.paymentType}
                              onChange={(e) => setClosedOrderData({ ...closedOrderData, paymentType: e.target.value })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm"
                            >
                              <option value="cash">Cash</option>
                              <option value="upi">UPI</option>
                              <option value="phonepe">PhonePe</option>
                              <option value="google_pay">Google Pay</option>
                              <option value="paytm">Paytm</option>
                              <option value="net_banking">Net Banking</option>
                              <option value="card">Card</option>
                              <option value="cheque">Cheque</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Amount</label>
                            <div className="relative">
                              <span className="absolute left-3 top-2 text-slate-400 text-sm">₹</span>
                              <input
                                type="number"
                                value={closedOrderData.amountReceived}
                                onChange={(e) => {
                                  const amount = parseFloat(e.target.value) || 0;
                                  const totalAmount = calculateInvoiceTotal(convertingEstimate.items);
                                  const previouslyPaid = convertingEstimate.amountReceived || convertingEstimate.amount_received || 0;
                                  const limit = totalAmount - previouslyPaid;

                                  // Auto-calculate payment status
                                  let status = 'pending';
                                  const cumulative = previouslyPaid + amount;

                                  if (cumulative <= 0) {
                                    status = 'pending';
                                  } else if (Math.abs(cumulative - totalAmount) < 0.01 || cumulative > totalAmount) {
                                    status = 'paid';
                                  } else {
                                    status = 'partial';
                                  }

                                  // Validation: Cannot exceed remaining balance
                                  if (amount > limit) {
                                    toast.error(`Amount cannot exceed the remaining balance (₹${limit.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`);
                                    return;
                                  }

                                  setClosedOrderData({
                                    ...closedOrderData,
                                    amountReceived: e.target.value,
                                    paymentStatus: status
                                  });
                                }}
                                onKeyDown={(e) => (e.key === 'ArrowUp' || e.key === 'ArrowDown') && e.preventDefault()}
                                onWheel={(e) => e.target.blur()}
                                className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#C5A57D] focus:border-transparent text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                placeholder="Enter payment amount"
                                min="0"
                                step="0.01"
                              />
                            </div>
                          </div>

                          <div className="pt-1">
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Payment Status</label>
                            <span className={`inline-flex px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${closedOrderData.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                              closedOrderData.paymentStatus === 'partial' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                              {closedOrderData.paymentStatus === 'paid' ? 'Paid' :
                                closedOrderData.paymentStatus === 'partial' ? 'Partial Payment' :
                                  'Pending'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Terms and Conditions & Material Description */}
                <div className="bg-white p-4 rounded-lg mt-4 mb-6 space-y-6">
                  <TermsAndConditionsInput
                    value={closedOrderData.termsAndConditions}
                    onChange={(value) => setClosedOrderData({ ...closedOrderData, termsAndConditions: value })}
                    label="Terms and Conditions"
                  />
                  
                  {convertingEstimate?.isWoodWork && (
                    <TermsAndConditionsInput
                      value={closedOrderData.materialDescription}
                      onChange={(value) => setClosedOrderData({ ...closedOrderData, materialDescription: value })}
                      label="Material Description"
                      placeholder="Enter material specifications..."
                    />
                  )}
                </div>



            </AppModal>
          )
        }

        {/* Edit Estimate Modal */}
        {
          showEditEstimateModal && editingEstimate && (
            <AppModal
              open
              onClose={() => setShowEditEstimateModal(false)}
              size="lg"
              title={editingEstimate?.isDuplicate ? 'Duplicate Estimate' : 'Edit Estimate'}
              footer={
                <>
                  <button
                    onClick={() => setShowEditEstimateModal(false)}
                    className={modalButton.secondary}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEstimateEdits}
                    disabled={loading}
                    className={modalButton.primary}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Save Changes</span>
                      </>
                    )}
                  </button>
                </>
              }
            >

                {/* Estimate Summary */}
                <div className="mb-6 p-4 bg-white rounded-lg border-2 border-black shadow-sm">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-bold text-[#0A5F38] uppercase tracking-wide">Estimate Summary</h3>
                    <button
                      onClick={() => setEditSummaryMode(!editSummaryMode)}
                      className="text-[#0A5F38] hover:text-[#084B2C] text-sm font-bold flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      {editSummaryMode ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-bold text-[#0A5F38] mb-1">Estimate Number</div>
                      <div className="font-semibold text-gray-800">
                        {editingEstimate.estimate_number || (editingEstimate.isDuplicate ? 'Auto-generated' : (editingEstimate._id?.slice(-8) || 'EST' + editingEstimate.id))}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-[#0A5F38] mb-1">Date</div>
                      {editSummaryMode ? (
                        <input
                          type="date"
                          value={editingEstimate.date ? new Date(editingEstimate.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                          onChange={(e) => setEditingEstimate({ ...editingEstimate, date: e.target.value })}
                          className="w-full px-3 py-2 border-2 border-[#C5A57D] rounded-lg focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent text-sm bg-white"
                        />
                      ) : (
                        <div className="font-semibold text-gray-800">
                          {editingEstimate.date ? new Date(editingEstimate.date).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-[#0A5F38] mb-1">Created By</div>
                      <div className="font-semibold text-gray-800">
                        {editingEstimate.salesperson?.name || editingEstimate.salesperson_name || (user && user.role === 'admin' ? user.name : 'N/A')}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-[#0A5F38] mb-1">Status</div>
                      <div className="font-semibold">
                        {editingEstimate.invoice_cancelled || editingEstimate.cancelled_invoice ? (
                          <span className="text-red-600">Invoice Cancelled</span>
                        ) : editingEstimate.cancelled_closed_order || editingEstimate.closed_order_cancelled ? (
                          <span className="text-red-600">Closed Order Cancelled</span>
                        ) : editingEstimate.status === 'cancelled' ? (
                          <span className="text-red-600">Cancelled</span>
                        ) : editingEstimate.status === 'converted' || editingEstimate.is_converted ? (
                          <span className="text-green-600">Converted</span>
                        ) : (
                          <span className="text-[#0A5F38]">Pending</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-slate-900">Customer Information</h3>
                    <button
                      onClick={() => {
                        if (!editMode) {
                          setEditingCustomer({
                            name: editingEstimate.customer_name || '',
                            phone: editingEstimate.customer_phone || '',
                            email: editingEstimate.customer_email || '',
                            address: editingEstimate.customer_address || '',
                            state: editingEstimate.customer_state || editingEstimate.party_state_name || (editingEstimate.customer_gst ? getStateNameFromGST(editingEstimate.customer_gst) : '') || '',
                            gstNumber: editingEstimate.customer_gst || editingEstimate.customer?.gstNumber || ''
                          });
                        }
                        setEditMode(!editMode);
                      }}
                      className="px-4 py-2 text-sm bg-[#0A5F38] text-white rounded-lg hover:bg-[#0d7a4a] transition-colors font-medium"
                    >
                      {editMode ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  {editMode ? (
                    <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border border-black">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                        <input
                          type="text"
                          value={editingCustomer.name || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, name: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={editingCustomer.countryCode || ''}
                            onChange={(e) => setEditingCustomer({ ...editingCustomer, countryCode: e.target.value })}
                            placeholder="+91"
                            maxLength={5}
                            className="w-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                          <input
                            type="tel"
                            value={editingCustomer.phone || ''}
                            onChange={(e) => setEditingCustomer({ ...editingCustomer, phone: e.target.value.replace(/\D/g, '') })}
                            placeholder={editingCustomer.countryCode ? 'Mobile number' : '10-digit mobile number'}
                            maxLength={editingCustomer.countryCode ? 11 : 10}
                            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                        <input
                          type="email"
                          value={editingCustomer.email || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, email: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                        <textarea
                          value={editingCustomer.address || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, address: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          rows="2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                        <select
                          value={editingCustomer.state || editingEstimate.party_state_name || editingEstimate.customer_state || ''}
                          onChange={(e) => setEditingCustomer({ ...editingCustomer, state: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                        >
                          <option value="">Select State</option>
                          {stateOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">GST Number</label>
                        <input
                          type="text"
                          value={editingCustomer.gstNumber || ''}
                          onChange={(e) => {
                            const newGst = e.target.value;
                            const newState = getStateNameFromGST(newGst);

                            setEditingCustomer(prev => ({
                              ...prev,
                              gstNumber: newGst,
                              // Auto-update state if not already set or if user allows overwrite (simplified logic here)
                              state: newState || prev.state
                            }));
                          }}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                          placeholder="Enter GST Number"
                          maxLength="15"
                        />
                        {editingCustomer.gstNumber && editingCustomer.gstNumber.length > 0 && editingCustomer.gstNumber.length < 15 && (
                          <div className="text-orange-500 text-sm mt-1">
                            GST number should be 15 characters long if provided
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border border-black">
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Name</div>
                        <div className="font-semibold">{editingCustomer.name || editingEstimate.customer_name || 'Walk-in Customer'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Phone</div>
                        <div className="font-semibold">{editingCustomer.phone || editingEstimate.customer_phone || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Email</div>
                        <div className="font-semibold">{editingCustomer.email || editingEstimate.customer_email || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">Address</div>
                        <div className="font-semibold">{editingCustomer.address || editingEstimate.customer_address || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">State</div>
                        <div className="font-semibold">{editingCustomer.state || editingEstimate.party_state_name || editingEstimate.customer_state || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-slate-600 mb-1">GST Number</div>
                        <div className="font-semibold">{editingCustomer.gstNumber || editingEstimate.customer_gst || 'N/A'}</div>
                      </div>
                    </div>
                  )}
                </div>



                {/* Product Details */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold text-slate-900">Product Details</h3>
                    <button
                      onClick={async () => {
                        // Fetch data locally to avoid state staleness issues during mapping
                        let localCategories = categories;
                        let localProducts = products;

                        if (!localCategories.length || !localProducts.length) {
                          try {
                            const data = await getFormData();
                            localCategories = data.categories || [];
                            localProducts = data.products || [];
                            setCategories(localCategories);
                            setProducts(localProducts);
                          } catch (err) {
                            console.error("Failed to load products/categories", err);
                          }
                        }

                        setEditModalContext('edit');

                        // ALWAYS re-initialize editing products to avoid stale data from previous edits
                        const itemsToMap = (editingEstimate ? editingEstimate.items : []) || [];
                        console.log('🔍 Initializing editing items from estimate:', editingEstimate?.estimate_number, itemsToMap);

                        const itemsWithIds = itemsToMap.map((item, idx) => {
                          // Robust category ID extraction
                          let catId = item.category_id ||
                            item.categoryId ||
                            item.category_name ||
                            item.categoryName ||
                            (typeof item.category === 'object' ? item.category?.id || item.category?._id || item.category?.name || item.category?.categoryName : item.category) ||
                            '';

                          // Robust product ID extraction
                          let prodId = item.product_id ||
                            item.productId ||
                            (typeof item.product === 'object' ? item.product?.id || item.product?._id : item.product) ||
                            '';

                          console.log(`📦 Item [${idx}] raw:`, { catId, prodId, itemCategory: item.category, productName: item.product_name });

                          // CRITICAL: If catId is still missing but we have a product_id, look up the product's category
                          if (!catId && prodId) {
                            const foundProd = localProducts.find(p => String(p.id || p._id) === String(prodId));
                            if (foundProd) {
                              catId = foundProd.category_id || foundProd.categoryId;
                              console.log(`✅ [${idx}] Resolved category from product ID:`, catId);
                            }
                          }

                          // If catId is a name or missing, try to find the actual ID
                          if (!catId || !/^[0-9a-fA-F]{24}$/.test(String(catId))) {
                            const foundCat = localCategories.find(c =>
                              (c.name || '').toLowerCase() === String(catId || '').toLowerCase() ||
                              (c.categoryName || '').toLowerCase() === String(catId || '').toLowerCase() ||
                              (c.id || c._id) === String(catId)
                            );
                            if (foundCat) {
                              catId = foundCat.id || foundCat._id;
                              console.log(`✅ [${idx}] Resolved category name/ID "${item.category || item.category_id}" to ID: ${catId}`);
                            } else if (item.product_name && !prodId) {
                              // Infer Others if name exists but no ID
                              const othersCat = localCategories.find(c =>
                                c.isOthers ||
                                (c.name || '').toLowerCase() === 'others' ||
                                (c.name || '').toLowerCase() === 'other'
                              );
                              if (othersCat) {
                                catId = othersCat.id || othersCat._id;
                                console.log(`ℹ️ [${idx}] Inferred "Others" category for product "${item.product_name}"`);
                              }
                            }
                          }

                          const selectedCategory = localCategories.find(c => String(c.id || c._id) === String(catId));
                          const isOthers = !!(
                            selectedCategory?.isOthers ||
                            (selectedCategory?.name || '').toLowerCase() === 'others' ||
                            (selectedCategory?.name || '').toLowerCase() === 'other' ||
                            item.isOthers ||
                            (item.product_name && !prodId) // FORCE isOthers if custom name and no product ID
                          );

                          const quantity = parseFloat(item.quantity) || 0;
                          const unitPrice = parseFloat(item.unit_price) || 0;
                          const gstRate = parseFloat(item.gst_rate) || 0;
                          const discount = parseFloat(item.discount) || 0;

                          const baseAmount = quantity * unitPrice;
                          const discountAmount = baseAmount * (discount / 100);
                          const taxableAmount = baseAmount - discountAmount;
                          const gstAmount = taxableAmount * (gstRate / 100);
                          const totalAmount = taxableAmount + gstAmount;

                          return {
                            ...item,
                            category_id: catId,
                            product_id: prodId,
                            isOthers: isOthers,
                            showFreeText: isOthers && !prodId, // Show free text if it's "Other" category and no product_id
                            // Explicitly preserve Wood Work fields
                            categoryLabel: item.categoryLabel || item.category_label || item.template_category || '',
                            nos: item.nos !== undefined ? item.nos : null,
                            length: item.length !== undefined ? item.length : null,
                            breadth: item.breadth !== undefined ? item.breadth : null,
                            isWoodWork: !!(item.isWoodWork || item.is_wood_work),
                            unit_type: item.unit_type || (isOthers ? 'PCS' : 'SFT'),
                            hsn_code: item.hsn_code || (item.productId?.product?.hsnCode) || '',
                            amount: taxableAmount,
                            gst_amount: gstAmount,
                            total_amount: totalAmount,
                            subtotal: totalAmount
                          };
                        });
                        console.log('✨ Mapped items for edit modal:', itemsWithIds);
                        setEditingProducts(recalcItemsAmounts(itemsWithIds));
                        setEditingApplyDiscount(itemsWithIds.some(item => (item.discount || 0) > 0));
                        setIsWoodWorkMode(!!editingEstimate.isWoodWork);
                        setEditModalContext('edit');
                        setShowEditProductsModal(true);
                        setShowEditEstimateModal(false);
                      }}
                      className="px-4 py-2 text-sm bg-[#0A5F38] text-white rounded-lg hover:bg-[#0d7a4a] transition-colors font-medium"
                    >
                      Edit Products
                    </button>
                  </div>
                  <div className={modalTable.wrap}>
                    <table className={modalTable.table}>
                      <thead className={modalTable.thead}>
                        <tr>
                          <th className={modalTable.th}>Category</th>
                          <th className={modalTable.th}>Product</th>
                          <th className={modalTable.thCenter}>Units</th>
                          <th className={modalTable.thCenter}>Quantity</th>
                          <th className={modalTable.thRight}>Unit Price</th>
                          {editingProducts.some(i => (i.discount || 0) > 0) && (
                            <th className={modalTable.thCenter}>Disc %</th>
                          )}
                          <th className={modalTable.thRight}>Amount</th>
                          <th className={modalTable.thCenter}>GST %</th>
                          <th className={modalTable.thRight}>Total</th>
                        </tr>
                      </thead>
                      <tbody className={modalTable.tbody}>
                        {editingProducts && editingProducts.map((item, index) => {
                          const category = categories.find(cat => String(cat.id || cat._id) === String(item.category_id));
                          const categoryName = category?.name || 'N/A';
                          const baseAmount = roundForCalculation(item.quantity * item.unit_price);
                          const discountAmount = roundForCalculation(baseAmount * ((item.discount || 0) / 100));
                          const taxableAmount = roundForCalculation(baseAmount - discountAmount);
                          const gstAmount = roundForCalculation(taxableAmount * ((item.gst_rate || 0) / 100));
                          const totalAmount = roundForCalculation(taxableAmount + gstAmount);

                          return (
                            <tr key={index} className={modalTable.tr}>
                              <td className={`${modalTable.td} whitespace-nowrap`}>{categoryName}</td>
                              <td className={modalTable.td}>
                                <div className="font-medium text-slate-900 whitespace-pre-wrap">{item.product_name}</div>
                              </td>
                              <td className={modalTable.tdCenter}>{item.unit_type || 'SFT'}</td>
                              <td className={modalTable.tdCenter}>{item.quantity}</td>
                              <td className={modalTable.tdRight}>₹{parseFloat(item.unit_price).toFixed(1)}</td>
                              {editingProducts.some(i => (i.discount || 0) > 0) && (
                                <td className={modalTable.tdCenter}>{item.discount || 0}%</td>
                              )}
                              <td className={modalTable.tdRight}>₹{taxableAmount.toFixed(1)}</td>
                              <td className={modalTable.tdCenter}>{item.gst_rate || 0}%</td>
                              <td className={`${modalTable.tdRight} font-semibold`}>₹{totalAmount.toFixed(1)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Terms and Conditions & Material Description */}
                <div className="mb-6 space-y-6">
                  <TermsAndConditionsInput
                    value={editingEstimate.termsAndConditions || ''}
                    onChange={(value) => setEditingEstimate({ ...editingEstimate, termsAndConditions: value })}
                    label="Terms and Conditions"
                  />

                  {editingEstimate?.isWoodWork && (
                    <TermsAndConditionsInput
                      value={editingEstimate.materialDescription || ''}
                      onChange={(value) => setEditingEstimate({ ...editingEstimate, materialDescription: value })}
                      label="Material Description"
                      placeholder="Enter material specifications..."
                    />
                  )}
                </div>

                {/* Total Summary */}
                <div className="mb-6 bg-white rounded-lg p-4">
                  <div className="flex justify-end">
                    <div className="w-64">
                      <div className="flex justify-between py-2 text-lg font-bold border-t border-slate-300">
                        <span>Grand Total:</span>
                        <span>₹{calculateInvoiceTotal(editingProducts).toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                </div>

            </AppModal>
          )
        }

      </div >

      {/* AI Proforma Upload Modal */}
      {showProformaUploadModal && (
        <AppModal
          open
          onClose={() => setShowProformaUploadModal(false)}
          size="md"
          layer="top"
          title="AI Proforma Extraction"
          icon={<Sparkles className="h-5 w-5" />}
          footer={!proformaExtracting ? (
            <>
              <button
                onClick={() => setShowProformaUploadModal(false)}
                className={modalButton.secondary}
              >
                Cancel
              </button>
              <button
                onClick={handleProformaUpload}
                disabled={!proformaFile}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Sparkles className="h-4 w-4" />
                Start Extraction
              </button>
            </>
          ) : null}
        >
          {!proformaExtracting ? (
            <div className="space-y-6">
              <div className="text-center">
                <div className="bg-indigo-50 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-4 border-2 border-dashed border-indigo-200 group hover:border-indigo-400 transition-colors">
                  <Upload className="h-10 w-10 text-indigo-600 group-hover:scale-110 transition-transform" />
                </div>
                <h4 className="text-lg font-bold text-slate-900 mb-1">Upload Proforma</h4>
                <p className="text-sm text-slate-500">Our AI will automatically read the PDF and fill the estimate form for you. The sender will be added as the customer.</p>
              </div>

              <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-100/50 hover:border-indigo-400 transition-all group">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <FileUp className="w-8 h-8 mb-3 text-slate-400 group-hover:text-indigo-600" />
                    <p className="mb-2 text-sm text-slate-700">
                      <span className="font-bold">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-slate-500">Proforma PDF (Max 10MB)</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="application/pdf,image/*"
                    onChange={(e) => setProformaFile(e.target.files[0])}
                  />
                </label>
              </div>

              {proformaFile && (
                <div className="flex items-center justify-between p-3 bg-indigo-50/50 border border-indigo-100 rounded-xl">
                  <div className="flex items-center truncate">
                    <FileText className="h-5 w-5 text-indigo-600 mr-2 flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-700 truncate">{proformaFile.name}</span>
                  </div>
                  <button
                    onClick={() => setProformaFile(null)}
                    className="text-slate-400 hover:text-red-500 p-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 space-y-6">
              <div className="relative w-24 h-24 mx-auto">
                <div className="absolute inset-0 border-4 border-indigo-100 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Sparkles className="h-8 w-8 text-indigo-600 animate-pulse" />
                </div>
              </div>
              <div>
                <h4 className="text-xl font-bold text-slate-900 mb-2">Analyzing Document...</h4>
                <div className="flex flex-col space-y-2 max-w-xs mx-auto">
                  <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-600 animate-infinite-progress"></div>
                  </div>
                  <p className="text-sm text-slate-500">This usually takes about 10-15 seconds.</p>
                </div>
              </div>
            </div>
          )}
        </AppModal>
      )}

      {/* Delete Confirmation Modal */}
      {
        showDeleteModal && deletingEstimate && (
          <AppModal
            open
            onClose={() => {
              setShowDeleteModal(false);
              setDeletingEstimate(null);
            }}
            size="sm"
            layer="top"
            title="Delete Estimate"
            icon={<Trash2 className="h-5 w-5" />}
            footer={
              <>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingEstimate(null);
                  }}
                  className={modalButton.secondary}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteEstimate}
                  className={modalButton.danger}
                >
                  Delete
                </button>
              </>
            }
          >
            <div className="text-center py-2">
              <p className="text-slate-900 font-medium mb-2">
                Are you sure you want to delete estimate <span className="font-bold">{deletingEstimate.estimate_number}</span>?
              </p>
              <p className="text-slate-600">
                This action cannot be undone.
              </p>
            </div>
          </AppModal>
        )
      }


    </div >

  );
};

export default AdminEstimateManagement;

